export const docSections = {
  'getting-started': {
    title: 'Getting Started',
    description: 'Quick installation and setup guide',
    icon: '🚀',
    order: 1,
    pages: [
      'installation',
      'first-run', 
      'basic-usage'
    ]
  },
  'cli': {
    title: 'CLI Reference',
    description: 'Command-line interface documentation',
    icon: '💻',
    order: 2,
    pages: [
      'commands',
      'configuration'
    ]
  },
  'security': {
    title: 'Security',
    description: 'Security architecture and best practices',
    icon: '🔒',
    order: 3,
    pages: [
      'architecture',
      'detailed-architecture',
      'encryption',
      'best-practices',
      'reporting'
    ]
  },
  'user-guide': {
    title: 'User Guide',
    description: 'Complete feature documentation',
    icon: '📖',
    order: 4,
    pages: [
      'passwords',
      'generator',
      'secrets',
      'backup-restore',
      'import-export',
      'configuration',
      'troubleshooting'
    ]
  },
  'developers': {
    title: 'Developers',
    description: 'API docs and contribution guide',
    icon: '⚡',
    order: 5,
    pages: [
      'api',
      'building',
      'contributing',
      'architecture',
      'advanced-building',
      'advanced-contributing',
      'full-architecture'
    ]
  },
  'tutorials': {
    title: 'Tutorials',
    description: 'Step-by-step guides',
    icon: '🎯',
    order: 6,
    pages: [
      'automation',
      'migration',
      'team-usage'
    ]
  }
};

export const documentationPages = {
  // Introduction
  'intro': {
    title: 'Welcome to PWGen-Rust',
    description: 'Advanced password and secrets manager built in Rust',
    content: `# Welcome to PWGen-Rust

Welcome to **PWGen-Rust** - the advanced password and secrets manager built in Rust! 🦀

## What is PWGen-Rust?

PWGen-Rust is a modern, secure, and user-friendly password and secrets management solution designed for individuals, developers, and teams. Built with Rust for maximum performance and security, PWGen-Rust provides enterprise-grade encryption while maintaining an intuitive user experience.

### 🎉 v1.2.0 Optimization Release

**Latest improvements:**
- **30-40% smaller binaries** through dependency optimization
- **Enhanced security** with modern cryptography (SHA-256 only)
- **Flexible build options** for different deployment scenarios
- **Cross-platform installers** (Snap, NSIS, WiX, PKG, DMG)
- **Conditional compilation** for platform-specific features

### 🎯 Key Features

- **🔒 Military-Grade Security** - AES-256-GCM encryption with Argon2 key derivation
- **🗝️ Comprehensive Secrets** - API keys, SSH keys, documents, environment variables
- **🚀 High Performance** - Native Rust performance with minimal resource usage
- **🎨 Modern UI** - Clean, responsive interface built with egui
- **🌐 Cross-Platform** - Windows, macOS, and Linux support
- **🔧 Developer-Friendly** - CLI and GUI interfaces for all workflows

## Why Choose PWGen-Rust?

### Security First 🛡️

- **Zero-Knowledge Architecture** - Your master password never leaves your device
- **Memory Safety** - Rust's ownership model prevents common vulnerabilities
- **Encrypted Storage** - All sensitive data encrypted with AES-256-GCM
- **Audit Logging** - Comprehensive tracking of all access and modifications

### Developer Experience 💻

- **Powerful CLI** - Full command-line interface for automation and scripting
- **REST API** - Coming soon! Currently in development (Q1 2026)
- **Browser Extensions** - Seamless auto-fill across all major browsers
- **Import/Export** - Easy migration from other password managers

### Enterprise Ready 🏢

- **Team Collaboration** - Role-based access control and sharing
- **Backup & Restore** - Encrypted backups with integrity verification
- **Template System** - Pre-configured templates for common services
- **Compliance** - Meet security requirements for enterprise environments

## Quick Start

Get up and running with PWGen-Rust in minutes:

### 1. Download and Install

Choose your platform on our [download page](#) or use our quick install scripts:

\`\`\`bash
# Linux (Snap Store - Now Available!)
# One-command install from Snap Store
sudo snap install pwgen-rust

# macOS - Download from GitHub releases
# https://github.com/KiefStudioMA/PWGen-Rust/releases/latest

# Windows - Download from GitHub releases
# https://github.com/KiefStudioMA/PWGen-Rust/releases/latest
\`\`\`

### 2. Create Your First Vault

\`\`\`bash
# CLI
pwgen vault create

# Or launch the GUI application:
pwgen-gui
\`\`\`

### 3. Add Your First Password

\`\`\`bash
# CLI Example
pwgen password add --site github.com --username your-username
\`\`\`

## What's Next?

- 📖 **[Getting Started Guide](getting-started/installation)** - Detailed setup instructions
- 💡 **[User Guide](user-guide/passwords)** - Learn all the features
- 🔧 **[CLI Reference](cli/overview)** - Command-line documentation
- 👩‍💻 **[Developer Guide](developers/architecture)** - Technical deep-dive

## Community & Support

- 🐛 **[Report Issues](https://github.com/KiefStudioMA/PWGen-Rust/issues)** - Bug reports and feature requests
- 💬 **[Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)** - Community support
- 🤝 **[Contributing](developers/contributing)** - Help improve PWGen-Rust
- 🔒 **[Security](security/reporting)** - Report security vulnerabilities

## Powered By Innovation

PWGen-Rust is proudly powered by:

- **[TRaViS](https://travisasm.com)** - AI-Powered EASM without asset caps
- **[Kief Studio](https://kief.studio)** - AI Integration & Technology Consulting  
- **[HxHippy](https://hxhippy.com)** - [@HxHippy](https://x.com/HxHippy) on X/Twitter

---

Ready to secure your digital life? **[Download PWGen-Rust now](#)** and experience the future of password management! 🚀`
  },

  // Getting Started
  'getting-started/installation': {
    title: 'Installation',
    description: 'Install PWGen-Rust on Windows, macOS, and Linux',
    content: `# Installation Guide

PWGen-Rust is available for Windows, macOS, and Linux with multiple installation methods for each platform.

## 🪟 Windows

### Method 1: GitHub Releases (Recommended)
\`\`\`bash
# Download the latest Windows installer
curl -L -o pwgen-installer.exe https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/pwgen-windows-x64.exe

# Run the installer
./pwgen-installer.exe
\`\`\`

### Method 2: Winget (Coming Soon)
\`\`\`bash
winget install pwgen-rust
\`\`\`

### Method 3: Chocolatey (Planned)
\`\`\`bash
choco install pwgen-rust
\`\`\`

## 🍎 macOS

### Method 1: GitHub Releases (Recommended)
\`\`\`bash
# Download the latest macOS installer
curl -L -o pwgen-installer.dmg https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/pwgen-macos.dmg

# Mount and install
open pwgen-installer.dmg
\`\`\`

### Method 2: Homebrew (Planned)
\`\`\`bash
brew install pwgen-rust
\`\`\`

## 🐧 Linux

### Method 1: Snap Store (Recommended) ✅
PWGen-Rust is now officially available on the Snap Store!

\`\`\`bash
sudo snap install pwgen-rust
\`\`\`

The snap package includes automatic updates and works across all major Linux distributions.

### Method 2: AppImage
\`\`\`bash
# Download AppImage
curl -L -o pwgen.AppImage https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/pwgen-linux-x64.AppImage

# Make executable and run
chmod +x pwgen.AppImage
./pwgen.AppImage
\`\`\`

### Method 3: Build from Source
\`\`\`bash
# Install Rust (if not already installed)
curl --proto '=https' --tlsv1.2 -sSf https://sh.rustup.rs | sh

# Clone and build
git clone https://github.com/KiefStudioMA/PWGen-Rust.git
cd pwgen
cargo build --release

# Install globally
cargo install --path .
\`\`\`

## System Requirements

| Platform | Minimum | Recommended |
|----------|---------|-------------|
| **Windows** | Windows 10 64-bit | Windows 11 64-bit |
| **macOS** | macOS 10.15 Catalina | macOS 13 Ventura or later |
| **Linux** | Ubuntu 18.04, CentOS 7 | Ubuntu 22.04, Fedora 36 |
| **RAM** | 512 MB | 1 GB |
| **Storage** | 50 MB | 100 MB |

## Verification

After installation, verify PWGen-Rust is working:

\`\`\`bash
# Check version
pwgen --version

# Run basic test
pwgen --help
\`\`\`

## Next Steps

- 📖 [First Run Guide](first-run) - Set up your master password
- 🔑 [Basic Usage](basic-usage) - Learn the fundamentals
- ⚙️ [Configuration](../user-guide/configuration) - Customize your setup

## Troubleshooting

### Common Issues

**Windows: "App can't run on this PC"**
- Ensure you're running 64-bit Windows
- Try running as Administrator

**macOS: "App can't be opened because it's from an unidentified developer"**
- Right-click the app and select "Open"
- Go to System Preferences > Security & Privacy > General > Allow

**Linux: Permission denied**
- Make sure the binary is executable: \`chmod +x pwgen\`
- Check if you have write permissions in the installation directory

For more help, visit our [Troubleshooting Guide](../user-guide/troubleshooting).`
  },

  'getting-started/first-run': {
    title: 'First Run',
    description: 'Initial setup and master password creation',
    content: `# First Run Setup

Welcome to PWGen-Rust! This guide will walk you through the initial setup process.

## 🔐 Creating Your Master Password

Your master password is the most important part of PWGen-Rust's security. It:
- **Never leaves your device** - Zero-knowledge architecture
- **Encrypts all your data** - AES-256-GCM encryption
- **Cannot be recovered** - Make sure you remember it!

### Launch PWGen-Rust

\`\`\`bash
# GUI version
pwgen

# CLI version  
pwgen init
\`\`\`

### Master Password Requirements

PWGen-Rust enforces strong master password requirements:

- **Minimum 12 characters** (recommended 16+)
- **Mixed case letters** (A-Z, a-z)
- **Numbers** (0-9)
- **Special characters** (!@#$%^&*)
- **No dictionary words**
- **Not a reused password**

### Example Strong Master Passwords

❌ **Weak Examples:**
- \`password123\` - Too simple
- \`MyPassword!\` - Dictionary word
- \`12345678901234567890\` - No complexity

✅ **Strong Examples:**
- \`Tr0ub4dor&3\` - Mix of everything
- \`correcthorsebatterystaple\` - Long passphrase
- \`Myd0g1sN4m3d!Fl0ppy\` - Personal but complex

## 🏠 Database Location

PWGen-Rust stores your encrypted database in:

| Platform | Location |
|----------|----------|
| **Windows** | \`%APPDATA%\\PWGen-Rust\\vault.db\` |
| **macOS** | \`~/Library/Application Support/PWGen-Rust/vault.db\` |
| **Linux** | \`~/.local/share/pwgen/vault.db\` |

## ⚙️ Initial Configuration

### Basic Settings

During first run, you'll configure:

1. **Master Password** - Your vault encryption key
2. **Password Generation** - Default length and complexity
3. **Auto-lock Timeout** - Security timeout settings
4. **Backup Location** - Where to store backups

### Example CLI Setup

\`\`\`bash
# Initialize new vault
pwgen init

# Set master password (you'll be prompted)
# Configure default password generation
pwgen config set password-length 16
pwgen config set include-symbols true
pwgen config set auto-lock-timeout 300  # 5 minutes

# Create first password entry
pwgen add --site="github.com" --username="myuser"
\`\`\`

## 🔒 Security Verification

After setup, PWGen-Rust will:

1. **Test encryption** - Verify your master password works
2. **Create initial backup** - Store encrypted backup
3. **Generate test entries** - Verify all systems working
4. **Show security summary** - Display encryption details

### Security Summary Example

\`\`\`
🔒 PWGen-Rust Security Summary
━━━━━━━━━━━━━━━━━━━━━━━━━━
✅ Database encrypted with AES-256-GCM
✅ Master password strength: Excellent (95/100)
✅ Key derivation: Argon2id (4GB memory, 3 iterations)
✅ Backup created: ~/Documents/pwgen-backup-2024-01-15.enc
✅ Zero-knowledge verified: Master password never stored

Your vault is secure! 🎉
\`\`\`

## 🎯 Next Steps

Now that PWGen-Rust is set up:

### Immediate Actions
1. **📱 Add your first passwords** - Start with critical accounts
2. **🔄 Create a backup** - Store it securely offline  
3. **📖 Learn the basics** - [Basic Usage Guide](basic-usage)

### Recommended Setup
1. **Import existing passwords** - [Import Guide](../user-guide/import-export)
2. **Configure auto-generation** - [Generator Settings](../user-guide/generator)
3. **Set up CLI automation** - [CLI Reference](../cli/overview)

## 🚨 Important Security Notes

### ⚠️ Master Password Recovery
- **PWGen-Rust cannot recover your master password**
- **No "forgot password" option exists**
- **No backdoors or recovery mechanisms**
- **This is by design for maximum security**

### 💾 Backup Strategy
- **Create encrypted backups regularly**
- **Store backups in multiple secure locations**
- **Test backup restoration periodically**
- **Never store backup password with the backup**

### 🔐 Best Practices
- **Use PWGen-Rust for ALL passwords** - Don't reuse any passwords
- **Enable auto-lock** - Protect against unauthorized access
- **Keep PWGen-Rust updated** - Security improvements in every release
- **Use strong device security** - Full disk encryption recommended

## Troubleshooting First Run

### Common Issues

**"Invalid master password strength"**
- Your password doesn't meet complexity requirements
- Try a longer password with mixed characters

**"Cannot create database file"**
- Check write permissions in the data directory
- Try running with administrator/sudo privileges

**"Database initialization failed"**
- Ensure adequate disk space (minimum 100MB free)
- Check for antivirus interference

For more help, see our [Troubleshooting Guide](../user-guide/troubleshooting).

## Ready to Go! 🚀

You're now ready to use PWGen-Rust securely! Start with the [Basic Usage Guide](basic-usage) to learn the fundamentals.`
  },

  'getting-started/basic-usage': {
    title: 'Basic Usage',
    description: 'Learn PWGen-Rust fundamentals and core features',
    content: `# Basic Usage Guide

Learn the fundamentals of PWGen-Rust for secure password management.

## 🖥️ GUI Interface

### Main Window

The PWGen-Rust GUI provides an intuitive interface:

1. **Search Bar** - Find passwords quickly
2. **Password List** - Browse all entries
3. **Details Panel** - View/edit password info
4. **Toolbar** - Quick actions (Add, Generate, Import)
5. **Status Bar** - Security status and sync info

### Quick Actions

| Action | Shortcut | Description |
|--------|----------|-------------|
| **Add Entry** | \`Ctrl+N\` | Create new password |
| **Generate Password** | \`Ctrl+G\` | Generate secure password |
| **Search** | \`Ctrl+F\` | Find entries |
| **Copy Password** | \`Ctrl+C\` | Copy to clipboard |
| **Lock Vault** | \`Ctrl+L\` | Lock immediately |

## 💻 CLI Interface

### Essential Commands

\`\`\`bash
# Add a new password
pwgen add --site="example.com" --username="user@email.com"

# Generate and add password
pwgen add --site="github.com" --username="myuser" --generate

# List all passwords
pwgen list

# Search for entries
pwgen search "github"

# Get specific password
pwgen get "github.com"

# Copy password to clipboard
pwgen copy "github.com"
\`\`\`

### Password Generation

\`\`\`bash
# Generate with default settings
pwgen generate

# Custom length
pwgen generate --length 20

# Exclude symbols
pwgen generate --length 16 --no-symbols

# Pronounceable password
pwgen generate --pronounceable --length 12

# Pin/numeric only
pwgen generate --numeric --length 6
\`\`\`

## 🔑 Managing Passwords

### Adding Passwords

#### Via GUI:
1. Click **"Add Entry"** button
2. Fill in **Site**, **Username**, **Password**
3. Add **Notes** and **Tags** (optional)
4. Click **"Save"**

#### Via CLI:
\`\`\`bash
# Interactive mode
pwgen add

# With parameters
pwgen add $
  --site="banking.example.com" $
  --username="john.doe@email.com" $
  --password="GeneratedPassword123!" $
  --notes="Main checking account" $
  --tags="banking,finance"
\`\`\`

### Organizing Entries

#### Tags
Use tags to organize passwords:
- **Purpose**: \`work\`, \`personal\`, \`banking\`
- **Importance**: \`critical\`, \`important\`, \`optional\`
- **Type**: \`email\`, \`social\`, \`shopping\`

#### Favorites
Mark frequently used passwords as favorites:
\`\`\`bash
# Mark as favorite
pwgen favorite "github.com"

# List favorites
pwgen list --favorites
\`\`\`

### Updating Passwords

#### GUI Method:
1. Select entry from list
2. Click **"Edit"** button
3. Update fields
4. Click **"Save"**

#### CLI Method:
\`\`\`bash
# Update password
pwgen update "github.com" --password="NewPassword123!"

# Update username
pwgen update "github.com" --username="new.email@domain.com"

# Add notes
pwgen update "github.com" --notes="Updated after security breach"
\`\`\`

## 🔐 Password Security

### Password Strength Analysis

PWGen-Rust analyzes password strength:

| Strength | Score | Description |
|----------|-------|-------------|
| **Weak** | 0-25 | Easily crackable |
| **Fair** | 26-50 | Vulnerable to attacks |
| **Good** | 51-75 | Adequate security |
| **Strong** | 76-90 | Excellent security |
| **Excellent** | 91-100 | Maximum security |

### Security Checks

\`\`\`bash
# Check all passwords
pwgen audit

# Check specific entry
pwgen audit "github.com"

# Show weak passwords
pwgen audit --weak

# Show duplicates
pwgen audit --duplicates

# Show old passwords (>90 days)
pwgen audit --old
\`\`\`

### Breach Monitoring

PWGen-Rust can check against known breaches:

\`\`\`bash
# Check all passwords against breach databases
pwgen breach-check

# Check specific password
pwgen breach-check "github.com"

# Show only compromised
pwgen breach-check --compromised-only
\`\`\`

## 🔍 Searching and Filtering

### Search Capabilities

#### GUI Search:
- **Site name**: Find by domain or service
- **Username**: Search by email or username
- **Tags**: Filter by organization tags
- **Notes**: Search within notes and descriptions

#### CLI Search:
\`\`\`bash
# Simple search
pwgen search "github"

# Case-sensitive search
pwgen search "GitHub" --case-sensitive

# Tag filtering
pwgen search --tag="work"

# Multiple criteria
pwgen search --site="*.com" --tag="social"

# Regular expressions
pwgen search --regex ".*\\.gov$"
\`\`\`

### Advanced Filtering

\`\`\`bash
# Show entries created this week
pwgen list --created-after="1 week ago"

# Show entries not accessed in 30 days
pwgen list --last-accessed-before="30 days ago"

# Show entries with weak passwords
pwgen list --strength="weak"

# Combine filters
pwgen list --tag="banking" --strength="strong" --favorites
\`\`\`

## 📋 Clipboard Integration

### Automatic Clipboard Management

PWGen-Rust provides secure clipboard handling:

- **Auto-clear**: Clipboard cleared after 30 seconds
- **Multi-clipboard**: Support for primary/secondary clipboard
- **Format options**: Plain text, JSON, CSV export

### CLI Clipboard Operations

\`\`\`bash
# Copy password
pwgen copy "github.com"

# Copy username
pwgen copy "github.com" --username

# Copy custom field
pwgen copy "github.com" --field="recovery-email"

# Disable auto-clear
pwgen copy "github.com" --no-auto-clear
\`\`\`

## 🔒 Security Features

### Auto-Lock

Configure automatic vault locking:

\`\`\`bash
# Set auto-lock timeout (seconds)
pwgen config set auto-lock-timeout 300

# Lock on system sleep
pwgen config set lock-on-sleep true

# Lock on screen saver
pwgen config set lock-on-screensaver true
\`\`\`

### Session Management

\`\`\`bash
# Show current session info
pwgen session status

# Extend session
pwgen session extend

# Lock immediately
pwgen lock

# Unlock vault
pwgen unlock
\`\`\`

## 📊 Usage Statistics

### View Statistics

\`\`\`bash
# General statistics
pwgen stats

# Password analysis
pwgen stats --passwords

# Usage patterns
pwgen stats --usage

# Security metrics
pwgen stats --security
\`\`\`

### Example Output

\`\`\`
📊 PWGen-Rust Statistics
━━━━━━━━━━━━━━━━━━━━━━━━━━
Total Entries: 147
├─ Passwords: 98
├─ API Keys: 23  
├─ SSH Keys: 15
└─ Documents: 11

Security Health: 94% ✅
├─ Strong passwords: 95%
├─ No duplicates: 100%
├─ Recent updates: 87%
└─ No breaches: 100%

Usage (Last 30 days):
├─ Most accessed: github.com (47 times)
├─ Recently added: 8 entries
└─ Updated: 12 entries
\`\`\`

## 🎯 Next Steps

Now that you know the basics:

1. **🔧 Customize settings** - [Configuration Guide](../user-guide/configuration)
2. **🛡️ Enhance security** - [Security Best Practices](../security/best-practices)
3. **⚡ Learn advanced features** - [User Guide](../user-guide/passwords)
4. **🔄 Set up backups** - [Backup & Restore](../user-guide/backup-restore)

## 💡 Pro Tips

### Productivity Shortcuts
- Use **tags consistently** for better organization
- Set up **favorites** for daily-use accounts
- Use **CLI aliases** for frequent commands
- Enable **auto-generation** for new accounts

### Security Best Practices
- **Audit passwords monthly** - Check for weak/old passwords
- **Update breached passwords immediately** - Monitor breach alerts
- **Use unique passwords everywhere** - Never reuse passwords
- **Regular backups** - Store encrypted backups securely

Happy password managing! 🚀`
  },

  // CLI Reference
  'cli/overview': {
    title: 'CLI Overview',
    description: 'Command-line interface introduction',
    content: `# CLI Overview

PWGen-Rust provides a powerful command-line interface for automation, scripting, and advanced users.

## 🚀 Getting Started

### Installation Verification

\`\`\`bash
# Check if PWGen-Rust CLI is installed
pwgen --version

# Show help
pwgen --help

# List all commands
pwgen help
\`\`\`

### Basic Syntax

\`\`\`bash
pwgen [GLOBAL_OPTIONS] <COMMAND> [COMMAND_OPTIONS] [ARGUMENTS]
\`\`\`

### Global Options

| Option | Description | Example |
|--------|-------------|---------|
| \`--vault PATH\` | Specify vault location | \`pwgen --vault /path/to/vault.db list\` |
| \`--config PATH\` | Use custom config | \`pwgen --config ~/.pwgen-alt.toml add\` |
| \`--verbose\` | Enable verbose output | \`pwgen --verbose import data.json\` |
| \`--quiet\` | Suppress non-error output | \`pwgen --quiet backup\` |
| \`--no-color\` | Disable colored output | \`pwgen --no-color list\` |
| \`--format FORMAT\` | Output format (json/yaml/table) | \`pwgen --format json list\` |

## 📚 Command Categories

### 🔐 Password Management
\`\`\`bash
pwgen add        # Add new password entry
pwgen get        # Retrieve password
pwgen list       # List all entries
pwgen update     # Update existing entry
pwgen remove     # Delete entry
pwgen search     # Search entries
\`\`\`

### 🎲 Password Generation
\`\`\`bash
pwgen generate   # Generate password
pwgen strength   # Check password strength
pwgen pronounce  # Generate pronounceable password
\`\`\`

### 🔒 Vault Operations
\`\`\`bash
pwgen init       # Initialize new vault
pwgen unlock     # Unlock vault
pwgen lock       # Lock vault
pwgen change-master  # Change master password
\`\`\`

### 📦 Import/Export
\`\`\`bash
pwgen import     # Import from various formats
pwgen export     # Export vault data
pwgen backup     # Create encrypted backup
pwgen restore    # Restore from backup
\`\`\`

### 🔍 Security & Analysis
\`\`\`bash
pwgen audit      # Security audit
pwgen breach-check   # Check against breach databases
pwgen stats      # Usage statistics
\`\`\`

### ⚙️ Configuration
\`\`\`bash
pwgen config     # Manage configuration
pwgen session    # Session management
\`\`\`

## 🎯 Common Workflows

### Daily Usage

\`\`\`bash
# Morning routine - unlock and check
pwgen unlock
pwgen audit --quick
pwgen list --recent

# Add new account
pwgen add --site="newservice.com" --generate

# Update existing password
pwgen update "oldservice.com" --generate

# End of day - backup and lock
pwgen backup
pwgen lock
\`\`\`

### Batch Operations

\`\`\`bash
# Import from browser
pwgen import chrome-passwords.csv --format=chrome

# Audit all passwords
pwgen audit --detailed > security-report.txt

# Update all weak passwords
pwgen list --strength=weak | xargs -I {} pwgen update {} --generate

# Export for backup
pwgen export --format=encrypted --output=backup-$(date +%Y%m%d).enc
\`\`\`

## 🔧 Configuration

### Configuration File

PWGen-Rust uses TOML configuration:

\`\`\`toml
# ~/.config/pwgen/config.toml

[vault]
default_path = "~/.local/share/pwgen/vault.db"
auto_backup = true
backup_location = "~/Documents/pwgen-backups"

[security]
auto_lock_timeout = 300  # 5 minutes
lock_on_idle = true
clipboard_clear_timeout = 30

[passwords]
default_length = 16
include_symbols = true
exclude_ambiguous = true
minimum_strength = 75

[cli]
default_format = "table"
colors = true
confirm_dangerous_operations = true
\`\`\`

### Environment Variables

\`\`\`bash
# Set vault location
export PWGEN_VAULT_PATH="/path/to/vault.db"

# Disable colored output
export PWGEN_NO_COLOR=1

# Set default format
export PWGEN_FORMAT="json"

# Config file location
export PWGEN_CONFIG="/path/to/config.toml"
\`\`\`

### Configure via CLI

\`\`\`bash
# View current config
pwgen config show

# Set values
pwgen config set passwords.default_length 20
pwgen config set security.auto_lock_timeout 600

# Reset to defaults
pwgen config reset

# Validate configuration
pwgen config validate
\`\`\`

## 📋 Output Formats

### Table Format (Default)

\`\`\`bash
pwgen list
\`\`\`

\`\`\`
┌─────────────────────┬──────────────────────┬──────────┬─────────────┐
│ Site                │ Username             │ Strength │ Last Access │
├─────────────────────┼──────────────────────┼──────────┼─────────────┤
│ github.com          │ user@example.com     │ Strong   │ 2 hours ago │
│ banking.example.com │ john.doe@email.com   │ Excellent│ 1 day ago   │
└─────────────────────┴──────────────────────┴──────────┴─────────────┘
\`\`\`

### JSON Format

\`\`\`bash
pwgen --format json list
\`\`\`

\`\`\`json
{
  "entries": [
    {
      "site": "github.com",
      "username": "user@example.com",
      "strength": "strong",
      "last_accessed": "2024-01-15T14:30:00Z",
      "tags": ["work", "development"]
    }
  ],
  "total": 1
}
\`\`\`

### YAML Format

\`\`\`bash
pwgen --format yaml get github.com
\`\`\`

\`\`\`yaml
site: github.com
username: user@example.com
password: "***hidden***"
strength: strong
created: 2024-01-10T09:15:00Z
updated: 2024-01-15T14:30:00Z
tags:
  - work
  - development
notes: Main development account
\`\`\`

## 🔐 Security Considerations

### Master Password Handling

\`\`\`bash
# Secure password input (recommended)
pwgen unlock  # Prompts securely

# From environment (less secure)
export PWGEN_MASTER_PASSWORD="your-master-password"
pwgen unlock --env

# From file (least secure)
echo "your-master-password" > /tmp/password
pwgen unlock --password-file /tmp/password
rm /tmp/password
\`\`\`

### Clipboard Security

\`\`\`bash
# Copy with auto-clear (default)
pwgen copy github.com

# Copy without auto-clear (dangerous)
pwgen copy github.com --no-auto-clear

# Copy to specific clipboard
pwgen copy github.com --clipboard=secondary
\`\`\`

### File Permissions

\`\`\`bash
# Secure vault permissions
chmod 600 ~/.local/share/pwgen/vault.db

# Secure config permissions
chmod 600 ~/.config/pwgen/config.toml

# Secure backup permissions
chmod 600 ~/Documents/pwgen-backups/*.enc
\`\`\`

## 🎛️ Advanced Features

### Scripting Integration

\`\`\`bash
#!/bin/bash
# Daily security check script

# Unlock vault
pwgen unlock || exit 1

# Check for security issues
WEAK_COUNT=$(pwgen audit --weak --count)
BREACH_COUNT=$(pwgen breach-check --count)

if [ "$WEAK_COUNT" -gt 0 ] || [ "$BREACH_COUNT" -gt 0 ]; then
    echo "⚠️  Security issues found:"
    echo "   Weak passwords: $WEAK_COUNT"
    echo "   Breached passwords: $BREACH_COUNT"
    exit 1
fi

echo "✅ All passwords secure!"

# Create backup
pwgen backup --auto-name

# Lock vault
pwgen lock
\`\`\`

### CI/CD Integration

\`\`\`yaml
# .github/workflows/security-check.yml
name: Password Security Check
on:
  schedule:
    - cron: '0 9 * * 1'  # Every Monday at 9 AM

jobs:
  security-audit:
    runs-on: ubuntu-latest
    steps:
      - name: Install PWGen-Rust
        run: snap install pwgen-rust
        
      - name: Restore vault
        run: |
          echo "\${{ secrets.VAULT_DATA }}" | base64 -d > vault.db
          
      - name: Security audit
        env:
          PWGEN_MASTER_PASSWORD: \${{ secrets.MASTER_PASSWORD }}
        run: |
          pwgen --vault vault.db audit --detailed
          pwgen --vault vault.db breach-check
\`\`\`

## 🆘 Getting Help

### Built-in Help

\`\`\`bash
# General help
pwgen help

# Command-specific help
pwgen help add
pwgen add --help

# List all commands
pwgen --help

# Show examples
pwgen examples
\`\`\`

### Debugging

\`\`\`bash
# Enable verbose output
pwgen --verbose command

# Debug mode (very detailed)
pwgen --debug command

# Log to file
pwgen --log-file /tmp/pwgen.log command
\`\`\`

## 🔗 Next Steps

- **[Commands Reference](commands)** - Detailed command documentation
- **[Configuration](configuration)** - Advanced configuration options
- **[User Guide](../user-guide/passwords)** - GUI and advanced features
- **[Automation](../tutorials/automation)** - Scripting and automation examples`
  },

  'cli/commands': {
    title: 'CLI Commands Reference',
    description: 'Complete reference for all PWGen-Rust CLI commands',
    content: `# CLI Commands Reference

Complete reference for all PWGen-Rust CLI commands and options.

## Global Options

These options work with all commands:

\`\`\`bash
pwgen [GLOBAL_OPTIONS] <COMMAND> [COMMAND_OPTIONS]
\`\`\`

### Global Flags

| Option | Description |
|--------|-------------|
| \`--vault <PATH>\` | Specify vault file location |
| \`--format <FORMAT>\` | Output format: \`text\`, \`json\`, \`csv\`, \`table\` |
| \`--verbose, -v\` | Enable verbose output |
| \`--quiet, -q\` | Suppress non-error output |
| \`--help, -h\` | Show help information |
| \`--version, -V\` | Show version information |
| \`--no-color\` | Disable colored output |

### Examples

\`\`\`bash
# Use custom vault location
pwgen --vault ~/work-vault.db password list

# Get JSON output
pwgen --format json password search "github"

# Verbose mode for debugging
pwgen --verbose vault create --path new-vault.db
\`\`\`

## Vault Commands

### \`vault create\`

Create a new vault with master password.

\`\`\`bash
pwgen vault create [OPTIONS]
\`\`\`

**Options:**
- \`--path <PATH>\` - Vault file location (required)
- \`--force\` - Overwrite existing vault

**Examples:**
\`\`\`bash
# Create new vault
pwgen vault create --path ~/my-vault.db

# Overwrite existing vault
pwgen vault create --path existing.db --force
\`\`\`

### \`vault status\`

Show vault information and status.

\`\`\`bash
pwgen vault status
\`\`\`

**Output includes:**
- Vault file location
- Lock status
- Entry count
- Last modified date

### \`vault lock\`

Lock the vault, requiring password for next access.

\`\`\`bash
pwgen vault lock
\`\`\`

### \`vault unlock\`

Unlock the vault with master password.

\`\`\`bash
pwgen vault unlock
\`\`\`

### \`vault change-password\`

Change the master password.

\`\`\`bash
pwgen vault change-password
\`\`\`

## Password Commands

### \`password add\`

Add a new password entry to the vault.

\`\`\`bash
pwgen password add [OPTIONS]
\`\`\`

**Options:**
- \`--site <SITE>\` - Website or service name (required)
- \`--username <USERNAME>\` - Username or email (required)
- \`--password <PASSWORD>\` - Password (prompted if not provided)
- \`--tags <TAGS>\` - Comma-separated tags
- \`--notes <NOTES>\` - Additional notes
- \`--url <URL>\` - Full URL for the service

**Examples:**
\`\`\`bash
# Add entry with prompted password
pwgen password add --site github.com --username user@example.com

# Add with all details
pwgen password add $
  --site "My Bank" $
  --username "customer123" $
  --password "secure-password" $
  --tags "banking,financial" $
  --notes "Main checking account" $
  --url "https://bank.example.com/login"
\`\`\`

### \`password list\`

List all password entries.

\`\`\`bash
pwgen password list [OPTIONS]
\`\`\`

**Options:**
- \`--tag <TAG>\` - Filter by tag
- \`--site <PATTERN>\` - Filter by site pattern
- \`--username <PATTERN>\` - Filter by username pattern
- \`--older-than <DURATION>\` - Show entries older than duration
- \`--newer-than <DURATION>\` - Show entries newer than duration
- \`--limit <NUMBER>\` - Limit number of results

**Examples:**
\`\`\`bash
# List all entries
pwgen password list

# Filter by tag
pwgen password list --tag work

# Find old passwords
pwgen password list --older-than "90 days"

# Search by site pattern
pwgen password list --site "*google*"
\`\`\`

### \`password search\`

Search password entries by keyword.

\`\`\`bash
pwgen password search <QUERY> [OPTIONS]
\`\`\`

**Options:**
- \`--case-sensitive\` - Enable case-sensitive search
- \`--limit <NUMBER>\` - Limit number of results

**Examples:**
\`\`\`bash
# Basic search
pwgen password search "github"

# Case-sensitive search
pwgen password search "GitHub" --case-sensitive

# Limit results
pwgen password search "email" --limit 5
\`\`\`

### \`password update\`

Update an existing password entry.

\`\`\`bash
pwgen password update [OPTIONS]
\`\`\`

**Options:**
- \`--site <SITE>\` - Site to update (required)
- \`--username <USERNAME>\` - New username
- \`--password <PASSWORD>\` - New password
- \`--tags <TAGS>\` - New tags (replaces existing)
- \`--add-tags <TAGS>\` - Add tags to existing
- \`--remove-tags <TAGS>\` - Remove specific tags
- \`--notes <NOTES>\` - New notes
- \`--url <URL>\` - New URL

**Examples:**
\`\`\`bash
# Update password only
pwgen password update --site github.com --password "new-password"

# Update multiple fields
pwgen password update $
  --site github.com $
  --username "new-email@example.com" $
  --add-tags "2fa-enabled"
\`\`\`

### \`password delete\`

Delete a password entry.

\`\`\`bash
pwgen password delete [OPTIONS]
\`\`\`

**Options:**
- \`--site <SITE>\` - Site to delete (required)
- \`--confirm\` - Skip confirmation prompt
- \`--tag <TAG>\` - Delete all entries with tag

**Examples:**
\`\`\`bash
# Delete specific entry
pwgen password delete --site github.com

# Delete without confirmation
pwgen password delete --site old-service.com --confirm

# Delete all entries with tag
pwgen password delete --tag "old" --confirm
\`\`\`

### \`password copy\`

Copy password to clipboard.

\`\`\`bash
pwgen password copy [OPTIONS]
\`\`\`

**Options:**
- \`--site <SITE>\` - Site to copy from (required)
- \`--username\` - Copy username instead of password
- \`--clear-after <SECONDS>\` - Auto-clear clipboard (default: 30)

**Examples:**
\`\`\`bash
# Copy password
pwgen password copy --site github.com

# Copy username
pwgen password copy --site github.com --username

# Custom clear time
pwgen password copy --site github.com --clear-after 60
\`\`\`

## Secret Commands

### \`secret add\`

Add various types of secrets (API keys, SSH keys, documents).

\`\`\`bash
pwgen secret add <TYPE> [OPTIONS]
\`\`\`

**Types:**
- \`api-key\` - API keys and tokens
- \`ssh-key\` - SSH private keys
- \`document\` - Files and certificates
- \`note\` - Secure text notes

**API Key Options:**
- \`--name <NAME>\` - Secret name (required)
- \`--value <VALUE>\` - API key value
- \`--provider <PROVIDER>\` - Service provider
- \`--tags <TAGS>\` - Tags

**SSH Key Options:**
- \`--name <NAME>\` - Key name (required)
- \`--key-file <FILE>\` - Path to private key file
- \`--tags <TAGS>\` - Tags

**Document Options:**
- \`--title <TITLE>\` - Document title (required)
- \`--file <FILE>\` - Path to file
- \`--tags <TAGS>\` - Tags

**Examples:**
\`\`\`bash
# Add API key
pwgen secret add api-key $
  --name "GitHub Token" $
  --provider "GitHub" $
  --tags "development,github"

# Add SSH key
pwgen secret add ssh-key $
  --name "Production Server" $
  --key-file ~/.ssh/prod_key $
  --tags "server,production"

# Add document
pwgen secret add document $
  --title "SSL Certificate" $
  --file ~/certs/ssl.pem $
  --tags "certificate,ssl"
\`\`\`

### \`secret list\`

List all secrets.

\`\`\`bash
pwgen secret list [OPTIONS]
\`\`\`

**Options:**
- \`--type <TYPE>\` - Filter by secret type
- \`--tag <TAG>\` - Filter by tag
- \`--name <PATTERN>\` - Filter by name pattern

### \`secret copy\`

Copy secret value to clipboard.

\`\`\`bash
pwgen secret copy --name <NAME> [OPTIONS]
\`\`\`

**Options:**
- \`--clear-after <SECONDS>\` - Auto-clear clipboard

### \`secret delete\`

Delete a secret.

\`\`\`bash
pwgen secret delete --name <NAME> [OPTIONS]
\`\`\`

**Options:**
- \`--confirm\` - Skip confirmation prompt

## Generator Commands

### \`generate\`

Generate secure passwords with customizable options.

\`\`\`bash
pwgen generate [OPTIONS]
\`\`\`

**Options:**
- \`--length <LENGTH>\` - Password length (default: 16)
- \`--count <COUNT>\` - Number of passwords to generate
- \`--symbols\` - Include symbols
- \`--no-symbols\` - Exclude symbols
- \`--numbers\` - Include numbers
- \`--no-numbers\` - Exclude numbers
- \`--uppercase\` - Include uppercase letters
- \`--no-uppercase\` - Exclude uppercase letters
- \`--lowercase\` - Include lowercase letters
- \`--no-lowercase\` - Exclude lowercase letters
- \`--no-ambiguous\` - Exclude ambiguous characters (0, O, l, 1)
- \`--pattern <PATTERN>\` - Use custom pattern
- \`--copy\` - Copy generated password to clipboard

**Pattern Syntax:**
- \`L\` - Uppercase letter
- \`l\` - Lowercase letter
- \`d\` - Digit
- \`s\` - Symbol
- Any other character is used literally

**Examples:**
\`\`\`bash
# Basic generation
pwgen generate

# Custom length with symbols
pwgen generate --length 24 --symbols

# Multiple passwords
pwgen generate --count 5 --length 12

# Pattern-based generation
pwgen generate --pattern "Llll-dddd-LLLL"

# No ambiguous characters
pwgen generate --length 20 --no-ambiguous

# Generate and copy
pwgen generate --length 16 --symbols --copy
\`\`\`

## Import Commands

### \`import\`

Import passwords from various sources.

\`\`\`bash
pwgen import <SOURCE> [OPTIONS]
\`\`\`

**Sources:**
- \`csv\` - Generic CSV file
- \`chrome\` - Chrome browser
- \`firefox\` - Firefox browser
- \`edge\` - Microsoft Edge
- \`safari\` - Safari browser
- \`opera\` - Opera browser
- \`brave\` - Brave browser
- \`lastpass\` - LastPass export
- \`bitwarden\` - Bitwarden export
- \`1password\` - 1Password export
- \`keepass\` - KeePass export

**Options:**
- \`--file <FILE>\` - Import file path (for file-based imports)
- \`--profile <PROFILE>\` - Browser profile name
- \`--merge\` - Merge with existing entries (don't overwrite)
- \`--dry-run\` - Show what would be imported without actually importing

**Examples:**
\`\`\`bash
# Import from Chrome
pwgen import chrome

# Import from specific Firefox profile
pwgen import firefox --profile "work"

# Import from CSV file
pwgen import csv --file passwords.csv

# Import from LastPass export
pwgen import lastpass --file lastpass_export.csv

# Dry run to preview import
pwgen import chrome --dry-run
\`\`\`

## Export Commands

### \`export\`

Export vault data to various formats.

\`\`\`bash
pwgen export <FORMAT> [OPTIONS]
\`\`\`

**Formats:**
- \`csv\` - Comma-separated values
- \`json\` - JSON format
- \`html\` - HTML report
- \`xml\` - XML format

**Options:**
- \`--output <FILE>\` - Output file path
- \`--include-passwords\` - Include passwords in export (security warning)
- \`--tag <TAG>\` - Export only entries with specific tag
- \`--template <TEMPLATE>\` - Use custom export template

**Examples:**
\`\`\`bash
# Export to CSV (passwords excluded by default)
pwgen export csv --output vault-export.csv

# Export to JSON with passwords
pwgen export json --output vault-backup.json --include-passwords

# Export specific tag
pwgen export csv --output work-passwords.csv --tag work

# Export to HTML report
pwgen export html --output vault-report.html
\`\`\`

## Backup Commands

### \`backup create\`

Create encrypted backup of the vault.

\`\`\`bash
pwgen backup create [OPTIONS]
\`\`\`

**Options:**
- \`--output <FILE>\` - Backup file location (required)
- \`--compression <LEVEL>\` - Compression level (0-9, default: 6)
- \`--password <PASSWORD>\` - Backup encryption password (prompted if not provided)

**Examples:**
\`\`\`bash
# Create basic backup
pwgen backup create --output vault-backup.pwgen

# Create compressed backup
pwgen backup create --output vault-backup.pwgen --compression 9

# Create backup with custom password
pwgen backup create --output vault-backup.pwgen --password "backup-password"
\`\`\`

### \`backup verify\`

Verify backup file integrity.

\`\`\`bash
pwgen backup verify --file <FILE>
\`\`\`

### \`backup list\`

List contents of backup file without restoring.

\`\`\`bash
pwgen backup list --file <FILE>
\`\`\`

### \`restore\`

Restore vault from backup file.

\`\`\`bash
pwgen restore --file <FILE> [OPTIONS]
\`\`\`

**Options:**
- \`--output <PATH>\` - Restored vault location
- \`--merge\` - Merge with existing vault
- \`--force\` - Overwrite existing vault

## Analysis Commands

### \`analyze\`

Analyze vault security and password strength.

\`\`\`bash
pwgen analyze <TYPE> [OPTIONS]
\`\`\`

**Analysis Types:**
- \`weak\` - Find weak passwords
- \`duplicates\` - Find duplicate passwords
- \`old\` - Find old passwords
- \`breached\` - Check against known breaches (requires internet)
- \`strength\` - Overall password strength report

**Options:**
- \`--min-strength <LEVEL>\` - Minimum strength threshold (1-5)
- \`--max-age <DAYS>\` - Maximum password age in days
- \`--export <FILE>\` - Export analysis results

**Examples:**
\`\`\`bash
# Find weak passwords
pwgen analyze weak

# Find duplicates
pwgen analyze duplicates --format table

# Find old passwords
pwgen analyze old --max-age 90

# Check for breached passwords
pwgen analyze breached

# Generate strength report
pwgen analyze strength --export security-report.html
\`\`\`

## Completion Commands

### \`completion\`

Generate shell completion scripts.

\`\`\`bash
pwgen completion <SHELL>
\`\`\`

**Supported Shells:**
- \`bash\`
- \`zsh\`
- \`fish\`
- \`powershell\`

**Examples:**
\`\`\`bash
# Generate Bash completion
pwgen completion bash > ~/.bash_completion.d/pwgen

# Generate Zsh completion
pwgen completion zsh > ~/.zsh/completions/_pwgen

# Generate Fish completion
pwgen completion fish > ~/.config/fish/completions/pwgen.fish
\`\`\`

## Exit Codes

| Code | Description |
|------|-------------|
| 0 | Success |
| 1 | General error |
| 2 | Command not found |
| 3 | Invalid arguments |
| 4 | Vault not found or locked |
| 5 | Entry not found |
| 6 | Authentication failed |
| 7 | Permission denied |
| 8 | Import/export error |
| 9 | Network error |

## Common Usage Patterns

### Daily Workflow

\`\`\`bash
# Morning routine
pwgen vault unlock
pwgen password list --tag work

# Add new password
pwgen password add --site new-service.com --username user@company.com

# Copy password when needed
pwgen password copy --site github.com

# End of day
pwgen vault lock
\`\`\`

### Security Maintenance

\`\`\`bash
# Weekly security check
pwgen analyze weak
pwgen analyze duplicates
pwgen analyze old --max-age 90

# Monthly backup
pwgen backup create --output "monthly-backup-$(date +%Y%m).pwgen"

# Quarterly breach check
pwgen analyze breached
\`\`\`

### Bulk Operations

\`\`\`bash
# Update tags for multiple entries
pwgen password list --tag "old-work" --format json | $
  jq -r '.[].site' | $
  xargs -I {} pwgen password update --site {} --remove-tags "old-work" --add-tags "archived"

# Generate passwords for multiple services
echo "service1.com service2.com service3.com" | $
  tr ' ' '\\n' | $
  xargs -I {} pwgen password add --site {} --username admin --password "$(pwgen generate --length 16)"
\`\`\`

## See Also

- [CLI Overview](overview) - Introduction and basic concepts
- [Configuration](configuration) - Advanced configuration options
- [User Guide](../user-guide/passwords) - GUI and CLI integration
- [Security](../security/architecture) - Security implementation details`
  },

  'cli/configuration': {
    title: 'CLI Configuration',
    description: 'Comprehensive guide to configuring PWGen-Rust CLI',
    content: `# CLI Configuration

Comprehensive guide to configuring PWGen-Rust CLI for optimal workflow and security.

## Configuration Overview

PWGen-Rust CLI uses a hierarchical configuration system that prioritizes settings in this order:

1. **Command-line flags** (highest priority)
2. **Environment variables**
3. **Configuration file**
4. **Built-in defaults** (lowest priority)

This allows you to set global defaults while overriding specific options as needed.

## Configuration File

### Default Location

The configuration file is automatically created at:

- **Linux/macOS**: \`~/.config/pwgen/config.toml\`
- **Windows**: \`%APPDATA%\\pwgen\\config.toml\`

### Custom Location

You can specify a custom configuration file:

\`\`\`bash
# Using environment variable
export PWGEN_CONFIG_PATH="/path/to/custom/config.toml"

# Or using command-line flag
pwgen --config /path/to/custom/config.toml password list
\`\`\`

### Configuration Format

The configuration file uses TOML format for easy reading and editing:

\`\`\`toml
# ~/.config/pwgen/config.toml

[general]
default_vault = "~/.config/pwgen/vault.db"
auto_lock_minutes = 15
confirm_destructive = true

[cli]
default_format = "table"
editor = "vim"
pager = "less"
max_results = 100

[output]
colors = true
unicode = true
timestamp_format = "%Y-%m-%d %H:%M:%S"
date_format = "%Y-%m-%d"

[clipboard]
clear_after_seconds = 30
use_primary_selection = false
notification = true

[security]
require_master_password = true
session_timeout_minutes = 30
password_visibility_timeout = 5
audit_log = true

[generator]
default_length = 16
include_symbols = true
include_numbers = true
include_uppercase = true
include_lowercase = true
exclude_ambiguous = false

[import]
default_merge_strategy = "skip"
backup_before_import = true
validate_imports = true

[backup]
default_compression = 6
include_metadata = true
verify_after_create = true
\`\`\`

## Configuration Sections

### General Settings

Controls core application behavior:

\`\`\`toml
[general]
# Default vault location
default_vault = "~/.config/pwgen/vault.db"

# Auto-lock vault after inactivity (minutes)
auto_lock_minutes = 15

# Require confirmation for destructive operations
confirm_destructive = true

# Enable debug logging
debug = false

# Log file location (empty disables file logging)
log_file = "~/.config/pwgen/cli.log"

# Maximum log file size in MB
max_log_size = 10
\`\`\`

### CLI Settings

Customizes command-line interface behavior:

\`\`\`toml
[cli]
# Default output format: text, json, csv, table
default_format = "table"

# External editor for interactive editing
editor = "vim"

# Pager for long output
pager = "less"

# Maximum number of results to display
max_results = 100

# Show progress bars for long operations
show_progress = true

# Disable interactive prompts (for scripting)
non_interactive = false

# Command history file
history_file = "~/.config/pwgen/history"

# Maximum history entries
max_history = 1000
\`\`\`

### Output Settings

Controls how information is displayed:

\`\`\`toml
[output]
# Enable colored output
colors = true

# Use Unicode characters for tables and icons
unicode = true

# Timestamp format for display
timestamp_format = "%Y-%m-%d %H:%M:%S"

# Date format for display
date_format = "%Y-%m-%d"

# Show relative times (e.g., "2 days ago")
relative_times = true

# Truncate long values in table format
truncate_values = true

# Maximum width for truncated values
max_value_width = 30

# Table style: ascii, unicode, minimal
table_style = "unicode"
\`\`\`

### Clipboard Settings

Manages clipboard operations:

\`\`\`toml
[clipboard]
# Auto-clear clipboard after specified seconds
clear_after_seconds = 30

# Use primary selection (Linux/Unix X11)
use_primary_selection = false

# Show desktop notification when copying
notification = true

# Notification timeout in seconds
notification_timeout = 3

# Clear clipboard on application exit
clear_on_exit = true
\`\`\`

### Security Settings

Enhances security behavior:

\`\`\`toml
[security]
# Always require master password confirmation
require_master_password = true

# Session timeout in minutes
session_timeout_minutes = 30

# Hide password after specified seconds when displayed
password_visibility_timeout = 5

# Enable audit logging
audit_log = true

# Audit log file location
audit_log_file = "~/.config/pwgen/audit.log"

# Require 2FA for sensitive operations (if configured)
require_2fa = false

# Lock vault on system suspend/hibernate
lock_on_suspend = true

# Disable memory swapping for sensitive data
disable_swap = true
\`\`\`

### Generator Settings

Default password generation options:

\`\`\`toml
[generator]
# Default password length
default_length = 16

# Include symbols by default
include_symbols = true

# Include numbers by default
include_numbers = true

# Include uppercase letters by default
include_uppercase = true

# Include lowercase letters by default
include_lowercase = true

# Exclude ambiguous characters (0, O, l, 1, etc.)
exclude_ambiguous = false

# Custom character sets
custom_symbols = "!@#$%^&*"
custom_numbers = "0123456789"
custom_lowercase = "abcdefghijklmnopqrstuvwxyz"
custom_uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"

# Minimum character requirements
min_symbols = 1
min_numbers = 1
min_uppercase = 1
min_lowercase = 1
\`\`\`

### Import Settings

Controls import behavior:

\`\`\`toml
[import]
# Default merge strategy: skip, overwrite, merge
default_merge_strategy = "skip"

# Create backup before importing
backup_before_import = true

# Validate imported data
validate_imports = true

# Show import preview before applying
preview_imports = true

# Maximum import batch size
max_batch_size = 1000

# Import timeout in seconds
import_timeout = 300
\`\`\`

### Backup Settings

Configures backup operations:

\`\`\`toml
[backup]
# Default compression level (0-9)
default_compression = 6

# Include metadata in backups
include_metadata = true

# Verify backup integrity after creation
verify_after_create = true

# Default backup directory
backup_directory = "~/.config/pwgen/backups"

# Maximum number of automatic backups to keep
max_auto_backups = 10

# Automatic backup interval in days
auto_backup_days = 7
\`\`\`

## Environment Variables

Environment variables override configuration file settings:

### Core Variables

\`\`\`bash
# Vault location
export PWGEN_VAULT_PATH="/path/to/vault.db"

# Configuration file location
export PWGEN_CONFIG_PATH="/path/to/config.toml"

# Output format
export PWGEN_FORMAT="json"

# Disable colored output
export PWGEN_NO_COLOR=1

# Non-interactive mode
export PWGEN_NON_INTERACTIVE=1

# Debug mode
export PWGEN_DEBUG=1

# Log file location
export PWGEN_LOG_FILE="/path/to/logfile.log"
\`\`\`

### Editor and Pager

\`\`\`bash
# External editor (overrides config file)
export PWGEN_EDITOR="nano"
export EDITOR="nano"  # Fallback

# Pager for long output
export PWGEN_PAGER="bat"
export PAGER="less"  # Fallback
\`\`\`

### Security Variables

\`\`\`bash
# Session timeout in minutes
export PWGEN_SESSION_TIMEOUT=30

# Clipboard clear timeout in seconds
export PWGEN_CLIPBOARD_TIMEOUT=60

# Disable audit logging
export PWGEN_NO_AUDIT=1

# Force master password prompt
export PWGEN_REQUIRE_PASSWORD=1
\`\`\`

### Import/Export Variables

\`\`\`bash
# Default import strategy
export PWGEN_IMPORT_STRATEGY="merge"

# Backup before import
export PWGEN_BACKUP_BEFORE_IMPORT=1

# Default export format
export PWGEN_EXPORT_FORMAT="csv"
\`\`\`

## Platform-Specific Configuration

### Linux Configuration

\`\`\`toml
[platform.linux]
# Use native keyring integration
use_keyring = true

# Keyring service name
keyring_service = "pwgen-rust"

# X11 clipboard integration
x11_clipboard = true

# Wayland clipboard integration
wayland_clipboard = true

# Desktop notifications via D-Bus
desktop_notifications = true
\`\`\`

### macOS Configuration

\`\`\`toml
[platform.macos]
# Use macOS Keychain integration
use_keychain = true

# Keychain service name
keychain_service = "dev.pwgenrust.cli"

# Use Touch ID for authentication
use_touchid = true

# Secure Enclave integration
use_secure_enclave = true

# macOS notification center
notification_center = true
\`\`\`

### Windows Configuration

\`\`\`toml
[platform.windows]
# Use Windows Credential Manager
use_credential_manager = true

# Credential target name
credential_target = "pwgen-rust-cli"

# Windows notification system
windows_notifications = true

# Windows Hello integration
use_windows_hello = true
\`\`\`

## Advanced Configuration

### Custom Commands

Define aliases for common operations:

\`\`\`toml
[aliases]
# Shorthand commands
ls = "password list"
find = "password search"
gen = "generate"
cp = "password copy"
rm = "password delete"

# Complex aliases with parameters
backup-daily = "backup create --output ~/backups/daily-$(date +%Y%m%d).pwgen"
audit = "analyze weak && analyze duplicates && analyze old --max-age 90"
\`\`\`

### Format Templates

Customize output formats:

\`\`\`toml
[templates]
# Custom table format
password_table = "{{ id | pad(3) }} | {{ site | truncate(20) }} | {{ username | truncate(15) }} | {{ tags | join(',') }}"

# Custom JSON format
password_json = '''
{
  "id": {{ id }},
  "site": "{{ site }}",
  "username": "{{ username }}",
  "created": "{{ created | date('%Y-%m-%d') }}",
  "tags": [{{ tags | map(quote) | join(',') }}]
}
'''
\`\`\`

### Plugin Configuration

Configure external integrations:

\`\`\`toml
[plugins]
# 1Password CLI integration
onepassword_cli = "/usr/local/bin/op"

# Bitwarden CLI integration
bitwarden_cli = "/usr/local/bin/bw"

# Custom password strength checker
strength_checker = "/usr/local/bin/zxcvbn"

# External breach checker
breach_checker = "https://api.pwnedpasswords.com/range/"
\`\`\`

## Configuration Validation

### Validate Configuration

Check your configuration file for errors:

\`\`\`bash
# Validate configuration file
pwgen config validate

# Show current configuration
pwgen config show

# Show configuration with sources
pwgen config show --verbose

# Test specific setting
pwgen config test clipboard.clear_after_seconds
\`\`\`

### Configuration Errors

Common configuration issues and solutions:

\`\`\`bash
# Invalid TOML syntax
pwgen config validate
# Error: Invalid TOML at line 15: expected '=' after key

# Invalid value type
pwgen config validate
# Error: security.session_timeout_minutes must be a number

# Missing required value
pwgen config validate
# Error: general.default_vault path does not exist
\`\`\`

## Best Practices

### Security Best Practices

1. **Protect Configuration Files**:
   \`\`\`bash
   # Set restrictive permissions
   chmod 600 ~/.config/pwgen/config.toml
   chmod 700 ~/.config/pwgen/
   \`\`\`

2. **Sensitive Values**:
   \`\`\`bash
   # Use environment variables for sensitive settings
   export PWGEN_VAULT_PATH="/secure/location/vault.db"
   # Don't put vault passwords in config files
   \`\`\`

3. **Audit Configuration**:
   \`\`\`bash
   # Regularly review settings
   pwgen config show | grep -E "(log|audit|timeout)"
   \`\`\`

### Performance Optimization

1. **Large Vaults**:
   \`\`\`toml
   [cli]
   max_results = 50  # Limit results for faster display
   
   [output]
   truncate_values = true  # Truncate long values
   \`\`\`

2. **Frequent Operations**:
   \`\`\`toml
   [general]
   auto_lock_minutes = 60  # Reduce lock frequency
   
   [clipboard]
   clear_after_seconds = 60  # Longer clipboard timeout
   \`\`\`

### Workflow Optimization

1. **Daily Use**:
   \`\`\`toml
   [cli]
   default_format = "table"  # Easy to read
   show_progress = true      # Visual feedback
   
   [aliases]
   work = "password list --tag work"
   personal = "password list --tag personal"
   \`\`\`

2. **Scripting**:
   \`\`\`toml
   [cli]
   non_interactive = true    # No prompts
   default_format = "json"   # Machine readable
   
   [output]
   colors = false           # Clean output
   \`\`\`

## Configuration Examples

### Minimal Configuration

Basic setup for new users:

\`\`\`toml
[general]
default_vault = "~/pwgen-vault.db"
auto_lock_minutes = 30

[cli]
default_format = "table"

[clipboard]
clear_after_seconds = 45
\`\`\`

### Power User Configuration

Advanced setup with all features:

\`\`\`toml
[general]
default_vault = "~/Documents/Security/pwgen.vault"
auto_lock_minutes = 10
confirm_destructive = true
debug = false
log_file = "~/.local/share/pwgen/cli.log"

[cli]
default_format = "table"
editor = "nvim"
pager = "bat"
max_results = 50
show_progress = true
history_file = "~/.local/share/pwgen/history"

[output]
colors = true
unicode = true
timestamp_format = "%Y-%m-%d %H:%M"
relative_times = true
table_style = "unicode"

[clipboard]
clear_after_seconds = 30
notification = true
clear_on_exit = true

[security]
session_timeout_minutes = 15
password_visibility_timeout = 3
audit_log = true
lock_on_suspend = true

[generator]
default_length = 20
exclude_ambiguous = true
min_symbols = 2
min_numbers = 2

[aliases]
ls = "password list"
find = "password search"
daily-backup = "backup create --output ~/Backups/pwgen-$(date +%Y%m%d).pwgen"
security-check = "analyze weak && analyze duplicates"
\`\`\`

### Team Configuration

Shared settings for team environments:

\`\`\`toml
[general]
confirm_destructive = true
log_file = "/var/log/pwgen/team.log"

[cli]
default_format = "json"
non_interactive = true
max_results = 1000

[security]
audit_log = true
audit_log_file = "/var/log/pwgen/audit.log"
require_master_password = true

[backup]
backup_directory = "/shared/backups/pwgen"
auto_backup_days = 1
verify_after_create = true

[import]
backup_before_import = true
preview_imports = true
\`\`\`

## Troubleshooting

### Common Issues

1. **Configuration Not Loading**:
   \`\`\`bash
   # Check file location
   pwgen config show --verbose
   
   # Verify file permissions
   ls -la ~/.config/pwgen/config.toml
   \`\`\`

2. **Environment Variable Conflicts**:
   \`\`\`bash
   # Show all PWGen-Rust environment variables
   env | grep PWGEN
   
   # Clear conflicting variables
   unset PWGEN_FORMAT
   \`\`\`

3. **Invalid Settings**:
   \`\`\`bash
   # Validate configuration
   pwgen config validate
   
   # Reset to defaults
   pwgen config reset
   \`\`\`

### Debug Mode

Enable detailed logging for troubleshooting:

\`\`\`bash
# Enable debug mode
export PWGEN_DEBUG=1
pwgen password list

# Or in config file
[general]
debug = true
log_file = "~/.config/pwgen/debug.log"
\`\`\`

## See Also

- [CLI Overview](overview) - Introduction to CLI usage
- [Commands Reference](commands) - Complete command documentation
- [User Guide](../user-guide/configuration) - GUI configuration
- [Security](../security/architecture) - Security implementation details`
  },

  // Security
  'security/architecture': {
    title: 'Security Architecture',
    description: 'PWGen-Rust\'s zero-knowledge security design',
    content: `# Security Architecture

PWGen-Rust is built with a **zero-knowledge, security-first architecture** designed to protect your sensitive data.

## 🏗️ Core Security Principles

### 1. Zero-Knowledge Architecture

**Your master password never leaves your device.**

\`\`\`
┌─────────────────┐    ┌──────────────────┐    ┌─────────────────┐
│   Your Device   │    │   PWGen-Rust Vault    │    │   External      │
│                 │    │                  │    │   Systems       │
│ Master Password │────▶│ Local Encryption │    │                 │
│ (memory only)   │    │ AES-256-GCM      │    │ ❌ No Access    │
│                 │    │                  │    │ ❌ No Storage   │
└─────────────────┘    └──────────────────┘    └─────────────────┘
\`\`\`

**Key Benefits:**
- **No server-side access** to your passwords
- **No recovery backdoors** or master keys
- **Complete user control** over encryption keys
- **Immunity to server breaches** - no passwords stored remotely

### 2. Defense in Depth

Multiple security layers protect your data:

1. **Application Layer** - Input validation, secure coding practices
2. **Encryption Layer** - AES-256-GCM symmetric encryption
3. **Key Derivation Layer** - Argon2id password-based key derivation
4. **Storage Layer** - Encrypted SQLite database
5. **Memory Layer** - Secure memory handling, automatic zeroization
6. **OS Layer** - Platform-specific security features

## 🔐 Cryptographic Implementation

### Encryption Algorithm: AES-256-GCM

PWGen-Rust uses **AES-256-GCM** (Advanced Encryption Standard, 256-bit, Galois/Counter Mode):

**Why AES-256-GCM?**
- **NIST approved** - Federal Information Processing Standard
- **AEAD encryption** - Authenticated Encryption with Associated Data
- **Integrity protection** - Built-in authentication
- **Performance optimized** - Hardware acceleration available
- **Quantum resistant** - 256-bit keys resist quantum attacks

**Implementation Details:**
\`\`\`rust
// Rust implementation (simplified)
use aes_gcm::{Aes256Gcm, Key, Nonce};
use aes_gcm::aead::{Aead, NewAead};

// 256-bit key derived from master password
let key = Key::from_slice(&derived_key);
let cipher = Aes256Gcm::new(key);

// 96-bit nonce (never reused)
let nonce = Nonce::from_slice(&random_nonce);

// Encrypt with authentication
let ciphertext = cipher.encrypt(nonce, plaintext.as_ref())?;
\`\`\`

### Key Derivation: Argon2id

**Argon2id** is used to derive encryption keys from your master password:

**Parameters:**
- **Memory Cost**: 4 GB (configurable)
- **Time Cost**: 3 iterations (configurable)
- **Parallelism**: 4 threads (matches CPU cores)
- **Salt**: 32 random bytes per vault
- **Output**: 256-bit encryption key

**Why Argon2id?**
- **Winner of Password Hashing Competition** (2015)
- **Memory-hard function** - Resists ASIC attacks
- **Side-channel resistant** - Protects against timing attacks
- **Configurable difficulty** - Adapts to hardware improvements

\`\`\`rust
// Key derivation implementation
use argon2::{Argon2, Version, Variant};

let config = Config {
    variant: Variant::Argon2id,
    version: Version::Version13,
    mem_cost: 4 * 1024 * 1024,  // 4 GB
    time_cost: 3,
    lanes: 4,
    secret: &[],
    ad: &[],
    hash_length: 32,
};

let key = argon2::hash_raw(
    master_password.as_bytes(),
    &salt,
    &config
)?;
\`\`\`

## 🗄️ Database Security

### SQLite with Encryption

PWGen-Rust uses **SQLite** with application-layer encryption:

**Database Structure:**
\`\`\`sql
-- Simplified schema
CREATE TABLE entries (
    id INTEGER PRIMARY KEY,
    site TEXT NOT NULL,
    username TEXT,
    password_encrypted BLOB NOT NULL,  -- AES-256-GCM encrypted
    notes_encrypted BLOB,              -- Optional encrypted notes
    tags TEXT,                         -- Comma-separated tags
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL,
    last_accessed INTEGER
);

CREATE TABLE metadata (
    key TEXT PRIMARY KEY,
    value_encrypted BLOB               -- Encrypted configuration
);
\`\`\`

**Encryption Strategy:**
- **Field-level encryption** - Sensitive fields encrypted individually
- **Unique nonces** - Each encrypted field uses unique nonce
- **Integrity protection** - GCM mode provides authentication
- **Metadata protection** - Even configuration is encrypted

### File System Security

**Database File Protection:**
\`\`\`bash
# Secure file permissions (Unix)
chmod 600 ~/.local/share/pwgen/vault.db

# Secure directory permissions
chmod 700 ~/.local/share/pwgen/
\`\`\`

**Location Strategy:**
- **Platform-specific locations** - Follow OS security guidelines
- **User-only access** - Readable only by the owning user
- **No temporary files** - All operations in-memory when possible

## 🧠 Memory Security

### Secure Memory Handling

PWGen-Rust implements comprehensive memory security:

**Automatic Zeroization:**
\`\`\`rust
use zeroize::{Zeroize, ZeroizeOnDrop};

#[derive(ZeroizeOnDrop)]
struct MasterPassword {
    data: Vec<u8>,
}

impl Drop for MasterPassword {
    fn drop(&mut self) {
        self.data.zeroize();  // Overwrites memory with zeros
    }
}
\`\`\`

**Memory Protection Features:**
- **Stack protection** - Sensitive data cleared from stack
- **Heap protection** - Encrypted passwords zeroized after use
- **Swap protection** - Memory marked as non-swappable where possible
- **Core dump protection** - Sensitive memory excluded from dumps

### Anti-Debugging Measures

**Runtime Protection:**
- **Debugger detection** - Detects if debugger is attached
- **Memory scanning protection** - Obfuscates sensitive data in memory
- **Time-based protections** - Detects abnormal execution timing

## 🔍 Threat Model

### Protected Against

#### ✅ **Local Attacks**
- **Malware scanning memory** - Secure memory handling
- **Disk forensics** - Strong encryption at rest
- **User account compromise** - Master password required
- **Physical device theft** - Encrypted database files

#### ✅ **Network Attacks**
- **Man-in-the-middle** - No network communication
- **Server breaches** - No server-side data storage
- **DNS hijacking** - No external dependencies
- **Traffic analysis** - No network traffic to analyze

#### ✅ **Cryptographic Attacks**
- **Brute force** - Argon2id makes this computationally expensive
- **Dictionary attacks** - Strong key derivation protects weak passwords
- **Rainbow tables** - Unique salts prevent precomputed attacks
- **Side-channel attacks** - Constant-time implementations

### Attack Scenarios & Mitigations

#### **Scenario 1: Malware on Device**
**Attack:** Malware attempts to steal passwords from memory
**Mitigation:** 
- Secure memory handling with automatic zeroization
- Master password timeout and auto-lock
- Minimal memory exposure window

#### **Scenario 2: Database File Theft**
**Attack:** Attacker steals encrypted database file
**Mitigation:**
- AES-256-GCM encryption requires master password
- Argon2id makes brute force attacks expensive
- No recovery mechanism prevents backdoor attacks

#### **Scenario 3: Shoulder Surfing**
**Attack:** Attacker observes master password entry
**Mitigation:**
- Session timeout automatically locks vault
- Option for biometric authentication (platform-dependent)
- Screen privacy filters (user responsibility)

#### **Scenario 4: Physical Device Access**
**Attack:** Attacker gains physical access to unlocked device
**Mitigation:**
- Automatic locking on screen saver/sleep
- Short timeout periods (configurable)
- OS-level device security (full disk encryption)

## 🛡️ Security Updates

### Vulnerability Response

**Security Update Process:**
1. **Immediate assessment** - Severity classification
2. **Patch development** - Fix with minimal disruption
3. **Security advisory** - Public disclosure with details
4. **Automatic updates** - Critical patches pushed immediately

### Cryptographic Agility

**Future-Proofing:**
- **Algorithm flexibility** - Can migrate to new algorithms
- **Key size adaptation** - Supports larger key sizes
- **Protocol versioning** - Backward compatibility with security upgrades

## 🔬 Security Auditing

### Regular Security Reviews

**Internal Auditing:**
- **Static code analysis** - Automated security scanning
- **Dynamic testing** - Runtime security validation
- **Penetration testing** - Simulated attack scenarios
- **Cryptographic review** - Expert analysis of implementations

### Third-Party Audits

PWGen-Rust undergoes regular third-party security audits:
- **Code review** - Independent security analysis
- **Cryptographic validation** - Algorithm implementation verification
- **Threat modeling** - Attack scenario analysis
- **Compliance verification** - Security standard compliance

## 📋 Security Certifications

### Standards Compliance

**Current Compliance:**
- **NIST Cybersecurity Framework** - Core security principles
- **OWASP ASVS Level 2** - Application security verification
- **Common Criteria** - Security evaluation criteria (in progress)

**Security Approach:**
PWGen-Rust follows industry security best practices but is not currently certified. As an open-source project, security comes from transparent code review and community auditing.

## 🔒 Best Practices for Users

### Master Password Security
- **Use a unique, strong master password** - Never reuse elsewhere
- **Consider a long passphrase** - Multiple random words
- **Don't write it down** - Memorize your master password
- **Enable auto-lock** - Minimize exposure window

### Operational Security
- **Keep PWGen-Rust updated** - Install security patches promptly
- **Use full disk encryption** - Protect the database file at rest
- **Regular backups** - Store encrypted backups securely
- **Monitor for breaches** - Check if your passwords are compromised

### Environment Security
- **Secure your device** - Use strong device authentication
- **Trusted networks only** - Avoid public WiFi for sensitive operations
- **Physical security** - Lock your screen when away
- **Malware protection** - Keep antivirus updated

## 🎯 Conclusion

PWGen-Rust's security architecture provides **defense-in-depth protection** through:

1. **Zero-knowledge design** - No trust in external systems
2. **Modern cryptography** - Industry-standard algorithms
3. **Secure implementation** - Memory-safe Rust code
4. **Comprehensive protection** - Multiple security layers
5. **Regular auditing** - Continuous security validation

Your passwords are protected by the same encryption used by governments and banks, implemented with the memory safety of Rust, and designed with a zero-trust architecture.

**Remember: Security is only as strong as its weakest link. Follow best practices and keep PWGen-Rust updated for maximum protection.** 🔐`
  },

  // User Guide
  'user-guide/passwords': {
    title: 'Password Management',
    description: 'Master secure password storage and management',
    content: `# Password Management

Master the art of secure password storage and management with PWGen-Rust.

## Overview

PWGen-Rust's password management system provides secure, encrypted storage for all your login credentials using AES-256-GCM encryption with Argon2 key derivation. Each password entry can include multiple fields and metadata for comprehensive organization.

## Entry Structure

Each password entry contains:

- **Site**: Website URL or application name
- **Username**: Login identifier (email, username, etc.)
- **Password**: The actual password (encrypted)
- **Notes**: Additional information or recovery details
- **Tags**: Organization labels for easy filtering
- **Favorites**: Quick access marking
- **Created/Modified**: Automatic timestamps
- **Password History**: Previous passwords for recovery

## Adding Password Entries

### GUI Method

1. **Navigate to Passwords Tab**
   - Click the 🔑 **Passwords** tab in the main interface

2. **Create New Entry**
   - Click **➕ Add Entry** button
   - Fill in the entry form:
     - **Site**: Enter the website or service name
     - **Username**: Your login username or email
     - **Password**: Use generator or enter manually
     - **Notes**: Optional additional information
     - **Tags**: Comma-separated organization tags

3. **Use Password Generator**
   - Click the **🎲 Generate** button next to password field
   - Adjust generation settings if needed
   - Generator will create a secure password automatically

4. **Save Entry**
   - Click **Save** to encrypt and store the entry
   - Entry will appear in your password list immediately

### CLI Method

\`\`\`bash
# Interactive password entry
pwgen password add --site github.com --username myuser@example.com

# With password specified
pwgen password add --site github.com --username myuser@example.com --password "SecurePass123!"

# Generate password automatically
pwgen password add --site github.com --username myuser@example.com --generate

# With additional metadata
pwgen password add $
  --site github.com $
  --username myuser@example.com $
  --generate $
  --notes "Personal GitHub account" $
  --tags "dev,personal"
\`\`\`

## Searching and Filtering

### Quick Search

**GUI Search:**
- Use the search box at the top of the password list
- Search is real-time and searches across all fields
- Results filter automatically as you type

**Search Capabilities:**
- Site names and URLs
- Usernames and email addresses
- Notes and additional information
- Tag names and categories

### Advanced Filtering

**Filter by Tags:**
- Click on any tag button to filter entries
- Multiple tags can be selected for intersection filtering
- Use tag combinations for precise organization

**Filter by Favorites:**
- Click the ⭐ button to show only favorite entries
- Great for frequently accessed accounts

**CLI Search:**
\`\`\`bash
# Search across all fields
pwgen search "github"

# Search specific field
pwgen search --field site "github.com"
pwgen search --field username "myuser"

# Search with tag filter
pwgen search --tag "work" "server"

# List all entries
pwgen list

# List with specific tag
pwgen list --tag "banking"
\`\`\`

## Accessing Passwords

### Copying Credentials

**GUI Copy Operations:**
- **🔑 Copy Password**: Click to copy password to clipboard
- **👤 Copy Username**: Click to copy username to clipboard
- **Automatic Clearing**: Clipboard clears after 30 seconds for security

**CLI Copy Operations:**
\`\`\`bash
# Copy password to clipboard
pwgen copy --site github.com

# Copy username instead
pwgen copy --site github.com --username

# Copy specific entry by ID
pwgen copy --id 123
\`\`\`

### Viewing Entry Details

**GUI Details:**
- Click on any entry to view full details
- All fields are displayed in a detailed view
- Password history is available if entries were updated

**CLI Details:**
\`\`\`bash
# Show entry details
pwgen show --site github.com

# Show with password visible
pwgen show --site github.com --show-password

# Show entry by ID
pwgen show --id 123
\`\`\`

## Editing and Updating

### Modifying Existing Entries

**GUI Editing:**
1. Click on the entry to select it
2. Click the **✏️ Edit** button
3. Modify any fields as needed
4. Previous password is automatically saved to history
5. Click **Save** to apply changes

**CLI Editing:**
\`\`\`bash
# Update password
pwgen password update --site github.com --password "NewPassword123!"

# Update username
pwgen password update --site github.com --username "newuser@example.com"

# Update multiple fields
pwgen password update $
  --site github.com $
  --username "newuser@example.com" $
  --notes "Updated account credentials" $
  --tags "dev,work"

# Generate new password
pwgen password update --site github.com --generate
\`\`\`

### Password History

PWGen-Rust automatically maintains a history of password changes:

**Accessing History:**
- In GUI: View entry details to see password history
- In CLI: Use \`pwgen history --site github.com\`

**History Features:**
- Up to 10 previous passwords stored
- Timestamps for each password change
- Useful for sites with password history requirements
- Helps recover from accidental password changes

## Organization with Tags

### Tag System

Tags provide flexible organization for your password entries:

**Common Tag Patterns:**
- **Category Tags**: \`work\`, \`personal\`, \`banking\`, \`social\`
- **Priority Tags**: \`important\`, \`critical\`, \`backup\`
- **Service Tags**: \`dev\`, \`email\`, \`cloud\`, \`gaming\`
- **Security Tags**: \`2fa\`, \`recovery\`, \`shared\`

### Managing Tags

**Adding Tags:**
\`\`\`bash
# Add tags to existing entry
pwgen password update --site github.com --add-tags "important,backup"

# Replace all tags
pwgen password update --site github.com --tags "work,dev,github"
\`\`\`

**Removing Tags:**
\`\`\`bash
# Remove specific tags
pwgen password update --site github.com --remove-tags "backup"
\`\`\`

**Tag Operations:**
\`\`\`bash
# List all tags in use
pwgen tags list

# Find entries with specific tag
pwgen list --tag "work"

# Multiple tag filtering
pwgen list --tags "work,important"
\`\`\`

## Favorites System

### Marking Favorites

**GUI Favorites:**
- Click the ⭐ icon on any entry to mark as favorite
- Favorite entries appear at the top of lists
- Use the favorites filter to show only starred entries

**CLI Favorites:**
\`\`\`bash
# Mark as favorite
pwgen password update --site github.com --favorite

# Remove from favorites
pwgen password update --site github.com --no-favorite

# List only favorites
pwgen list --favorites
\`\`\`

## Security Features

### Password Strength Analysis

PWGen-Rust automatically analyzes password strength:

**Strength Indicators:**
- **Weak**: Short passwords, common patterns
- **Medium**: Adequate length, some complexity
- **Strong**: Long passwords with high entropy
- **Very Strong**: Maximum complexity and length

**Improvement Suggestions:**
- Automatic recommendations for weak passwords
- Integration with password generator for upgrades
- Warnings for reused passwords across entries

### Duplicate Detection

**Duplicate Password Detection:**
- Automatic detection of reused passwords
- Warnings when entering duplicate passwords
- Search for all entries using the same password

**CLI Duplicate Analysis:**
\`\`\`bash
# Find duplicate passwords
pwgen analyze duplicates

# Find weak passwords
pwgen analyze weak

# Security audit of all passwords
pwgen analyze security
\`\`\`

## Import and Migration

### Bulk Import

**Supported Formats:**
- CSV (browser exports, spreadsheets)
- JSON (other password managers)
- 1PIF (1Password)
- XML (KeePass)

**Import Process:**
\`\`\`bash
# Import from CSV
pwgen import csv --file passwords.csv

# Import from browser
pwgen import browser --browser chrome

# Import from other managers
pwgen import keepass --file database.xml
pwgen import bitwarden --file export.json
\`\`\`

## Best Practices

### Password Creation

1. **Use the Generator**: Always prefer generated passwords over manual ones
2. **Unique Passwords**: Never reuse passwords across different services
3. **Appropriate Length**: Use 16+ characters for high-security accounts
4. **Regular Updates**: Change passwords every 90 days for sensitive accounts

### Organization

1. **Consistent Tagging**: Develop a consistent tag system for organization
2. **Descriptive Names**: Use clear, searchable site names
3. **Detailed Notes**: Include recovery information and account details
4. **Regular Cleanup**: Remove old or unused entries periodically

### Security

1. **Regular Audits**: Use the analysis tools to find weak passwords
2. **Immediate Updates**: Change passwords immediately after security breaches
3. **Favorite Management**: Keep frequently used accounts easily accessible
4. **Backup Regularly**: Ensure password database is backed up securely

## Advanced Features

### Password Policies

Set up password policies for different types of accounts:

\`\`\`bash
# Set minimum requirements
pwgen policy create --name "banking" --min-length 20 --require-symbols

# Apply policy to entries
pwgen password update --site bank.com --policy "banking"
\`\`\`

### Automated Workflows

**CLI Automation:**
\`\`\`bash
# Daily security check
pwgen analyze security --format json > security-report.json

# Batch password updates
pwgen list --tag "old" | xargs -I {} pwgen password update --id {} --generate

# Export specific categories
pwgen export --tag "work" --format csv > work-passwords.csv
\`\`\`

## Troubleshooting

### Common Issues

**Entry Not Found:**
- Check spelling in search terms
- Try searching by username instead of site
- Verify correct vault is unlocked

**Copy Not Working:**
- Check clipboard permissions
- Try manual selection from entry details
- Restart application if clipboard is stuck

**Slow Performance:**
- Large password databases (1000+ entries) may load slowly
- Use search and filtering to narrow results
- Consider archiving old entries

### Recovery Options

**Password Recovery:**
- Use password history for recently changed passwords
- Check notes for recovery information
- Review email for password reset options

**Entry Recovery:**
- Check backups for accidentally deleted entries
- Use import functionality to restore from backups
- Contact support if database corruption is suspected

## Next Steps

- [Secrets Management](secrets) - Beyond passwords
- [Password Generator](generator) - Advanced generation options
- [Import & Export](import-export) - Data migration tools`
  },

  'user-guide/generator': {
    title: 'Password Generator',
    description: 'Create strong, secure passwords with customizable options',
    content: `# Password Generator

Create strong, secure passwords with customizable generation options.

## Overview

PWGen-Rust's password generator creates cryptographically secure passwords using Rust's cryptographically secure random number generator (CSPRNG). The generator supports various character sets, length requirements, and security patterns to meet different password policies.

## Generation Options

### Character Sets

**Uppercase Letters (A-Z):**
- Essential for password complexity
- Required by most password policies
- Provides 26 additional character options

**Lowercase Letters (a-z):**
- Base character set for most passwords
- Improves readability and typing
- Provides 26 character options

**Numbers (0-9):**
- Adds numeric complexity
- Required by many security policies
- Provides 10 additional character options

**Symbols (!@#$%^&*):**
- Significantly increases password entropy
- Common symbols: \`!@#$%^&*()_+-=[]{}|;:,.<>?\`
- Some systems may restrict certain symbols

### Advanced Options

**Exclude Ambiguous Characters:**
- Removes visually similar characters
- Excludes: \`0\` (zero), \`O\` (capital O), \`l\` (lowercase L), \`I\` (capital I)
- Improves password readability and reduces typing errors

**Exclude Similar Characters:**
- Removes characters that might be confused
- Excludes: \`il1Lo0O\`
- Helpful for passwords that need to be read aloud or written

**Pronounceable Passwords:**
- Generates passwords following syllable patterns
- Easier to remember but slightly less secure
- Good for temporary passwords or recovery codes

## Using the Generator

### GUI Generator

1. **Navigate to Generator Tab**
   - Click the **🎲 Generator** tab in the main interface

2. **Configure Settings**
   - **Length**: Set password length (8-128 characters)
   - **Character Types**: Select uppercase, lowercase, numbers, symbols
   - **Exclusions**: Choose ambiguous character handling
   - **Pattern**: Select generation pattern (random, pronounceable, etc.)

3. **Generate Password**
   - Click **🎲 Generate New Password**
   - Password appears in the preview field
   - Click **🔄 Regenerate** for new password
   - Click **📋 Copy** to copy to clipboard

4. **Save to Entry**
   - Click **💾 Save as Entry** to create new password entry
   - Or use **📝 Use in Entry** to apply to existing entry

### CLI Generator

**Basic Generation:**
\`\`\`bash
# Generate with default settings (16 characters)
pwgen generate

# Generate with specific length
pwgen generate --length 24

# Generate multiple passwords
pwgen generate --count 5
\`\`\`

**Character Set Control:**
\`\`\`bash
# Only uppercase and lowercase
pwgen generate --length 20 --upper --lower

# Include symbols
pwgen generate --length 16 --upper --lower --numbers --symbols

# Exclude ambiguous characters
pwgen generate --length 16 --no-ambiguous

# Pronounceable password
pwgen generate --length 12 --pronounceable
\`\`\`

**Advanced Options:**
\`\`\`bash
# Custom character set
pwgen generate --length 16 --charset "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"

# Pattern-based generation
pwgen generate --pattern "Llll-dddd-LLLL" # Letter-digit patterns

# Exclude specific characters
pwgen generate --length 16 --exclude "0O1l"
\`\`\`

## Generation Patterns

### Random Generation

**Default Pattern:**
- Completely random character selection
- Maximum entropy and security
- No predictable patterns

**Characteristics:**
- High entropy (3.3 bits per character for full charset)
- Passes most security audits
- Difficult to remember but most secure

### Pronounceable Generation

**Syllable-Based:**
- Follows consonant-vowel patterns
- Easier to remember and pronounce
- Slightly reduced entropy

**Pattern Examples:**
- \`ba-ri-tu-mo\` (8 characters)
- \`ke-lar-ni-dox\` (10 characters)
- \`pa-tel-ka-mor-ne\` (12 characters)

### Pattern-Based Generation

**Custom Patterns:**
- Use templates to define password structure
- Combine predictable structure with random content
- Good for passwords that need specific formats

**Pattern Syntax:**
- \`L\` = Uppercase letter
- \`l\` = Lowercase letter  
- \`d\` = Digit
- \`s\` = Symbol
- \`?\` = Any character from selected sets

**Pattern Examples:**
\`\`\`bash
# Credit card PIN style
pwgen generate --pattern "dddd"

# Windows license key style
pwgen generate --pattern "LLLLL-LLLLL-LLLLL-LLLLL-LLLLL"

# Mixed complexity
pwgen generate --pattern "Lllldddd!!"
\`\`\`

## Security Considerations

### Entropy Calculation

**Character Set Size:**
- Lowercase only: 26 characters = 4.7 bits per character
- + Uppercase: 52 characters = 5.7 bits per character
- + Numbers: 62 characters = 5.95 bits per character
- + Symbols: 94 characters = 6.55 bits per character

**Password Strength:**
- 8 characters (full charset): 52.4 bits (weak)
- 12 characters (full charset): 78.6 bits (good)
- 16 characters (full charset): 104.8 bits (strong)
- 20 characters (full charset): 131 bits (very strong)

### Security Recommendations

**Length Guidelines:**
- **Minimum**: 12 characters for personal accounts
- **Recommended**: 16 characters for important accounts
- **High Security**: 20+ characters for sensitive systems
- **Passphrases**: 5-7 words for memorizable security

**Character Set Usage:**
- Always include uppercase and lowercase letters
- Include numbers for policy compliance
- Add symbols for maximum security
- Avoid if system doesn't support all symbols

## Integration with Password Entries

### Direct Integration

**GUI Integration:**
- Generator tab integrates with password entries
- One-click password generation during entry creation
- Automatic application to selected entries

**CLI Integration:**
\`\`\`bash
# Generate and add to new entry
pwgen password add --site github.com --username user --generate

# Update existing entry with generated password
pwgen password update --site github.com --generate

# Generate with specific requirements
pwgen password add --site bank.com --username user --generate --length 20 --symbols
\`\`\`

### Batch Generation

**Multiple Passwords:**
\`\`\`bash
# Generate multiple passwords for review
pwgen generate --count 10 --length 16

# Generate with different patterns
pwgen generate --count 5 --pattern "Llll-dddd-LLLL"

# Export to file
pwgen generate --count 20 --output passwords.txt
\`\`\`

## Customization and Profiles

### Generator Profiles

**Creating Profiles:**
\`\`\`bash
# Create profile for banking passwords
pwgen profile create banking $
  --length 20 $
  --upper --lower --numbers --symbols $
  --no-ambiguous

# Create profile for development
pwgen profile create dev $
  --length 16 $
  --upper --lower --numbers $
  --exclude "'\\"\`"

# Create pronounceable profile
pwgen profile create memorable $
  --length 12 $
  --pronounceable
\`\`\`

**Using Profiles:**
\`\`\`bash
# Generate using profile
pwgen generate --profile banking

# Add entry with profile
pwgen password add --site bank.com --username user --profile banking
\`\`\`

### Custom Character Sets

**Defining Custom Sets:**
\`\`\`bash
# Base64-like character set
pwgen generate --charset "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"

# Hexadecimal passwords
pwgen generate --charset "0123456789ABCDEF" --length 32

# ASCII printable without quotes
pwgen generate --charset "!#$%&()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[]^_abcdefghijklmnopqrstuvwxyz{|}~"
\`\`\`

## Testing and Validation

### Password Strength Testing

**Built-in Analysis:**
\`\`\`bash
# Test password strength
pwgen analyze strength "MyPassword123!"

# Test against common password lists
pwgen analyze dictionary "password123"

# Test entropy calculation
pwgen analyze entropy "random-password"
\`\`\`

**Strength Metrics:**
- Entropy calculation (bits of security)
- Dictionary attack resistance
- Pattern analysis
- Character distribution analysis

### Compliance Testing

**Policy Validation:**
\`\`\`bash
# Test against common policies
pwgen validate policy "MyPassword123!" --policy windows
pwgen validate policy "MyPassword123!" --policy unix
pwgen validate policy "MyPassword123!" --policy custom.json
\`\`\`

**Custom Policy Definition:**
\`\`\`json
{
  "name": "Corporate Policy",
  "min_length": 12,
  "max_length": 32,
  "require_uppercase": true,
  "require_lowercase": true,
  "require_numbers": true,
  "require_symbols": true,
  "forbidden_patterns": ["123", "abc", "password"],
  "max_repeating": 2
}
\`\`\`

## Performance Considerations

### Generation Speed

**Optimization Tips:**
- Simple character sets generate faster
- Pattern-based generation is slower than random
- Pronounceable passwords require more computation
- Batch generation is more efficient than individual calls

**Benchmarking:**
\`\`\`bash
# Benchmark generation speed
pwgen benchmark --count 1000 --length 16

# Compare different methods
pwgen benchmark --methods random,pronounceable,pattern
\`\`\`

### Memory Usage

**Secure Memory Handling:**
- Generated passwords are stored in secure memory
- Automatic clearing after use
- No swap file storage
- Secure clipboard handling

## Advanced Features

### Cryptographic Quality

**Random Number Generation:**
- Uses system cryptographic random number generator
- ChaCha20 for additional entropy mixing
- Seeded from multiple entropy sources
- Suitable for cryptographic applications

**Entropy Sources:**
- Operating system random number generator
- Hardware random number generators (when available)
- Process timing variations
- Memory allocation patterns

### API Integration

**Programmatic Access:**
\`\`\`bash
# JSON output for scripts
pwgen generate --format json --count 5

# Integration with other tools
pwgen generate --format base64 --length 32

# Webhook integration
pwgen generate --webhook-url "https://api.example.com/password"
\`\`\`

## Troubleshooting

### Common Issues

**Weak Passwords Generated:**
- Increase password length
- Enable all character sets
- Avoid pronounceable generation for high security
- Check entropy calculations

**Policy Compliance Failures:**
- Review password policy requirements
- Adjust character set selection
- Increase password length
- Check for forbidden patterns

**Performance Issues:**
- Use simpler generation methods
- Reduce batch sizes
- Avoid complex patterns
- Check system entropy availability

### Best Practices

**Generation Strategy:**
1. Start with reasonable defaults (16 characters, all character sets)
2. Adjust based on system requirements
3. Test generated passwords against target systems
4. Create profiles for different use cases
5. Regular review and updates of generation policies

**Security Guidelines:**
1. Never reuse generated passwords
2. Store generated passwords securely
3. Use appropriate length for security level
4. Regularly update generation policies
5. Monitor for password policy changes

## Next Steps

- [Password Management](passwords) - Using generated passwords
- [Security Architecture](../security/architecture) - Understanding security
- [Import & Export](import-export) - Password migration`
  },

  'user-guide/secrets': {
    title: 'Secrets Management',
    description: 'Beyond passwords - manage API keys, SSH keys, certificates, and secure documents',
    content: `# Secrets Management

Beyond passwords - manage API keys, SSH keys, certificates, and secure documents.

## Overview

PWGen-Rust's secrets management system extends beyond simple password storage to handle various types of sensitive information that modern developers and organizations need to secure:

- **API Keys**: Service tokens, access keys, webhooks
- **SSH Keys**: Private keys, public keys, certificates
- **Documents**: Encrypted file storage, secure notes
- **Environment Variables**: Configuration secrets, connection strings
- **Certificates**: SSL/TLS certificates, signing keys

## Secret Types

### API Keys

Store and manage service tokens, API keys, and access credentials.

**Structure:**
- **Name**: Descriptive identifier
- **Provider**: Service name (AWS, Google, GitHub, etc.)
- **Key Type**: API key, access token, webhook secret
- **Key Value**: The actual secret (encrypted)
- **Expiration**: Optional expiration date
- **Notes**: Usage instructions, scopes, permissions
- **Tags**: Organization labels

**Adding API Keys (GUI):**
1. Go to **🗝️ Secrets** tab
2. Click **➕ Add Secret**
3. Select **API Key** type
4. Fill in the form:
   - **Name**: \`GitHub Personal Access Token\`
   - **Provider**: \`GitHub\`
   - **Key Type**: \`Personal Access Token\`
   - **Key Value**: \`ghp_xxxxxxxxxxxxxxxxxxxx\`
   - **Expiration**: Set reminder date
   - **Notes**: Scopes and permissions
   - **Tags**: \`dev\`, \`github\`, \`personal\`

**Adding API Keys (CLI):**
\`\`\`bash
# Interactive API key entry
pwgen secret add api-key --name "GitHub PAT" --provider "GitHub"

# Complete API key entry
pwgen secret add api-key $
  --name "GitHub Personal Access Token" $
  --provider "GitHub" $
  --key-type "Personal Access Token" $
  --key-value "ghp_xxxxxxxxxxxxxxxxxxxx" $
  --expiration "2024-12-31" $
  --notes "Repo access for CI/CD" $
  --tags "dev,github,ci"
\`\`\`

### SSH Keys

Securely store SSH private keys, public keys, and certificates.

**Structure:**
- **Name**: Key identifier
- **Key Type**: RSA, ED25519, ECDSA
- **Private Key**: Encrypted private key content
- **Public Key**: Public key content
- **Passphrase**: Key passphrase (if any)
- **Certificate**: SSH certificate (if applicable)
- **Hosts**: Associated hostnames
- **Tags**: Organization labels

**Adding SSH Keys (GUI):**
1. Go to **🗝️ Secrets** tab
2. Click **➕ Add Secret**
3. Select **SSH Key** type
4. Fill in the form:
   - **Name**: \`Production Server Key\`
   - **Key Type**: \`ED25519\`
   - **Private Key**: Paste private key content
   - **Public Key**: Paste public key content
   - **Hosts**: \`prod.example.com\`
   - **Tags**: \`server\`, \`production\`

**Adding SSH Keys (CLI):**
\`\`\`bash
# Interactive SSH key entry
pwgen secret add ssh-key --name "Production Server"

# From file
pwgen secret add ssh-key $
  --name "Production Server Key" $
  --private-key-file ~/.ssh/prod_ed25519 $
  --public-key-file ~/.ssh/prod_ed25519.pub $
  --hosts "prod.example.com,backup.example.com" $
  --tags "server,production"

# Generate new SSH key pair
pwgen secret generate ssh-key $
  --name "New Server Key" $
  --key-type "ED25519" $
  --hosts "new-server.example.com"
\`\`\`

### Secure Documents

Store encrypted documents, notes, and file attachments.

**Structure:**
- **Title**: Document identifier
- **Content Type**: Text, binary, file attachment
- **Content**: Encrypted document content
- **File Name**: Original filename (for attachments)
- **File Type**: MIME type or file extension
- **Size**: File size information
- **Tags**: Organization labels

**Adding Documents (GUI):**
1. Go to **🗝️ Secrets** tab
2. Click **➕ Add Secret**
3. Select **Document** type
4. Choose content type:
   - **Text**: For secure notes, configs
   - **File**: Upload file attachment
5. Fill in details and save

**Adding Documents (CLI):**
\`\`\`bash
# Text document
pwgen secret add document $
  --title "Server Configuration" $
  --content "Database connection details..." $
  --tags "server,config"

# File attachment
pwgen secret add document $
  --title "SSL Certificate" $
  --file-path ./certificate.pem $
  --tags "ssl,certificate"

# Secure note
pwgen secret add document $
  --title "Recovery Codes" $
  --content-type "text" $
  --content "Recovery codes: 123456, 789012..." $
  --tags "recovery,2fa"
\`\`\`

### Environment Variables

Store configuration secrets, connection strings, and environment variables.

**Structure:**
- **Name**: Variable name
- **Value**: Variable value (encrypted)
- **Environment**: Development, staging, production
- **Service**: Associated service or application
- **Description**: Usage description
- **Tags**: Organization labels

**Adding Environment Variables (GUI):**
1. Go to **🗝️ Secrets** tab
2. Click **➕ Add Secret**
3. Select **Environment Variable** type
4. Fill in the form:
   - **Name**: \`DATABASE_URL\`
   - **Value**: \`postgres://user:pass@host:port/db\`
   - **Environment**: \`Production\`
   - **Service**: \`Web Application\`
   - **Description**: Main database connection

**Adding Environment Variables (CLI):**
\`\`\`bash
# Single environment variable
pwgen secret add env-var $
  --name "DATABASE_URL" $
  --value "postgres://user:pass@host:port/db" $
  --environment "production" $
  --service "web-app" $
  --tags "database,production"

# Batch import from .env file
pwgen secret import env-file $
  --file .env.production $
  --environment "production" $
  --service "web-app"
\`\`\`

## Managing Secrets

### Viewing Secrets

**GUI Navigation:**
- Go to **🗝️ Secrets** tab
- Browse by type or use filters
- Click on any secret to view details
- Copy functionality for each secret type

**CLI Listing:**
\`\`\`bash
# List all secrets
pwgen secret list

# Filter by type
pwgen secret list --type "api-key"
pwgen secret list --type "ssh-key"

# Filter by tags
pwgen secret list --tag "production"

# Search secrets
pwgen secret search "github"
\`\`\`

### Copying Secret Data

**Type-Specific Copy Operations:**

**API Keys:**
- Copy key value to clipboard
- Copy formatted curl command
- Copy environment variable format

**SSH Keys:**
- Copy private key content
- Copy public key content
- Copy SSH config entry

**Documents:**
- Copy document content
- Export file attachment
- Copy formatted text

**Environment Variables:**
- Copy variable value
- Copy as export statement
- Copy formatted for shell

**CLI Copy Operations:**
\`\`\`bash
# Copy secret value
pwgen secret copy --name "GitHub PAT"

# Copy in specific format
pwgen secret copy --name "Database URL" --format "export"
pwgen secret copy --name "SSH Key" --format "ssh-config"

# Export to file
pwgen secret export --name "SSL Certificate" --output-file cert.pem
\`\`\`

### Updating Secrets

**GUI Updates:**
1. Select the secret from the list
2. Click **✏️ Edit** button
3. Modify fields as needed
4. Click **Save** to apply changes

**CLI Updates:**
\`\`\`bash
# Update API key expiration
pwgen secret update --name "GitHub PAT" --expiration "2025-12-31"

# Update SSH key hosts
pwgen secret update --name "Server Key" --hosts "new-host.example.com"

# Rotate API key
pwgen secret update --name "AWS Access Key" --key-value "new-key-value"
\`\`\`

### Deleting Secrets

**Secure Deletion:**
- GUI: Select secret and click **🗑️ Delete** button
- CLI: \`pwgen secret delete --name "Secret Name"\`
- Confirmation required for deletion
- Secure memory clearing after deletion

## Secret Templates

### Pre-configured Templates

PWGen-Rust includes templates for common services:

**Cloud Providers:**
- AWS (Access Key, Secret Key, Session Token)
- Google Cloud (Service Account, API Key)
- Azure (Client ID, Client Secret, Tenant ID)

**Development Services:**
- GitHub (Personal Access Token, SSH Key)
- Docker (Registry credentials)
- Kubernetes (Service Account, Config)

**Using Templates (GUI):**
1. Click **➕ Add Secret**
2. Select **From Template**
3. Choose service template
4. Fill in template-specific fields

**Using Templates (CLI):**
\`\`\`bash
# List available templates
pwgen template list

# Create from template
pwgen secret add template --template "aws-iam" $
  --name "Production AWS" $
  --access-key "AKIA..." $
  --secret-key "..." $
  --tags "aws,production"
\`\`\`

### Custom Templates

**Creating Custom Templates:**
\`\`\`bash
# Create template for your service
pwgen template create $
  --name "custom-api" $
  --fields "endpoint,api-key,webhook-secret" $
  --description "Custom API service template"

# Use custom template
pwgen secret add template --template "custom-api" $
  --name "My API Service" $
  --endpoint "https://api.example.com" $
  --api-key "..." $
  --webhook-secret "..."
\`\`\`

## Security Features

### Encryption

All secrets are encrypted using AES-256-GCM:
- Individual encryption for each secret
- Unique encryption keys derived from master password
- No plaintext storage anywhere on disk
- Secure memory handling during processing

### Access Control

**Personal Vaults:**
- Master password protection
- Auto-lock after inactivity
- Secure clipboard handling
- Memory protection

**Team Features (Future):**
- Role-based access control
- Secret sharing permissions
- Audit logging
- Access revocation

### Expiration Management

**Automatic Reminders:**
- Set expiration dates for API keys
- Certificate expiration tracking
- Automated renewal reminders
- Integration with notification systems

**CLI Expiration Management:**
\`\`\`bash
# List expiring secrets
pwgen secret list --expiring-soon

# Set expiration reminder
pwgen secret update --name "API Key" --expiration "2024-12-31"

# Bulk expiration check
pwgen secret analyze expiration
\`\`\`

## Integration and Automation

### Command Line Integration

**Environment Setup:**
\`\`\`bash
# Export secrets as environment variables
eval $(pwgen secret export --tag "development" --format "env")

# Load secrets into shell session
source <(pwgen secret export --service "web-app" --format "shell")
\`\`\`

**CI/CD Integration:**
\`\`\`bash
# Get secret for CI pipeline
API_KEY=$(pwgen secret get --name "Deploy Key" --format "raw")

# Inject into application config
pwgen secret export --tag "production" --format "json" > app-secrets.json
\`\`\`

### Application Integration

**Configuration Management:**
\`\`\`bash
# Generate application config
pwgen secret export --service "web-app" --format "yaml" > config.yaml

# Docker environment file
pwgen secret export --tag "docker" --format "env" > .env.docker
\`\`\`

## Best Practices

### Organization

1. **Consistent Naming**: Use clear, descriptive names for all secrets
2. **Tagging Strategy**: Develop consistent tagging for environments and services
3. **Regular Reviews**: Periodically review and clean up unused secrets
4. **Documentation**: Use notes fields to document secret usage and permissions

### Security

1. **Principle of Least Privilege**: Only store secrets that are actually needed
2. **Regular Rotation**: Rotate API keys and certificates regularly
3. **Expiration Dates**: Set expiration reminders for all time-sensitive secrets
4. **Access Logging**: Monitor access patterns for suspicious activity

### Operational

1. **Backup Strategy**: Include secrets in regular backup procedures
2. **Recovery Planning**: Document secret recovery procedures
3. **Team Coordination**: Establish clear procedures for shared secrets
4. **Monitoring**: Set up alerts for expiring certificates and keys

## Advanced Features

### Secret Sharing (Future)

**Team Collaboration:**
- Secure secret sharing between team members
- Role-based access control
- Audit trail for all secret access
- Revocation and access management

### API Integration (Future)

**Programmatic Access:**
- REST API for secret management
- SDK for popular programming languages
- Webhook integration for secret updates
- Third-party service integration

### Compliance Features (Future)

**Enterprise Security:**
- Audit logging and monitoring
- Data residency controls  
- Integration with enterprise identity systems
- Comprehensive security documentation

## Troubleshooting

### Common Issues

**Import Failures:**
- Verify file format and encoding
- Check for special characters in secret names
- Ensure proper permissions for file access

**Performance Issues:**
- Large secrets (documents) may load slowly
- Use filtering to narrow secret lists
- Consider archiving old secrets

**Copy Operations:**
- Check clipboard permissions
- Verify secret format compatibility
- Try alternative copy formats

### Recovery Scenarios

**Lost API Keys:**
- Check secret history for previous versions
- Review notes for recovery information
- Contact service provider for key regeneration

**Corrupted Files:**
- Restore from recent backups
- Verify backup integrity before restoration
- Contact support for database recovery assistance

## Next Steps

- [Password Generator](generator) - Advanced password generation
- [Import & Export](import-export) - Data migration and backup
- [Security Architecture](../security/architecture) - Understanding encryption`
  },

  'user-guide/backup-restore': {
    title: 'Backup & Restore',
    description: 'Protect your password vault with comprehensive backup and recovery strategies',
    content: `# Backup & Restore

Protect your password vault with comprehensive backup and recovery strategies.

## Overview

Regular backups are essential for protecting your password vault against data loss, corruption, or hardware failures. PWGen-Rust provides multiple backup options with strong encryption and verification features.

## Backup Types

### Full Vault Backup

**Complete Backup:**
- Includes all passwords, secrets, and metadata
- Preserves tags, notes, and creation dates
- Maintains password history
- Encrypted with master password

**Creating Full Backups:**

**GUI Method:**
1. Go to **🔧 Tools** tab
2. Click **💾 Create Backup**
3. Choose backup location and filename
4. Select backup options:
   - Include password history
   - Include secrets
   - Compression level
5. Click **Create Backup**

**CLI Method:**
\`\`\`bash
# Basic full backup
pwgen backup create --output ~/backups/vault-$(date +%Y%m%d).pwgen

# Backup with custom encryption
pwgen backup create --output vault-backup.pwgen --encrypt-password

# Include all data types
pwgen backup create --output complete-backup.pwgen $
  --include-passwords --include-secrets --include-history
\`\`\`

### Incremental Backup

**Changed Data Only:**
- Backs up only modified entries since last backup
- Faster backup process for large vaults
- Requires base backup for complete restoration

**Creating Incremental Backups:**
\`\`\`bash
# Create base backup first
pwgen backup create --output base-backup.pwgen --full

# Create incremental backups
pwgen backup create --output incremental-$(date +%Y%m%d).pwgen $
  --incremental --since-backup base-backup.pwgen

# Daily incremental backup script
pwgen backup create --output "incremental-$(date +%Y%m%d).pwgen" $
  --incremental --since "yesterday"
\`\`\`

### Selective Backup

**Tag-Based Backup:**
\`\`\`bash
# Backup work-related entries only
pwgen backup create --output work-backup.pwgen --tag "work"

# Backup multiple categories
pwgen backup create --output important-backup.pwgen $
  --tags "work,banking,critical"

# Exclude specific categories
pwgen backup create --output personal-backup.pwgen $
  --exclude-tags "work,temporary"
\`\`\`

**Type-Specific Backup:**
\`\`\`bash
# Backup only passwords
pwgen backup create --output passwords-only.pwgen --passwords-only

# Backup only secrets
pwgen backup create --output secrets-only.pwgen --secrets-only

# Backup specific secret types
pwgen backup create --output api-keys.pwgen --secret-types "api-key,env-var"
\`\`\`

## Backup Formats

### Native PWGen-Rust Format (.pwgen)

**Encrypted Archive:**
- Native PWGen-Rust format with full fidelity
- Preserves all metadata and relationships
- Encrypted with AES-256-GCM
- Includes integrity verification

**Advantages:**
- Complete data preservation
- Fast restore process
- Built-in encryption
- Integrity checking

### Portable Formats

**JSON Backup:**
\`\`\`bash
# JSON backup (human-readable)
pwgen backup create --output vault.json --format json

# Encrypted JSON backup
pwgen backup create --output vault.json.enc --format json --encrypt
\`\`\`

**CSV Backup:**
\`\`\`bash
# CSV backup (compatible with spreadsheets)
pwgen backup create --output vault.csv --format csv

# CSV with custom fields
pwgen backup create --output vault.csv --format csv $
  --fields "site,username,password,notes,tags,created,modified"
\`\`\`

**XML Backup:**
\`\`\`bash
# KeePass-compatible XML
pwgen backup create --output vault.xml --format keepass-xml
\`\`\`

## Backup Security

### Encryption Options

**Master Password Encryption:**
\`\`\`bash
# Use current master password (default)
pwgen backup create --output backup.pwgen

# Prompt for different password
pwgen backup create --output backup.pwgen --encrypt-password

# Use keyfile
pwgen backup create --output backup.pwgen --keyfile backup.key
\`\`\`

**Additional Encryption:**
\`\`\`bash
# Encrypt with GPG
pwgen backup create --output backup.pwgen
gpg --encrypt --recipient "user@example.com" backup.pwgen

# Encrypt with age
pwgen backup create --output backup.pwgen
age -r age1public_key_here backup.pwgen > backup.pwgen.age
\`\`\`

### Backup Verification

**Integrity Checking:**
\`\`\`bash
# Verify backup integrity
pwgen backup verify --file backup.pwgen

# Detailed verification report
pwgen backup verify --file backup.pwgen --detailed

# Test restore without applying changes
pwgen backup verify --file backup.pwgen --test-restore
\`\`\`

**Automated Verification:**
\`\`\`bash
#!/bin/bash
# Backup verification script

BACKUP_FILE="vault-backup.pwgen"

# Create backup
pwgen backup create --output "$BACKUP_FILE"

# Verify integrity
if pwgen backup verify --file "$BACKUP_FILE"; then
  echo "Backup verified successfully"
else
  echo "Backup verification failed!"
  exit 1
fi

# Test restore process
if pwgen backup verify --file "$BACKUP_FILE" --test-restore; then
  echo "Restore test passed"
else
  echo "Restore test failed!"
  exit 1
fi
\`\`\`

## Backup Storage

### Local Storage

**File System Backup:**
\`\`\`bash
# Local directory backup
BACKUP_DIR="$HOME/pwgen-backups"
mkdir -p "$BACKUP_DIR"
pwgen backup create --output "$BACKUP_DIR/vault-$(date +%Y%m%d).pwgen"

# External drive backup
pwgen backup create --output "/media/backup-drive/pwgen-backup.pwgen"

# Network attached storage
pwgen backup create --output "/mnt/nas/backups/pwgen-backup.pwgen"
\`\`\`

### Cloud Storage

**Cloud Backup Script:**
\`\`\`bash
#!/bin/bash
# Cloud backup automation

BACKUP_FILE="vault-$(date +%Y%m%d).pwgen"

# Create encrypted backup
pwgen backup create --output "$BACKUP_FILE"

# Upload to different cloud providers
# AWS S3
aws s3 cp "$BACKUP_FILE" "s3://my-backup-bucket/pwgen/"

# Google Cloud Storage
gsutil cp "$BACKUP_FILE" "gs://my-backup-bucket/pwgen/"

# Dropbox (using dropbox_uploader)
./dropbox_uploader.sh upload "$BACKUP_FILE" "/Apps/PWGen-Rust/"

# Clean local backup
rm "$BACKUP_FILE"
\`\`\`

**Encrypted Cloud Storage:**
\`\`\`bash
# Encrypt before cloud upload
pwgen backup create --output backup.pwgen
gpg --encrypt --recipient "backup@example.com" backup.pwgen

# Upload encrypted file
rclone copy backup.pwgen.gpg remote:backups/pwgen/
rm backup.pwgen backup.pwgen.gpg
\`\`\`

## Automated Backup

### Scheduled Backups

**Cron Job Setup:**
\`\`\`bash
# Edit crontab
crontab -e

# Daily backup at 2 AM
0 2 * * * /usr/local/bin/pwgen backup create --output "$HOME/backups/daily-$(date +\\%Y\\%m\\%d).pwgen"

# Weekly full backup on Sunday at 3 AM
0 3 * * 0 /usr/local/bin/pwgen backup create --output "$HOME/backups/weekly-$(date +\\%Y\\%m\\%d).pwgen" --full

# Monthly archive on 1st of month
0 4 1 * * /usr/local/bin/pwgen backup create --output "$HOME/archives/monthly-$(date +\\%Y\\%m).pwgen"
\`\`\`

**Windows Task Scheduler:**
\`\`\`powershell
# PowerShell script for Windows
$BackupDir = "$env:USERPROFILE\\PWGen-Rust\\Backups"
$BackupFile = "vault-$(Get-Date -Format 'yyyyMMdd').pwgen"

New-Item -ItemType Directory -Force -Path $BackupDir
& pwgen backup create --output "$BackupDir\\$BackupFile"

# Clean old backups (keep 30 days)
Get-ChildItem "$BackupDir\\vault-*.pwgen" | Where-Object {$_.LastWriteTime -lt (Get-Date).AddDays(-30)} | Remove-Item
\`\`\`

### Backup Rotation

**Rotation Strategy:**
\`\`\`bash
#!/bin/bash
# Backup rotation script

BACKUP_DIR="$HOME/pwgen-backups"
DATE=$(date +%Y%m%d)

# Create today's backup
pwgen backup create --output "$BACKUP_DIR/daily-$DATE.pwgen"

# Weekly backup (Sunday)
if [ $(date +%u) -eq 7 ]; then
  cp "$BACKUP_DIR/daily-$DATE.pwgen" "$BACKUP_DIR/weekly-$DATE.pwgen"
fi

# Monthly backup (1st of month)
if [ $(date +%d) -eq 01 ]; then
  cp "$BACKUP_DIR/daily-$DATE.pwgen" "$BACKUP_DIR/monthly-$DATE.pwgen"
fi

# Cleanup old backups
# Keep daily backups for 7 days
find "$BACKUP_DIR" -name "daily-*.pwgen" -mtime +7 -delete

# Keep weekly backups for 4 weeks
find "$BACKUP_DIR" -name "weekly-*.pwgen" -mtime +28 -delete

# Keep monthly backups for 12 months
find "$BACKUP_DIR" -name "monthly-*.pwgen" -mtime +365 -delete
\`\`\`

## Restore Process

### Full Restore

**Complete Vault Restoration:**

**GUI Restore:**
1. Go to **🔧 Tools** tab
2. Click **📁 Restore from Backup**
3. Select backup file
4. Choose restore options:
   - Replace current vault
   - Merge with current vault
   - Restore to new vault
5. Enter backup password if different
6. Click **Restore**

**CLI Restore:**
\`\`\`bash
# Replace current vault
pwgen restore --file backup.pwgen --replace

# Merge with current vault
pwgen restore --file backup.pwgen --merge

# Restore to new vault file
pwgen restore --file backup.pwgen --output new-vault.db

# Restore with conflict resolution
pwgen restore --file backup.pwgen --merge --conflict-resolution newest
\`\`\`

### Selective Restore

**Partial Restoration:**
\`\`\`bash
# Restore only passwords
pwgen restore --file backup.pwgen --passwords-only

# Restore specific tags
pwgen restore --file backup.pwgen --tags "work,banking"

# Restore entries modified after specific date
pwgen restore --file backup.pwgen --modified-since "2024-01-01"

# Restore deleted entries only
pwgen restore --file backup.pwgen --deleted-only
\`\`\`

### Conflict Resolution

**Merge Strategies:**
\`\`\`bash
# Keep newer entries
pwgen restore --file backup.pwgen --merge --conflict-resolution newer

# Keep backup entries
pwgen restore --file backup.pwgen --merge --conflict-resolution backup

# Keep current entries
pwgen restore --file backup.pwgen --merge --conflict-resolution current

# Interactive conflict resolution
pwgen restore --file backup.pwgen --merge --conflict-resolution interactive
\`\`\`

**Custom Merge Rules:**
\`\`\`json
{
  "duplicate_handling": "merge",
  "password_conflicts": "prefer_newer",
  "tag_conflicts": "merge_all",
  "note_conflicts": "append",
  "metadata_conflicts": "prefer_backup"
}
\`\`\`

## Disaster Recovery

### Recovery Scenarios

**Complete System Loss:**
1. Install PWGen-Rust on new system
2. Restore from most recent backup
3. Verify all data restored correctly
4. Update any changed passwords
5. Resume regular backup schedule

**Vault Corruption:**
1. Stop using corrupted vault immediately
2. Restore from most recent verified backup
3. Compare against previous backups if needed
4. Investigate cause of corruption
5. Implement additional backup measures

**Forgotten Master Password:**
- Master passwords cannot be recovered
- Must restore from backup created with known password
- Consider backup encryption with separate key
- Implement password hint system for future

### Recovery Testing

**Regular Recovery Tests:**
\`\`\`bash
#!/bin/bash
# Monthly recovery test

TEST_DIR="/tmp/pwgen-recovery-test"
BACKUP_FILE="$HOME/backups/latest-backup.pwgen"

# Create test environment
mkdir -p "$TEST_DIR"
cd "$TEST_DIR"

# Test restore process
pwgen restore --file "$BACKUP_FILE" --output "test-vault.db"

# Verify restored data
pwgen --vault "test-vault.db" list --count > restore-count.txt
pwgen --vault "test-vault.db" analyze integrity > integrity-report.txt

# Compare with original
echo "Recovery test completed. Check restore-count.txt and integrity-report.txt"

# Cleanup
cd -
rm -rf "$TEST_DIR"
\`\`\`

## Backup Best Practices

### 3-2-1 Backup Rule

**Implementation:**
- **3 Copies**: Original + 2 backups
- **2 Different Media**: Local drive + cloud/external
- **1 Offsite**: Cloud storage or remote location

**Example Setup:**
\`\`\`bash
#!/bin/bash
# 3-2-1 backup implementation

DATE=$(date +%Y%m%d)

# Copy 1: Original vault (daily use)
# Copy 2: Local backup
pwgen backup create --output "$HOME/backups/local-$DATE.pwgen"

# Copy 3: External drive backup
pwgen backup create --output "/media/external/pwgen-$DATE.pwgen"

# Copy 4: Cloud backup (offsite)
pwgen backup create --output "cloud-$DATE.pwgen"
rclone copy "cloud-$DATE.pwgen" "gdrive:backups/pwgen/"
rm "cloud-$DATE.pwgen"
\`\`\`

### Security Guidelines

1. **Encrypt All Backups**: Never store unencrypted backups
2. **Verify Regularly**: Test restore process monthly
3. **Secure Storage**: Use encrypted storage locations
4. **Access Control**: Limit backup access to authorized users
5. **Audit Trail**: Log backup and restore operations

### Operational Guidelines

1. **Automate Backups**: Use scheduled automated backups
2. **Monitor Success**: Verify backup completion
3. **Rotate Backups**: Implement proper retention policies
4. **Document Procedures**: Maintain recovery documentation
5. **Test Regularly**: Perform recovery tests

## Troubleshooting

### Backup Issues

**Common Problems:**
- Insufficient disk space
- Permission denied errors
- Backup corruption
- Network connectivity issues
- Encryption key problems

**Solutions:**
\`\`\`bash
# Check disk space
df -h /backup/location

# Fix permissions
chmod 700 /backup/location
chown user:user /backup/location

# Verify backup integrity
pwgen backup verify --file backup.pwgen

# Test network connectivity
ping backup-server.example.com

# Reset encryption keys
pwgen backup create --output backup.pwgen --reset-encryption
\`\`\`

### Restore Issues

**Common Problems:**
- Incorrect backup password
- Corrupted backup files
- Version compatibility issues
- Merge conflicts
- Missing dependencies

**Solutions:**
\`\`\`bash
# Verify backup file
file backup.pwgen
pwgen backup info --file backup.pwgen

# Try different passwords
pwgen backup unlock --file backup.pwgen --try-passwords

# Check version compatibility
pwgen backup version --file backup.pwgen

# Force restore with conflict resolution
pwgen restore --file backup.pwgen --force --conflict-resolution backup
\`\`\`

## Advanced Features

### Backup Encryption

**Multiple Encryption Layers:**
\`\`\`bash
# Create backup with custom encryption
pwgen backup create --output backup.pwgen --encrypt-algorithm "AES-256-GCM"

# Add additional encryption layer
gpg --encrypt --recipient "backup@example.com" backup.pwgen

# Age encryption
age -r age1public_key backup.pwgen > backup.pwgen.age
\`\`\`

### Backup Monitoring

**Monitoring Script:**
\`\`\`bash
#!/bin/bash
# Backup monitoring and alerting

BACKUP_DIR="$HOME/backups"
LATEST_BACKUP=$(ls -t "$BACKUP_DIR"/*.pwgen | head -1)
BACKUP_AGE=$(( ($(date +%s) - $(stat -c %Y "$LATEST_BACKUP")) / 86400 ))

if [ $BACKUP_AGE -gt 1 ]; then
  echo "WARNING: Latest backup is $BACKUP_AGE days old"
  # Send alert email or notification
  mail -s "PWGen-Rust Backup Alert" admin@example.com < backup-alert.txt
fi

# Verify latest backup
if ! pwgen backup verify --file "$LATEST_BACKUP"; then
  echo "ERROR: Latest backup verification failed"
  # Send critical alert
fi
\`\`\`

## Next Steps

- [Security Architecture](../security/architecture) - Understanding encryption
- [CLI Reference](../cli/commands) - Advanced backup commands
- [Import & Export](import-export) - Data migration tools`
  },

  'user-guide/import-export': {
    title: 'Import & Export',
    description: 'Migrate data from other password managers and create portable backups',
    content: `# Import & Export

Migrate data from other password managers and create portable backups.

## Overview

PwGen supports importing from major password managers and browsers, as well as exporting your data in various formats for backup or migration purposes.

## Importing Data

### Browser Password Import

**Supported Browsers:**
- Google Chrome / Chromium
- Mozilla Firefox  
- Microsoft Edge
- Safari (macOS)
- Opera / Vivaldi

**Chrome Import:**
\`\`\`bash
# Export from Chrome first:
# 1. Go to chrome://settings/passwords
# 2. Click "Download file" next to "Export passwords"
# 3. Save as CSV file

# Import to PwGen
pwgen-cli import csv --file chrome-passwords.csv --browser chrome
\`\`\`

**Firefox Import:**
\`\`\`bash
# Export from Firefox first:
# 1. Go to about:logins
# 2. Click menu (⋯) > "Export Logins"
# 3. Save as CSV file

# Import to PwGen
pwgen-cli import csv --file firefox-passwords.csv --browser firefox
\`\`\`

### Password Manager Import

**LastPass:**
\`\`\`bash
# Export from LastPass:
# 1. Log into LastPass web interface
# 2. Go to Advanced Options > Export
# 3. Save as CSV file

# Import to PwGen
pwgen-cli import lastpass --file lastpass_export.csv
\`\`\`

**1Password:**
\`\`\`bash
# Export from 1Password:
# 1. Open 1Password app
# 2. File > Export > All Items
# 3. Choose 1PIF format

# Import to PwGen
pwgen-cli import 1password --file 1password_export.1pif
\`\`\`

**Bitwarden:**
\`\`\`bash
# Export from Bitwarden:
# 1. Go to bitwarden.com/vault
# 2. Tools > Export Vault
# 3. Choose JSON format

# Import to PwGen
pwgen-cli import bitwarden --file bitwarden_export.json
\`\`\`

**KeePass:**
\`\`\`bash
# Export from KeePass:
# 1. File > Export
# 2. Choose KeePass XML (2.x) format

# Import to PwGen  
pwgen-cli import keepass --file keepass_export.xml
\`\`\`

**Dashlane:**
\`\`\`bash
# Export from Dashlane:
# 1. Go to Settings > Export Data
# 2. Download as CSV

# Import to PwGen
pwgen-cli import dashlane --file dashlane_export.csv
\`\`\`

### Generic CSV Import

**CSV Format Requirements:**
\`\`\`csv
site,username,password,notes,tags
github.com,user@example.com,password123,Development account,"dev,work"
google.com,user@gmail.com,password456,Personal email,"personal,email"
\`\`\`

**CSV Import Options:**
\`\`\`bash
# Basic CSV import
pwgen-cli import csv --file passwords.csv

# Custom column mapping
pwgen-cli import csv --file passwords.csv $
  --columns "url:0,username:1,password:2,title:3,notes:4"

# Skip header row
pwgen-cli import csv --file passwords.csv --skip-header

# Specify delimiter
pwgen-cli import csv --file passwords.tsv --delimiter "\\t"
\`\`\`

### GUI Import

**Using the GUI:**
1. Go to **🔧 Tools** tab
2. Click **📥 Import Data**
3. Select import source:
   - Browser (Chrome, Firefox, etc.)
   - Password Manager (LastPass, 1Password, etc.)
   - Generic CSV file
4. Choose the exported file
5. Review import preview
6. Click **Import** to complete

**Import Preview:**
- Shows how many entries will be imported
- Highlights potential duplicates
- Allows field mapping customization
- Provides import conflict resolution options

## Exporting Data

### Export Formats

**CSV Export:**
\`\`\`bash
# Export all passwords to CSV
pwgen-cli export csv --output passwords.csv

# Export with specific fields
pwgen-cli export csv --output passwords.csv $
  --fields "site,username,password,notes,tags,created"

# Export filtered entries
pwgen-cli export csv --output work-passwords.csv --tag "work"
\`\`\`

**JSON Export:**
\`\`\`bash
# Export to JSON (includes all metadata)
pwgen-cli export json --output vault-backup.json

# Pretty-printed JSON
pwgen-cli export json --output vault-backup.json --pretty

# Export secrets separately
pwgen-cli export json --output secrets.json --type secrets
\`\`\`

**KeePass XML Export:**
\`\`\`bash
# Export to KeePass-compatible XML
pwgen-cli export keepass --output vault.xml

# Include password history
pwgen-cli export keepass --output vault.xml --include-history
\`\`\`

**1Password 1PIF Export:**
\`\`\`bash
# Export to 1Password format
pwgen-cli export 1password --output vault.1pif
\`\`\`

### Encrypted Export

**Encrypted Backup:**
\`\`\`bash
# Create encrypted backup (uses master password)
pwgen-cli export encrypted --output vault-backup.pwgen

# Create encrypted backup with different password
pwgen-cli export encrypted --output vault-backup.pwgen --password

# Include all data types
pwgen-cli export encrypted --output full-backup.pwgen --include-secrets
\`\`\`

### Selective Export

**Tag-Based Export:**
\`\`\`bash
# Export only work-related entries
pwgen-cli export csv --output work.csv --tag "work"

# Export multiple tags
pwgen-cli export csv --output important.csv --tags "work,banking"

# Exclude specific tags
pwgen-cli export csv --output personal.csv --exclude-tags "work"
\`\`\`

**Date-Range Export:**
\`\`\`bash
# Export entries created in last 30 days
pwgen-cli export csv --output recent.csv --since "30 days ago"

# Export entries modified since specific date
pwgen-cli export csv --output updated.csv --modified-since "2024-01-01"
\`\`\`

## Migration Strategies

### From LastPass

**Complete Migration:**
1. Export from LastPass to CSV
2. Import to PwGen using LastPass format
3. Verify all entries imported correctly
4. Update weak passwords using PwGen generator
5. Organize with tags for better management

**Migration Script:**
\`\`\`bash
#!/bin/bash
# LastPass to PwGen migration

# Import passwords
pwgen-cli import lastpass --file lastpass_export.csv

# Import secure notes
pwgen-cli import lastpass-notes --file lastpass_notes.csv

# Generate report
pwgen-cli analyze imported --format report > migration-report.txt

# Update weak passwords
pwgen-cli list --weak | while read -r entry; do
  pwgen-cli password update --id "$entry" --generate
done
\`\`\`

### From 1Password

**Family/Team Migration:**
\`\`\`bash
# Export from 1Password (each vault separately)
# Import each vault with different tags

pwgen-cli import 1password --file personal.1pif --add-tag "personal"
pwgen-cli import 1password --file work.1pif --add-tag "work"
pwgen-cli import 1password --file shared.1pif --add-tag "shared"
\`\`\`

### From Browser Storage

**Chrome Migration:**
\`\`\`bash
# Export Chrome passwords
# chrome://settings/passwords -> Export

# Import with browser-specific handling
pwgen-cli import chrome --file chrome-passwords.csv

# Clean up duplicate entries
pwgen-cli deduplicate --merge-strategy prefer-newest
\`\`\`

## Duplicate Handling

### Duplicate Detection

**Automatic Detection:**
- Site URL matching
- Username matching
- Exact password matching
- Similar entry detection

**Detection Options:**
\`\`\`bash
# Find duplicates
pwgen-cli duplicates list

# Find duplicates by site
pwgen-cli duplicates list --field site

# Find weak duplicates (same password, different sites)
pwgen-cli duplicates list --cross-site
\`\`\`

### Duplicate Resolution

**Merge Strategies:**
\`\`\`bash
# Keep newest entry
pwgen-cli duplicates merge --strategy newest

# Keep entry with most metadata
pwgen-cli duplicates merge --strategy complete

# Manual resolution
pwgen-cli duplicates resolve --interactive

# Custom merge rules
pwgen-cli duplicates merge --rules merge-rules.json
\`\`\`

**Merge Rules Configuration:**
\`\`\`json
{
  "site_matching": "exact",
  "username_matching": "case_insensitive",
  "password_matching": "exact",
  "merge_strategy": "prefer_newest",
  "preserve_tags": true,
  "merge_notes": true,
  "keep_history": true
}
\`\`\`

## Data Validation

### Import Validation

**Pre-Import Checks:**
\`\`\`bash
# Validate CSV format before import
pwgen-cli validate csv --file passwords.csv

# Check for common issues
pwgen-cli validate csv --file passwords.csv --report issues.txt

# Preview import without executing
pwgen-cli import csv --file passwords.csv --dry-run
\`\`\`

**Common Issues:**
- Invalid CSV format or encoding
- Missing required fields
- Password field empty or malformed
- Special characters in site names
- Extremely long field values

### Post-Import Validation

**Verification Steps:**
\`\`\`bash
# Count imported entries
pwgen-cli list --count

# Check for import errors
pwgen-cli import-log --last-import

# Verify specific entries
pwgen-cli search "github.com" | head -5

# Generate import report
pwgen-cli analyze import --report import-summary.txt
\`\`\`

## Automation and Scripting

### Batch Import Scripts

**Multi-Source Import:**
\`\`\`bash
#!/bin/bash
# Import from multiple sources

# Import browser passwords
for browser in chrome firefox edge; do
  if [ -f "\${browser}-passwords.csv" ]; then
    pwgen-cli import csv --file "\${browser}-passwords.csv" $
      --browser "$browser" --add-tag "browser-$browser"
  fi
done

# Import password managers
for manager in lastpass bitwarden; do
  if [ -f "\${manager}-export.csv" ]; then
    pwgen-cli import "$manager" --file "\${manager}-export.csv" $
      --add-tag "migrated-$manager"
  fi
done

# Generate migration report
pwgen-cli analyze migration --output migration-report.html
\`\`\`

### Export Automation

**Scheduled Backups:**
\`\`\`bash
#!/bin/bash
# Daily backup script

DATE=$(date +%Y%m%d)
BACKUP_DIR="$HOME/pwgen-backups"

mkdir -p "$BACKUP_DIR"

# Create encrypted backup
pwgen-cli export encrypted --output "$BACKUP_DIR/vault-$DATE.pwgen"

# Create CSV backup for external tools
pwgen-cli export csv --output "$BACKUP_DIR/passwords-$DATE.csv"

# Clean old backups (keep 30 days)
find "$BACKUP_DIR" -name "vault-*.pwgen" -mtime +30 -delete
find "$BACKUP_DIR" -name "passwords-*.csv" -mtime +30 -delete
\`\`\`

## Security Considerations

### Import Security

**Secure Import Practices:**
1. Verify source file integrity
2. Import from trusted locations only
3. Delete source files after import
4. Review imported entries for anomalies
5. Update weak passwords after import

**File Handling:**
\`\`\`bash
# Secure file operations
umask 077  # Restrict file permissions

# Import with secure file handling
pwgen-cli import csv --file passwords.csv --secure-delete

# Verify file deletion
shred -vfz passwords.csv
\`\`\`

### Export Security

**Secure Export Practices:**
1. Use encrypted formats when possible
2. Protect export files with strong passwords
3. Store exports in secure locations
4. Delete exports after use
5. Never export to untrusted systems

**Encrypted Export Options:**
\`\`\`bash
# Export with additional encryption
pwgen-cli export encrypted --output backup.pwgen --encrypt-with-key

# Export to encrypted USB device
pwgen-cli export encrypted --output /media/encrypted-usb/backup.pwgen

# Create password-protected ZIP
pwgen-cli export csv --output passwords.csv
zip -e backup.zip passwords.csv
shred -vfz passwords.csv
\`\`\`

## Troubleshooting

### Import Issues

**Common Problems:**
- File encoding issues (use UTF-8)
- CSV delimiter problems
- Missing or extra columns
- Password field truncation
- Special character handling

**Solutions:**
\`\`\`bash
# Fix encoding issues
iconv -f ISO-8859-1 -t UTF-8 passwords.csv > passwords-utf8.csv

# Adjust CSV settings
pwgen-cli import csv --file passwords.csv $
  --delimiter ";" --quote-char '"' --escape-char '\\\\'

# Handle large files
split -l 1000 large-passwords.csv chunk-
for chunk in chunk-*; do
  pwgen-cli import csv --file "$chunk"
done
\`\`\`

### Export Issues

**Common Problems:**
- File permission errors
- Disk space limitations
- Large export sizes
- Format compatibility issues

**Solutions:**
\`\`\`bash
# Check available space
df -h /path/to/export

# Export in chunks
pwgen-cli export csv --output chunk1.csv --limit 1000 --offset 0
pwgen-cli export csv --output chunk2.csv --limit 1000 --offset 1000

# Compress large exports
pwgen-cli export json --output vault.json
gzip vault.json
\`\`\`

## Best Practices

### Migration Planning

1. **Inventory Current Data**: List all sources and data types
2. **Plan Import Order**: Start with most important data
3. **Test with Sample**: Import small subset first
4. **Verify Accuracy**: Check random sampling of imported data
5. **Update Security**: Change weak passwords after import

### Backup Strategy

1. **Regular Exports**: Schedule automated backups
2. **Multiple Formats**: Export in different formats
3. **Secure Storage**: Use encrypted backup locations
4. **Test Restores**: Verify backups can be restored
5. **Offsite Storage**: Keep copies in separate locations

### Data Hygiene

1. **Clean Before Export**: Remove unused entries
2. **Standardize Tags**: Use consistent tagging
3. **Update Metadata**: Ensure notes and URLs are current
4. **Resolve Duplicates**: Clean up duplicate entries
5. **Validate Passwords**: Check for weak or expired passwords

## Next Steps

- [Backup & Restore](backup-restore) - Comprehensive backup strategies
- [Security Architecture](../security/architecture) - Understanding data protection
- [CLI Reference](../cli/commands) - Advanced command-line operations`
  },

  'user-guide/configuration': {
    title: 'Configuration',
    description: 'Customize PwGen to suit your preferences and workflow',
    content: `# Configuration

Customize PwGen to suit your preferences and workflow.

## Configuration File

Default location: \`~/.config/pwgen/config.toml\`

\`\`\`toml
[ui]
theme = "auto"  # auto, light, dark
font_size = 14
window_size = [1200, 800]

[security]
auto_lock_minutes = 15
clipboard_clear_seconds = 30

[generator]
default_length = 16
default_symbols = true
exclude_ambiguous = true

[cli]
default_format = "table"
colors = true
\`\`\`

## Environment Variables

\`\`\`bash
# Vault location
export PWGEN_VAULT_PATH="/path/to/vault.db"

# Output format
export PWGEN_FORMAT="json"

# Disable colors
export PWGEN_NO_COLOR=1
\`\`\`

## GUI Settings

Access via Settings menu:
- Theme selection (light/dark/auto)
- Font size adjustment
- Window size preferences
- Auto-lock timeout
- Clipboard timeout

## CLI Configuration

### Output Formats
- \`table\` - Formatted table (default)
- \`json\` - JSON output
- \`csv\` - Comma-separated values
- \`yaml\` - YAML format

### Shell Completions

\`\`\`bash
# Bash
pwgen-cli completion bash > ~/.bash_completion.d/pwgen-cli

# Zsh  
pwgen-cli completion zsh > ~/.zsh/completions/_pwgen-cli

# Fish
pwgen-cli completion fish > ~/.config/fish/completions/pwgen-cli.fish
\`\`\`

## Security Settings

### Auto-lock
- Configure inactivity timeout
- Lock on screen saver
- Lock on system suspend

### Clipboard
- Clear timeout (default: 30 seconds)
- Disable clipboard history
- Primary vs clipboard selection (Linux)

## Advanced Configuration

### Custom Vault Location
\`\`\`bash
pwgen-cli --vault /custom/path/vault.db
\`\`\`

### Logging
\`\`\`toml
[logging]
level = "info"  # error, warn, info, debug, trace
file = "~/.config/pwgen/pwgen.log"
\`\`\`

### Backup Settings
\`\`\`toml
[backup]
auto_backup = true
backup_interval_hours = 24
backup_retention_days = 30
backup_location = "~/.config/pwgen/backups"
\`\`\``
  },

  'user-guide/troubleshooting': {
    title: 'Troubleshooting',
    description: 'Common issues and solutions for PwGen',
    content: `# Troubleshooting

Common issues and solutions for PwGen.

## Installation Issues

### Command Not Found

**Problem**: \`pwgen-cli: command not found\` after installation

**Solution**:
\`\`\`bash
# Add cargo bin to PATH
echo 'export PATH="$HOME/.cargo/bin:$PATH"' >> ~/.bashrc
source ~/.bashrc

# Or install system-wide
sudo cp ~/.cargo/bin/pwgen-* /usr/local/bin/
\`\`\`

### Build Errors

**Problem**: Compilation fails during installation

**Solution**:
\`\`\`bash
# Update Rust
rustup update

# Install build dependencies (Linux)
sudo apt install build-essential pkg-config libssl-dev

# Install build dependencies (macOS)  
xcode-select --install
\`\`\`

## Runtime Issues

### Vault Won't Open

**Problem**: "Failed to open vault" error

**Solutions**:
- Verify vault file exists and has correct permissions
- Check master password is correct
- Try restoring from backup if vault is corrupted

### Performance Issues

**Problem**: Slow loading with large vaults

**Solutions**:
- Use search and filtering to narrow results
- Consider archiving old entries
- Increase available memory

## GUI Issues

### Won't Start

**Problem**: GUI application fails to launch

**Solutions**:
\`\`\`bash
# Check for missing libraries (Linux)
sudo apt install libgtk-3-dev

# Check display settings
echo $DISPLAY

# Try from terminal to see error messages
pwgen-gui
\`\`\`

## Security Issues

### Forgotten Master Password

**Problem**: Can't remember master password

**Solutions**:
- Master passwords cannot be recovered by design
- Restore from backup with known password
- Start fresh if no backups available

### Suspected Breach

**Problem**: Possible security compromise

**Actions**:
1. Change master password immediately
2. Review all stored passwords
3. Update compromised passwords
4. Check access logs if available

## Getting Help

- 📖 [Documentation](../getting-started/installation)
- 🐛 [GitHub Issues](https://github.com/KiefStudioMA/PWGen-Rust/issues)
- 💬 [Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)`
  },

  'security/detailed-architecture': {
    title: 'Detailed Security Architecture',
    description: 'Comprehensive overview of PwGen-rust\'s security design principles and implementation',
    content: `# Security Architecture

Comprehensive overview of PwGen-rust's security design principles and implementation.

## Design Philosophy

PwGen-rust is built on the principle of **defense in depth**, implementing multiple layers of security to protect your sensitive data. Our architecture follows the **zero-trust** model where every component assumes potential compromise of other components.

### Core Security Principles

1. **Zero-Knowledge Architecture** - We never have access to your data or passwords
2. **Local-First Design** - All operations happen on your device
3. **Memory Safety** - Rust prevents entire classes of vulnerabilities
4. **Principle of Least Privilege** - Components have minimal required permissions
5. **Fail-Safe Defaults** - Secure by default configuration

## Architecture Overview

\`\`\`
┌─────────────────────────────────────────────────────────┐
│                    User Interface                        │
│                  (GUI / CLI / API)                       │
└─────────────────────┬───────────────────────────────────┘
                      │
┌─────────────────────┴───────────────────────────────────┐
│                 Application Layer                        │
│            (Business Logic & Validation)                 │
└─────────────────────┬───────────────────────────────────┘
                      │
┌─────────────────────┴───────────────────────────────────┐
│               Cryptography Layer                         │
│         (Encryption / Decryption / KDF)                  │
└─────────────────────┬───────────────────────────────────┘
                      │
┌─────────────────────┴───────────────────────────────────┐
│                 Storage Layer                            │
│              (SQLite + File System)                      │
└─────────────────────────────────────────────────────────┘
\`\`\`

## Security Layers

### 1. User Interface Layer

**Security Controls:**
- Input validation and sanitization
- Clipboard auto-clear after 30 seconds
- Auto-lock on inactivity (configurable)
- Password field masking
- Secure password generation UI

**Threat Mitigation:**
- Prevents injection attacks through input validation
- Reduces shoulder-surfing risk with masked fields
- Limits exposure window with auto-lock

### 2. Application Layer

**Security Controls:**
- Authentication before any operation
- Session management with timeout
- Audit logging for security events
- Rate limiting for failed attempts
- Permission-based access control

**Implementation Details:**
\`\`\`rust
// Example: Authentication check
fn perform_operation() -> Result<()> {
    ensure_authenticated()?;
    validate_permissions()?;
    audit_log_access()?;
    // Perform operation
}
\`\`\`

### 3. Cryptography Layer

**Security Controls:**
- AES-256-GCM for data encryption
- Argon2 for key derivation
- Secure random number generation
- Proper key management
- Memory zeroization after use

**Key Management:**
\`\`\`
Master Password → Argon2 → Master Key → Vault Key
                    ↓
                  Salt (unique per vault)
\`\`\`

### 4. Storage Layer

**Security Controls:**
- Encrypted SQLite database
- File permissions (700 on Unix)
- Integrity verification
- Secure deletion when needed
- Backup encryption

## Memory Security

### Rust's Memory Safety Guarantees

PwGen-rust leverages Rust's ownership system to prevent:
- **Buffer overflows** - Bounds checking at compile time
- **Use-after-free** - Ownership rules prevent dangling pointers
- **Double-free** - Single ownership prevents multiple frees
- **Null pointer dereferences** - Option types instead of nulls

### Sensitive Data Handling

\`\`\`rust
use zeroize::Zeroize;

// Sensitive data is automatically cleared
struct SensitiveData {
    #[zeroize(drop)]
    password: String,
}

// Manual zeroization for extra security
fn handle_password(mut password: String) {
    // Use password
    process_password(&password)?;
    
    // Clear from memory
    password.zeroize();
}
\`\`\`

## Threat Model

### Threats We Defend Against

1. **Network Attacks**
   - Man-in-the-middle: No network communication required
   - Remote exploitation: Local-only architecture
   - Data interception: All data encrypted at rest

2. **Local Attacks**
   - Database theft: Encrypted with strong KDF
   - Memory scraping: Automatic zeroization
   - Process injection: OS-level protections

3. **Physical Attacks**
   - Device theft: Encrypted vault requires master password
   - Evil maid: Detection through integrity checks
   - Cold boot: Memory cleared after use

4. **Application Attacks**
   - SQL injection: Parameterized queries only
   - XSS/CSRF: Not applicable (no web interface)
   - Buffer overflows: Prevented by Rust

### Out of Scope Threats

1. **Compromised Operating System**
   - Kernel-level malware
   - Hardware keyloggers
   - BIOS/UEFI rootkits

2. **Social Engineering**
   - Phishing for master password
   - Shoulder surfing
   - Rubber hose cryptanalysis

3. **Advanced Persistent Threats**
   - Nation-state actors with unlimited resources
   - Zero-day exploits in OS or hardware
   - Supply chain attacks

## Access Control

### Authentication Flow

\`\`\`
1. User enters master password
2. Derive key using Argon2 with vault salt
3. Attempt to decrypt vault metadata
4. If successful, create session
5. Set session timeout
6. Allow access to vault operations
\`\`\`

### Session Management

- Sessions expire after inactivity timeout
- No persistent sessions across application restarts
- Session keys derived from master key
- Immediate invalidation on lock

## Audit and Logging

### Security Events Logged

- Vault creation and deletion
- Successful and failed authentication attempts
- Password/secret access (configurable)
- Configuration changes
- Import/export operations
- Backup creation

### Log Security

- Logs contain no sensitive data
- Timestamps in UTC
- Structured format for analysis
- Configurable retention period
- Optional log encryption

## Secure Development Practices

### Code Security

1. **Dependencies**
   - Minimal dependencies
   - Regular security audits
   - Automated vulnerability scanning
   - Pinned versions for reproducibility

2. **Build Process**
   - Reproducible builds
   - Signed releases
   - SHA-256 checksums
   - Secure CI/CD pipeline

3. **Testing**
   - Comprehensive test suite
   - Fuzzing for input validation
   - Security-specific test cases
   - Regular penetration testing

### Supply Chain Security

- All dependencies reviewed before inclusion
- Cryptographic dependencies from trusted sources
- Build artifacts signed with GPG
- Transparent build process

## Platform-Specific Security

### Linux

- Uses kernel keyring where available
- Respects XDG directory standards
- Integrates with system password managers
- SELinux/AppArmor compatible

### macOS

- Keychain integration (planned)
- Code signing for Gatekeeper
- Sandboxing for additional isolation
- Touch ID support (planned)

### Windows

- DPAPI integration (planned)
- Windows Hello support (planned)
- Secure desktop for password entry
- UAC compatibility

## Security Recommendations

### For Users

1. **Strong Master Password**
   - Minimum 12 characters
   - Use passphrase for memorability
   - Never reuse from other services

2. **System Security**
   - Keep OS updated
   - Use full-disk encryption
   - Enable firewall
   - Install antivirus

3. **Operational Security**
   - Lock vault when not in use
   - Regular backups
   - Verify downloads with checksums
   - Report suspicious behavior

### For Developers

1. **Contributing**
   - Follow secure coding guidelines
   - Add tests for security features
   - Document security implications
   - Request security review for crypto changes

2. **Deployment**
   - Verify signatures before deployment
   - Use latest stable version
   - Monitor security advisories
   - Plan for emergency updates

## Incident Response

### Security Issue Reporting

If you discover a security vulnerability:

1. **Do NOT** create a public GitHub issue
2. Email security details to security@pwgenrust.dev
3. Include:
   - Description of vulnerability
   - Steps to reproduce
   - Potential impact
   - Suggested fix (if any)

### Response Timeline

- **24 hours**: Initial acknowledgment
- **72 hours**: Preliminary assessment
- **7 days**: Fix development begins
- **30 days**: Patch released (critical issues faster)

## Future Security Enhancements

### Short Term (2025)

- Hardware security key support
- Biometric authentication
- Secure enclave integration
- Formal security audit

### Long Term (2026+)

- Post-quantum cryptography
- Distributed vault synchronization
- Multi-party computation for sharing
- Homomorphic encryption for cloud sync

## Compliance and Certifications

### Current Compliance

PwGen-rust's security architecture supports:
- **GDPR** - Privacy by design, data portability
- **HIPAA** - Encryption at rest, access controls
- **PCI DSS** - Strong cryptography, secure storage
- **SOX** - Audit trails, data integrity

### Security Development

PWGen-Rust prioritizes security through:
- Open source transparency and community review
- Memory-safe Rust implementation
- Modern cryptographic standards
- Regular security updates and improvements

## Resources

### Learn More

- [Encryption Details](encryption) - Deep dive into crypto implementation
- [Best Practices](best-practices) - Security recommendations
- [Threat Model](https://github.com/KiefStudioMA/PWGen-Rust/blob/main/SECURITY.md)
- [Rust Security](https://doc.rust-lang.org/book/ch19-01-unsafe-rust.html)`
  },

  'security/encryption': {
    title: 'Encryption',
    description: 'Deep dive into PwGen-rust\'s state-of-the-art encryption implementation and security architecture',
    content: `# Encryption

Deep dive into PwGen-rust's state-of-the-art encryption implementation and security architecture.

## Overview

PwGen-rust implements military-grade encryption using industry-standard algorithms and best practices. Our encryption architecture is designed to protect your sensitive data even in the event of database compromise.

## Encryption Standards

### AES-256-GCM (Galois/Counter Mode)

PwGen-rust uses **AES-256-GCM** for all data encryption, providing:

- **256-bit encryption keys** - Maximum security strength
- **Authenticated encryption** - Ensures data integrity and authenticity
- **NIST approved** - Meets [NIST SP 800-38D](https://nvlpubs.nist.gov/nistpubs/Legacy/SP/nistspecialpublication800-38d.pdf) standards
- **Performance optimized** - Hardware acceleration support where available

**Why AES-256-GCM?**
- Combines encryption and authentication in a single operation
- Prevents tampering and ensures data hasn't been modified
- Widely supported and thoroughly vetted by security experts
- Optimal balance of security and performance

### Argon2 Key Derivation

For key derivation from your master password, PwGen-rust uses **Argon2**, the winner of the Password Hashing Competition:

- **Memory-hard algorithm** - Resistant to GPU and ASIC attacks
- **Configurable parameters** - Time and memory costs can be adjusted
- **Side-channel resistant** - Protects against timing attacks
- **Future-proof** - Designed to remain secure as hardware improves

**Argon2 vs PBKDF2:**
- Argon2 is specifically designed to be memory-hard
- Provides better protection against parallel attacks
- More resistant to custom hardware attacks
- Recommended by [NIST SP 800-132](https://nvlpubs.nist.gov/nistpubs/Legacy/SP/nistspecialpublication800-132.pdf) for new applications

## Encryption Process

### 1. Master Password Processing

\`\`\`
User Input → Argon2 → 256-bit Master Key
\`\`\`

When you enter your master password:
1. Password is combined with a unique salt
2. Processed through Argon2 with configured iterations
3. Produces a 256-bit master key
4. Original password is immediately cleared from memory

### 2. Data Encryption

\`\`\`
Plaintext → AES-256-GCM → Ciphertext + Authentication Tag
\`\`\`

For each piece of data:
1. Generate a unique 96-bit nonce (never reused)
2. Encrypt data using AES-256-GCM with the master key
3. Produce ciphertext and 128-bit authentication tag
4. Store nonce, ciphertext, and tag together

### 3. Data Decryption

\`\`\`
Ciphertext + Tag → Verify → AES-256-GCM → Plaintext
\`\`\`

When accessing data:
1. Retrieve nonce, ciphertext, and authentication tag
2. Verify authentication tag (fails if data tampered)
3. Decrypt only if verification succeeds
4. Clear decrypted data from memory after use

## Security Features

### Zero-Knowledge Architecture

- **Local-only encryption** - All encryption happens on your device
- **No key escrow** - We never have access to your master password or keys
- **No recovery backdoors** - Lost master password means data is unrecoverable
- **Client-side security** - Server (when available) never sees unencrypted data

### Memory Security

PwGen-rust leverages Rust's memory safety features:

- **Automatic zeroization** - Sensitive data cleared from memory after use
- **No buffer overflows** - Rust's ownership system prevents memory vulnerabilities
- **Stack protection** - Sensitive data never stored in swap files
- **Secure random generation** - Uses OS-provided CSPRNG for all random values

### Key Security

- **Unique salts** - Every vault uses a unique salt for key derivation
- **No key reuse** - Each data item encrypted with unique nonce
- **Forward secrecy** - Compromising one item doesn't affect others
- **Key rotation support** - Change master password without re-encrypting data

## Implementation Details

### Cryptographic Libraries

PwGen-rust uses well-audited Rust cryptography libraries:

- **\`ring\`** - For AES-GCM encryption (same library used by AWS)
- **\`argon2\`** - Reference implementation of Argon2
- **\`getrandom\`** - Secure random number generation
- **\`zeroize\`** - Secure memory clearing

### Storage Format

Encrypted data is stored with:
\`\`\`
[Version][Salt][Nonce][Ciphertext][AuthTag]
\`\`\`

- **Version**: Allows for future algorithm upgrades
- **Salt**: Unique per vault (32 bytes)
- **Nonce**: Unique per encryption (12 bytes)
- **Ciphertext**: Variable length encrypted data
- **AuthTag**: Authentication tag (16 bytes)

## Threat Model

### What We Protect Against

- **Database theft** - Encrypted data useless without master password
- **Memory dumps** - Sensitive data zeroized after use
- **Tampering** - Authentication tags detect any modifications
- **Brute force** - Argon2 makes password cracking expensive
- **Side channels** - Constant-time operations where possible

### What We Cannot Protect Against

- **Compromised system** - Keyloggers or malware on your device
- **Weak master passwords** - Use strong, unique master password
- **Physical access** - Unlocked vault on unattended computer
- **User errors** - Sharing master password or leaving vault unlocked

## Best Practices

### Master Password Selection

1. **Length**: Minimum 12 characters, recommend 16+
2. **Complexity**: Mix of character types or passphrase
3. **Uniqueness**: Never used elsewhere
4. **Memorability**: You must remember it - no recovery possible

### Operational Security

1. **Regular backups** - Encrypted backups protect against data loss
2. **Auto-lock** - Configure appropriate timeout for your environment
3. **Secure machine** - Keep OS and software updated
4. **Physical security** - Lock computer when stepping away

### Compliance

PwGen-rust's encryption meets requirements for:
- **GDPR** - Strong encryption for personal data protection
- **HIPAA** - Suitable for healthcare data encryption
- **PCI DSS** - Appropriate for payment card data
- **SOX** - Adequate controls for financial data

## Technical Specifications

### Algorithm Parameters

**AES-256-GCM:**
- Key size: 256 bits
- Block size: 128 bits
- Nonce size: 96 bits
- Tag size: 128 bits

**Argon2id:**
- Memory: 64 MB (configurable)
- Iterations: 3 (configurable)
- Parallelism: 1
- Output: 256 bits

### Performance Characteristics

- **Encryption speed**: ~1GB/s on modern hardware with AES-NI
- **Key derivation**: 100-500ms (configurable for security/speed trade-off)
- **Memory usage**: ~100MB including Argon2 memory requirements
- **Database overhead**: ~5% for encryption metadata

## Future Enhancements

### Planned Improvements

- **Post-quantum algorithms** - Preparing for quantum computing threats
- **Hardware key support** - YubiKey and similar devices
- **Key rotation** - Automated master key rotation
- **Multiple key slots** - Support for multiple master passwords

### Algorithm Agility

PwGen-rust is designed to support algorithm updates:
- Version field allows detection of encryption version
- Modular design enables algorithm swapping
- Backward compatibility for reading old formats
- Migration tools for algorithm upgrades

## Resources

### Further Reading

- [NIST Cryptographic Standards](https://csrc.nist.gov/publications/sp)
- [Rust Crypto Libraries](https://github.com/RustCrypto)
- [OWASP Cryptographic Storage](https://cheatsheetseries.owasp.org/cheatsheets/Cryptographic_Storage_Cheat_Sheet.html)
- [Argon2 Specification](https://github.com/P-H-C/phc-winner-argon2)

### Security Audits

While PwGen-rust has not yet undergone formal security audit, we:
- Use well-audited cryptographic libraries
- Follow established best practices
- Welcome security research and responsible disclosure
- Plan formal audit as project matures

For security concerns, please see our [Security Policy](reporting).`
  },

  'security/best-practices': {
    title: 'Security Best Practices',
    description: 'Essential security guidelines for using PwGen-rust safely and effectively',
    content: `# Security Best Practices

Essential security guidelines for using PwGen-rust safely and effectively.

## Master Password Guidelines

### Creating a Strong Master Password

Your master password is the key to all your secrets. It must be:

1. **Unique** - Never used anywhere else
2. **Long** - Minimum 12 characters, recommend 16+
3. **Memorable** - You must remember it without writing it down
4. **Complex** - But not necessarily random

#### Recommended Approaches

**Passphrase Method (Recommended):**
\`\`\`
Example: "Coffee@7AM!Makes-Me-Happy2024"
- Easy to remember
- Long and complex
- Personal meaning
- Includes symbols and numbers
\`\`\`

**Story Method:**
\`\`\`
Example: "MyDog$pot!Loves2Chase#Squirrels"
- Creates mental image
- Natural to type
- Includes variety
- Hard to guess
\`\`\`

**Acronym Method:**
\`\`\`
Example: "IgmF@MIT1998&GotMyPhD2004!"
(I graduated from MIT in 1998 and got my PhD in 2004!)
- Personal significance
- Mix of characters
- Easy to remember
- Unique to you
\`\`\`

### What to Avoid

❌ **Common Patterns:**
- Password123!
- Qwerty!234
- Admin@2024
- P@ssw0rd

❌ **Personal Information:**
- Birthdays
- Names of family/pets
- Address information
- Phone numbers

❌ **Dictionary Words:**
- Even with substitutions (P@ssw0rd)
- Common phrases
- Keyboard patterns

## Operational Security

### Daily Usage

1. **Lock When Away**
   \`\`\`bash
   # Configure auto-lock (in minutes)
   pwgen-cli config set auto-lock-minutes 5
   \`\`\`
   - Always lock before stepping away
   - Use keyboard shortcut: \`Ctrl+L\`
   - Configure aggressive auto-lock for sensitive environments

2. **Clipboard Security**
   - Clipboard auto-clears after 30 seconds
   - Never paste passwords into untrusted applications
   - Be aware of clipboard managers that may store history

3. **Visual Security**
   - Position screen away from cameras/windows
   - Use privacy screens in public spaces
   - Be aware of shoulder surfers
   - Use password reveal sparingly

### System Security

1. **Keep Software Updated**
   \`\`\`bash
   # Check for updates regularly
   pwgen-cli --version
   
   # Update to latest version
   # Linux (Snap)
   sudo snap refresh pwgen-rust
   \`\`\`

2. **Secure Your Device**
   - Enable full-disk encryption
   - Use strong system password
   - Enable firewall
   - Keep OS updated
   - Use antivirus software

3. **Network Security**
   - PwGen-rust works offline (no network needed)
   - Be cautious of network monitoring
   - Use VPN on untrusted networks
   - Verify downloads from official sources

## Password Management

### Password Quality

1. **Use the Generator**
   \`\`\`bash
   # Generate strong passwords
   pwgen-cli generate --length 20 --symbols
   
   # For maximum security
   pwgen-cli generate --length 32 --all-chars
   \`\`\`

2. **Unique Passwords**
   - Never reuse passwords across sites
   - Use tags to organize similar accounts
   - Regular security audits to find duplicates

3. **Password Rotation**
   - Change passwords after breaches
   - Rotate high-value accounts quarterly
   - Update weak passwords immediately
   - Keep password history for recovery

### Account Organization

1. **Effective Tagging**
   \`\`\`
   Recommended tag structure:
   - Category: work, personal, family
   - Security: 2fa, no-2fa, sensitive
   - Type: email, banking, social, dev
   - Status: active, inactive, shared
   \`\`\`

2. **Naming Conventions**
   \`\`\`
   Good examples:
   - "GitHub - Personal"
   - "AWS - Work Account"
   - "Bank of America - Checking"
   
   Avoid:
   - "Login 1"
   - "Website"
   - "Password"
   \`\`\`

3. **Notes Field Usage**
   - Security questions (answers only)
   - Account numbers (partial)
   - Recovery information
   - 2FA backup codes

## Backup Strategy

### Regular Backups

1. **Backup Schedule**
   \`\`\`bash
   # Weekly backup (automate with cron)
   pwgen-cli backup create ~/backups/pwgen-$(date +%Y%m%d).backup
   \`\`\`

2. **Backup Storage**
   - Store on separate device
   - Encrypt backup location
   - Test restore process regularly
   - Keep multiple versions

3. **Backup Security**
   - Backups are encrypted with master password
   - Store backup password hint separately
   - Consider off-site storage
   - Protect backup media physically

### Recovery Planning

1. **Document Recovery Process**
   - Store instructions securely
   - Test recovery procedures
   - Keep software versions noted
   - Multiple recovery options

2. **Emergency Access**
   - Consider password splitting with trusted parties
   - Document account recovery procedures
   - Maintain alternate contact methods
   - Plan for incapacitation

## Advanced Security

### Two-Factor Authentication

1. **Enable 2FA Everywhere**
   - Prioritize financial accounts
   - Use app-based 2FA over SMS
   - Store backup codes in PwGen-rust
   - Document 2FA setup in notes

2. **2FA Best Practices**
   \`\`\`
   Store in PwGen notes:
   - Backup codes (encrypted)
   - QR code text (for recovery)
   - Recovery phone/email
   - Setup date
   \`\`\`

### Secret Management

1. **API Keys**
   - Set expiration reminders
   - Rotate regularly
   - Document permissions/scopes
   - Never commit to version control

2. **SSH Keys**
   - Use strong passphrases
   - Store private keys encrypted
   - Document key purposes
   - Regular key rotation

3. **Sensitive Documents**
   - Encrypt before storing
   - Verify integrity regularly
   - Limit access logging
   - Secure deletion when removed

## Security Auditing

### Regular Audits

1. **Monthly Reviews**
   \`\`\`bash
   # Find weak passwords
   pwgen-cli analyze weak
   
   # Find duplicates
   pwgen-cli analyze duplicates
   
   # Check for old passwords
   pwgen-cli list --older-than 365d
   \`\`\`

2. **Audit Checklist**
   - [ ] Review weak passwords
   - [ ] Check for duplicates
   - [ ] Update old passwords
   - [ ] Remove unused accounts
   - [ ] Verify 2FA status
   - [ ] Test backups

### Breach Response

1. **Immediate Actions**
   - Change affected password immediately
   - Check for password reuse
   - Enable 2FA if not already
   - Monitor account activity

2. **Follow-up**
   - Update related accounts
   - Review security questions
   - Check for identity theft
   - Document breach details

## Sharing Secrets

### Secure Sharing Methods

1. **Temporary Sharing**
   - Use secure messaging apps
   - Set message expiration
   - Verify recipient identity
   - Change password after sharing

2. **Team Sharing**
   - Wait for PwGen-rust team features
   - Use separate vaults for teams
   - Document access policies
   - Regular access reviews

### What Never to Share

❌ **Never Share:**
- Your master password
- Your vault file
- Unencrypted passwords
- Screenshots of passwords
- Passwords over email
- Passwords in chat apps

## Mobile Security

### Mobile Considerations

Until official mobile apps are released:

1. **Alternatives**
   - Use strong device lock
   - Enable device encryption
   - Avoid password managers in browsers
   - Use secure notes apps temporarily

2. **Syncing Caution**
   - Don't sync vault to cloud services
   - Avoid automatic backups to cloud
   - Be cautious with file sharing apps
   - Wait for official sync features

## Compliance Considerations

### Industry Standards

1. **Healthcare (HIPAA)**
   - Separate vault for PHI
   - Document access logs
   - Regular security training
   - Incident response plan

2. **Financial (PCI DSS)**
   - Segregate payment data
   - Quarterly password rotation
   - Strong password requirements
   - Access control documentation

3. **Enterprise (SOX)**
   - Audit trail maintenance
   - Separation of duties
   - Change documentation
   - Regular compliance reviews

## Emergency Procedures

### Lost Master Password

1. **Prevention**
   - Create password hint (not password)
   - Consider secure password splitting
   - Regular mental practice
   - Backup recovery planning

2. **If Lost**
   - No recovery possible (by design)
   - Restore from backup if available
   - Start fresh if no backup
   - Learn from experience

### Compromised Vault

1. **Immediate Response**
   - Change master password
   - Rotate all passwords
   - Review access logs
   - Check for unauthorized changes

2. **Investigation**
   - Determine compromise scope
   - Check system for malware
   - Review security practices
   - Implement improvements

## Security Culture

### Building Good Habits

1. **Daily Practices**
   - Lock vault when done
   - Verify site authenticity
   - Question password requests
   - Report suspicious activity

2. **Continuous Improvement**
   - Stay informed on threats
   - Regular security training
   - Share knowledge with others
   - Contribute to community

### Red Flags to Watch

⚠️ **Warning Signs:**
- Unexpected password prompts
- Changed passwords you didn't modify
- Unusual account activity
- Phishing attempts
- System performance issues

## Getting Help

### Support Resources

- **Documentation**: [Full docs](https://pwgenrust.dev/docs)
- **Security Issues**: security@pwgenrust.dev
- **Community**: [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)
- **Updates**: [@HxHippy](https://x.com/HxHippy)

### Reporting Security Issues

If you discover a security vulnerability:
1. Do NOT post publicly
2. Email details privately
3. Include steps to reproduce
4. Allow time for fix
5. Coordinated disclosure

---

Remember: Security is not a feature, it's a practice. Stay vigilant, stay updated, and stay secure with PwGen-rust! 🔐`
  },

  'security/reporting': {
    title: 'Security Reporting',
    description: 'How to report security vulnerabilities in PwGen-rust responsibly and effectively',
    content: `# Security Reporting

How to report security vulnerabilities in PwGen-rust responsibly and effectively.

## Reporting Security Issues

If you discover a security vulnerability, please report it responsibly:

### Contact Information

- **Primary Email**: security@pwgenrust.dev
- **Alternative**: security@hxhippy.com
- **Response Time**: Within 24-48 hours
- **Follow Updates**: [@HxHippy](https://x.com/HxHippy)

### What to Include

1. **Description**: Clear description of the vulnerability
2. **Steps**: How to reproduce the issue
3. **Impact**: Potential security impact
4. **Environment**: Operating system, version, etc.

### What NOT to Include

- Do not publicly disclose the vulnerability
- Do not exploit the vulnerability beyond verification
- Do not access data that isn't yours

## Security Response Process

1. **Acknowledgment**: We'll acknowledge receipt within 48 hours
2. **Investigation**: We'll investigate and assess the issue
3. **Fix**: We'll develop and test a fix
4. **Disclosure**: Coordinated disclosure after fix is available
5. **Credit**: We'll credit reporters (if desired)

## Scope

### In Scope
- Core PwGen-rust applications (CLI, GUI)
- Cryptographic implementations (AES-256-GCM, Argon2)
- Data handling and storage (SQLite encryption)
- Authentication mechanisms
- Memory safety issues
- Key management

### Out of Scope
- Third-party dependencies (report upstream)
- Social engineering attacks
- Physical access attacks
- Brute force attacks

## Security Best Practices

### For Users
- Keep PwGen-rust updated to latest version
- Use strong, unique master passwords (12+ characters)
- Enable auto-lock features (default: 15 minutes)
- Regular encrypted backups in secure locations
- Verify downloads with SHA-256 checksums

### For Developers
- Follow secure coding practices
- Regular security audits
- Dependency vulnerability scanning
- Code review process

## Bug Bounty

We currently don't offer monetary rewards, but we provide:
- Public acknowledgment (if desired)
- Mention in release notes
- Direct contact with development team

## Previous Security Issues

We maintain transparency about past security issues:
- [Security Advisories](https://github.com/KiefStudioMA/PWGen-Rust/security/advisories)
- [CVE Database](https://cve.mitre.org/)
- [Release Notes](https://github.com/KiefStudioMA/PWGen-Rust/releases)

## Security Features

PwGen-rust implements defense-in-depth security:
- **AES-256-GCM encryption** with authenticated encryption
- **Argon2 key derivation** (memory-hard, side-channel resistant)
- **Rust memory safety** preventing buffer overflows and use-after-free
- **Zeroization** of sensitive data in memory
- **Protection against timing attacks**
- **Local-first architecture** (no network exposure)
- **Regular dependency audits**

## Contact

For security-related questions:
- **Email**: security@pwgenrust.dev
- **GitHub**: [Security tab](https://github.com/KiefStudioMA/PWGen-Rust/security)
- **Policy**: [SECURITY.md](https://github.com/KiefStudioMA/PWGen-Rust/blob/main/SECURITY.md)
- **Community**: [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)`
  },

  'developers/api': {
    title: 'API Server',
    description: 'The API server component for PwGen-rust is currently in development',
    content: `# API Server

The API server component for PwGen-rust is currently in development. At present, only placeholder code exists in the repository.

## Current Status

⚠️ **Note**: The API server functionality is not yet available in the current release. If you're looking for API integrations, please stay tuned for future releases!

## What's Coming

The planned API server will provide:
- RESTful endpoints for password and secrets management
- Secure authentication and authorization
- Integration capabilities for custom workflows
- Team collaboration features
- Automated backup and synchronization

## Roadmap

The API server is scheduled for release in Q1 2026 as part of our development roadmap. Follow our progress on [GitHub](https://github.com/KiefStudioMA/PWGen-Rust) or join the discussion in our [community forums](https://github.com/KiefStudioMA/PWGen-Rust/discussions).

## Current Alternatives

While the API server is in development, you can:
- Use the **CLI** for scripting and automation
- Leverage the **GUI** for interactive management
- Import/export data using supported formats

For updates on the API development, please watch our [GitHub repository](https://github.com/KiefStudioMA/PWGen-Rust) or follow [@HxHippy](https://x.com/HxHippy) on X/Twitter.`
  },

  'developers/building': {
    title: 'Building from Source',
    description: 'Complete guide to building PwGen-rust from source code on all supported platforms',
    content: `# Building from Source

Complete guide to building PwGen-rust from source code on all supported platforms.

## Prerequisites

### Required Tools

1. **Rust Toolchain**
   \`\`\`bash
   # Install rustup (Rust installer)
   curl --proto '=https' --tlsv1.2 -sSf https://sh.rustup.rs | sh
   
   # Use minimum required version
   rustup install 1.75.0
   rustup default 1.75.0
   \`\`\`

2. **Git**
   \`\`\`bash
   # Ubuntu/Debian
   sudo apt install git
   
   # macOS
   brew install git
   
   # Windows: Download from git-scm.com
   \`\`\`

3. **Build Tools**
   \`\`\`bash
   # Ubuntu/Debian
   sudo apt install build-essential pkg-config
   
   # CentOS/RHEL/Fedora
   sudo dnf install gcc gcc-c++ pkg-config
   
   # macOS (install Xcode Command Line Tools)
   xcode-select --install
   
   # Windows (install Visual Studio Build Tools)
   # Download from visualstudio.microsoft.com
   \`\`\`

### Platform-Specific Dependencies

#### Linux

**Ubuntu/Debian:**
\`\`\`bash
# GUI dependencies
sudo apt install libgtk-3-dev libgtk-3-0

# Optional: For system integration
sudo apt install libxss1 libgconf-2-4 libnss3 libxtst6 libatspi2.0-0 libdrm2 libxcomposite1 libxdamage1 libxrandr2 libgbm1 libxss1 libasound2

# SSL support
sudo apt install libssl-dev
\`\`\`

**CentOS/RHEL/Fedora:**
\`\`\`bash
# GUI dependencies
sudo dnf install gtk3-devel

# SSL support
sudo dnf install openssl-devel
\`\`\`

**Arch Linux:**
\`\`\`bash
# All dependencies
sudo pacman -S base-devel gtk3 openssl
\`\`\`

#### macOS

\`\`\`bash
# Install dependencies via Homebrew
brew install pkg-config openssl cmake

# Set environment variables for OpenSSL
echo 'export PATH="/opt/homebrew/bin:$PATH"' >> ~/.zshrc
echo 'export LDFLAGS="-L/opt/homebrew/lib"' >> ~/.zshrc
echo 'export CPPFLAGS="-I/opt/homebrew/include"' >> ~/.zshrc
\`\`\`

#### Windows

1. **Visual Studio Build Tools 2022**
   - Download and install from Microsoft
   - Include C++ build tools
   - Include Windows 10/11 SDK

2. **Optional: vcpkg for dependencies**
   \`\`\`powershell
   git clone https://github.com/Microsoft/vcpkg.git
   cd vcpkg
   .\\bootstrap-vcpkg.bat
   \`\`\`

## Source Code

### Clone Repository

\`\`\`bash
# Clone the repository
git clone https://github.com/KiefStudioMA/PWGen-Rust.git
cd PWGen-Rust

# Check available branches/tags
git branch -a
git tag -l

# Use specific version (recommended for production)
git checkout v1.2.0
\`\`\`

### Repository Structure

\`\`\`
PWGen-Rust/
├── Cargo.toml          # Workspace configuration
├── Cargo.lock          # Dependency lockfile
├── pwgen-core/         # Core library
├── pwgen-cli/          # Command-line interface
├── pwgen-gui/          # Desktop GUI application
├── pwgen-server/       # API server (in development)
├── scripts/            # Build and installation scripts
├── docs/               # Additional documentation
├── tests/              # Integration tests
└── .github/            # CI/CD workflows
\`\`\`

## Build Process

### Default Build

\`\`\`bash
# Build all components (debug mode)
cargo build

# Build specific component
cargo build -p pwgen-cli
cargo build -p pwgen-gui
cargo build -p pwgen-core

# Run tests
cargo test

# Format code
cargo fmt

# Run linter
cargo clippy -- -D warnings
\`\`\`

### Release Build

\`\`\`bash
# Build optimized release version
cargo build --release

# Binaries will be in target/release/
ls target/release/pwgen-*
\`\`\`

### Feature-Specific Builds

PwGen-rust supports conditional compilation for different use cases:

\`\`\`bash
# Minimal build (CLI only, no GUI dependencies)
cargo build --release --no-default-features --features "cli"

# GUI only
cargo build --release --no-default-features --features "gui"

# Maximum security build
cargo build --release --features "secure"

# Development build with debug features
cargo build --features "dev-tools,logging"
\`\`\`

### Cross-Compilation

#### Linux to Windows

\`\`\`bash
# Install target
rustup target add x86_64-pc-windows-gnu

# Install cross-compilation tools
sudo apt install gcc-mingw-w64-x86-64

# Build
cargo build --release --target x86_64-pc-windows-gnu
\`\`\`

#### macOS to Linux

\`\`\`bash
# Install target
rustup target add x86_64-unknown-linux-gnu

# Note: May require Docker or additional setup for dependencies
\`\`\`

## Build Optimization

### Performance Optimization

\`\`\`toml
# Cargo.toml profile for maximum performance
[profile.release]
opt-level = 3           # Maximum optimization
lto = "fat"             # Link-time optimization
codegen-units = 1       # Single codegen unit for better optimization
panic = "abort"         # Smaller binary size
strip = true            # Remove debug symbols
\`\`\`

### Size Optimization

\`\`\`toml
# Cargo.toml profile for minimal size
[profile.min-size]
inherits = "release"
opt-level = "z"         # Optimize for size
lto = true
panic = "abort"
strip = true
\`\`\`

\`\`\`bash
# Build with size optimization
cargo build --profile min-size
\`\`\`

### Security-Focused Build

\`\`\`toml
# Cargo.toml profile for security
[profile.secure]
inherits = "release"
overflow-checks = true  # Runtime integer overflow checks
debug-assertions = true # Enable debug assertions in release
\`\`\`

## Platform-Specific Builds

### Linux Packaging

#### AppImage

\`\`\`bash
# Install linuxdeploy
wget https://github.com/linuxdeploy/linuxdeploy/releases/download/continuous/linuxdeploy-x86_64.AppImage
chmod +x linuxdeploy-x86_64.AppImage

# Build and package
cargo build --release
./scripts/build-appimage.sh
\`\`\`

#### Snap Package

\`\`\`bash
# Install snapcraft
sudo apt install snapcraft

# Build snap
snapcraft
\`\`\`

#### Debian Package

\`\`\`bash
# Install cargo-deb
cargo install cargo-deb

# Build .deb package
cargo deb -p pwgen-gui
\`\`\`

### macOS Packaging

#### App Bundle

\`\`\`bash
# Build GUI application
cargo build --release -p pwgen-gui

# Create app bundle
./scripts/create-macos-app.sh

# Sign and notarize (requires Apple Developer account)
codesign --sign "Developer ID Application: Your Name" PwGen.app
xcrun notarytool submit PwGen.dmg
\`\`\`

#### Installer Package

\`\`\`bash
# Create .pkg installer
pkgbuild --root dist/macos --identifier dev.pwgenrust.pwgen --version 1.2.0 PwGen.pkg
\`\`\`

### Windows Packaging

#### NSIS Installer

\`\`\`bash
# Install NSIS (on Windows)
# Download from nsis.sourceforge.io

# Build executable
cargo build --release --target x86_64-pc-windows-msvc

# Create installer
makensis scripts/windows-installer.nsi
\`\`\`

#### MSI Installer (WiX)

\`\`\`bash
# Install WiX Toolset
# Download from wixtoolset.org

# Build MSI
candle scripts/pwgen.wxs
light pwgen.wixobj -out PwGen.msi
\`\`\`

## Testing

### Unit Tests

\`\`\`bash
# Run all tests
cargo test

# Run tests with output
cargo test -- --nocapture

# Run specific test
cargo test test_encryption

# Run tests for specific crate
cargo test -p pwgen-core
\`\`\`

### Integration Tests

\`\`\`bash
# Run integration tests
cargo test --test integration

# Run with real vault file
PWGEN_TEST_VAULT=/tmp/test.vault cargo test
\`\`\`

### Security Tests

\`\`\`bash
# Install cargo-audit for vulnerability scanning
cargo install cargo-audit

# Check for vulnerabilities
cargo audit

# Install cargo-deny for dependency analysis
cargo install cargo-deny

# Check licenses and security
cargo deny check
\`\`\`

### Fuzzing

\`\`\`bash
# Install cargo-fuzz
cargo install cargo-fuzz

# Run fuzzing tests
cargo fuzz run fuzz_encryption
cargo fuzz run fuzz_parser
\`\`\`

## Continuous Integration

### GitHub Actions

The project includes CI/CD workflows:

\`\`\`yaml
# .github/workflows/ci.yml
name: CI
on: [push, pull_request]
jobs:
  test:
    runs-on: \${{ matrix.os }}
    strategy:
      matrix:
        os: [ubuntu-latest, macos-latest, windows-latest]
        rust: [stable, beta]
    steps:
      - uses: actions/checkout@v4
      - uses: dtolnay/rust-toolchain@master
        with:
          toolchain: \${{ matrix.rust }}
      - run: cargo test --all-features
\`\`\`

### Local CI Simulation

\`\`\`bash
# Install act to run GitHub Actions locally
curl https://raw.githubusercontent.com/nektos/act/master/install.sh | sudo bash

# Run CI locally
act
\`\`\`

## Troubleshooting

### Common Build Issues

#### Missing Dependencies

**Error:** \`error: Microsoft Visual C++ 14.0 is required\`
\`\`\`bash
# Solution: Install Visual Studio Build Tools
\`\`\`

**Error:** \`fatal error: 'gtk/gtk.h' file not found\`
\`\`\`bash
# Solution: Install GTK development headers
sudo apt install libgtk-3-dev
\`\`\`

#### Compilation Errors

**Error:** \`error: linking with 'cc' failed\`
\`\`\`bash
# Solution: Install build tools
sudo apt install build-essential
\`\`\`

**Error:** \`error: could not find system library 'openssl'\`
\`\`\`bash
# Ubuntu/Debian
sudo apt install libssl-dev

# macOS
brew install openssl
export OPENSSL_DIR=/opt/homebrew/opt/openssl
\`\`\`

#### Performance Issues

**Slow compilation:**
\`\`\`bash
# Use more CPU cores
export CARGO_BUILD_JOBS=4

# Use faster linker (Linux)
sudo apt install lld
export RUSTFLAGS="-C link-arg=-fuse-ld=lld"
\`\`\`

**Large binary size:**
\`\`\`bash
# Strip debug symbols
cargo build --release
strip target/release/pwgen-gui

# Use UPX compression (optional)
upx --best target/release/pwgen-gui
\`\`\`

## Advanced Topics

### Custom Build Scripts

Create custom build configurations:

\`\`\`toml
# build.rs
fn main() {
    // Custom build logic
    println!("cargo:rustc-link-lib=ssl");
    
    // Platform-specific compilation
    if cfg!(target_os = "windows") {
        println!("cargo:rustc-link-lib=crypt32");
    }
}
\`\`\`

### Environment Variables

Control build behavior:

\`\`\`bash
# Debug logging during build
export RUST_LOG=debug

# Custom features
export PWGEN_FEATURES="secure,audit"

# Build with custom config
export PWGEN_CONFIG_PATH=/custom/config.toml
\`\`\`

### Memory Safety

Verify memory safety:

\`\`\`bash
# Install Miri for unsafe code checking
rustup component add miri

# Run with Miri
cargo miri test
\`\`\`

## Release Process

### Preparing a Release

\`\`\`bash
# Update version numbers
sed -i 's/version = "1.1.0"/version = "1.2.0"/' Cargo.toml

# Update changelog
echo "## [1.2.0] - $(date +%Y-%m-%d)" >> CHANGELOG.md

# Run full test suite
cargo test --all-features

# Build all targets
cargo build --release --all-features
\`\`\`

### Creating Release Artifacts

\`\`\`bash
# Run release script
./scripts/prepare-release.sh v1.2.0

# This creates:
# - Linux: .tar.gz, .deb, .AppImage
# - macOS: .dmg, .pkg
# - Windows: .exe, .msi
# - Source: .tar.gz with verification
\`\`\`

## Resources

### Build Tools

- [Rust Installation](https://rustup.rs/)
- [Cargo Book](https://doc.rust-lang.org/cargo/)
- [Cross-compilation Guide](https://rust-lang.github.io/rustup/cross-compilation.html)

### Platform-Specific

- [Linux Packaging](https://wiki.debian.org/HowToPackageForDebian)
- [macOS Deployment](https://developer.apple.com/documentation/xcode/distributing-your-app-for-beta-testing-and-releases)
- [Windows Packaging](https://docs.microsoft.com/en-us/windows/msix/)

### Security

- [Rust Security Guidelines](https://anssi-fr.github.io/rust-guide/)
- [Supply Chain Security](https://github.com/RustSec/rustsec)
- [Cargo Audit](https://github.com/RustSec/rustsec/tree/main/cargo-audit)

---

For additional help with building, see our [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions) or [open an issue](https://github.com/KiefStudioMA/PWGen-Rust/issues).`
  },

  'developers/contributing': {
    title: 'Contributing',
    description: 'Guide for contributing to PwGen-rust development',
    content: `# Contributing

Help make PwGen-rust even better! This guide covers everything you need to know about contributing to the project.

## Getting Started

### Prerequisites

1. **Rust Development Environment**
   - Latest stable Rust (1.75+)
   - Familiarity with Rust programming
   - Basic understanding of cryptography concepts

2. **Development Tools**
   - Git for version control
   - Your preferred IDE/editor
   - Understanding of security best practices

### Quick Setup

\`\`\`bash
# Fork and clone the repository
git clone https://github.com/YOUR_USERNAME/PWGen-Rust.git
cd PWGen-Rust

# Build and test
cargo build
cargo test

# Format and lint
cargo fmt
cargo clippy
\`\`\`

## Contribution Guidelines

### Code of Conduct

We are committed to providing a welcoming and inclusive experience for everyone. Please read our [Code of Conduct](https://github.com/KiefStudioMA/PWGen-Rust/blob/main/CODE_OF_CONDUCT.md) before contributing.

### Types of Contributions

- **Bug Reports**: Help us identify and fix issues
- **Feature Requests**: Suggest new functionality
- **Code Contributions**: Submit pull requests
- **Documentation**: Improve guides and tutorials
- **Security**: Report vulnerabilities responsibly
- **Testing**: Help expand test coverage

## Development Workflow

### 1. Create an Issue

Before starting work:
- Check existing issues to avoid duplication
- Create a new issue describing your proposal
- Wait for maintainer feedback on significant changes

### 2. Fork and Branch

\`\`\`bash
# Fork the repository on GitHub
# Clone your fork
git clone https://github.com/YOUR_USERNAME/PWGen-Rust.git

# Create a feature branch
git checkout -b feature/your-feature-name

# Or for bug fixes
git checkout -b fix/issue-123
\`\`\`

### 3. Development

Follow our coding standards:
- Write clear, documented code
- Include comprehensive tests
- Follow Rust conventions
- Add security considerations

### 4. Testing

\`\`\`bash
# Run all tests
cargo test

# Run specific tests
cargo test test_name

# Check formatting
cargo fmt --check

# Run linter
cargo clippy -- -D warnings

# Security audit
cargo audit
\`\`\`

### 5. Commit and Push

\`\`\`bash
# Make focused commits
git add .
git commit -m "feat: add password strength meter

- Implement zxcvbn algorithm
- Add visual strength indicator
- Include tests for edge cases"

# Push to your fork
git push origin feature/your-feature-name
\`\`\`

### 6. Pull Request

Create a pull request with:
- Clear title and description
- Reference to related issues
- Screenshots for UI changes
- Test coverage information

## Coding Standards

### Rust Style

Follow standard Rust conventions:
\`\`\`rust
// Good: Clear, documented functions
/// Encrypts plaintext using AES-256-GCM
/// 
/// # Arguments
/// * \`plaintext\` - Data to encrypt
/// * \`key\` - 256-bit encryption key
/// 
/// # Returns
/// Encrypted data with nonce and authentication tag
pub fn encrypt(plaintext: &[u8], key: &Key) -> Result<Vec<u8>, CryptoError> {
    // Implementation
}

// Bad: Unclear, undocumented
pub fn enc(d: &[u8], k: &Key) -> Result<Vec<u8>, CryptoError> {
    // Implementation
}
\`\`\`

### Security Considerations

- **No Unsafe Code**: Avoid \`unsafe\` unless absolutely necessary
- **Zeroize Secrets**: Always clear sensitive data from memory
- **Constant-Time Operations**: Use constant-time comparisons for secrets
- **Input Validation**: Validate all inputs thoroughly
- **Error Handling**: Handle errors gracefully without leaking information

### Documentation

- Document all public APIs
- Include examples in doc comments
- Update README and guides as needed
- Write clear commit messages

## Security Contributions

### Reporting Vulnerabilities

**Do NOT create public issues for security vulnerabilities.**

Instead:
1. Email: security@pwgenrust.dev
2. Include detailed reproduction steps
3. Wait for acknowledgment
4. Allow time for fix before disclosure

### Security Code Reviews

Security-sensitive changes require:
- Extra careful review
- Multiple approvals
- Security-focused testing
- Documentation of security implications

## Testing Guidelines

### Test Types

1. **Unit Tests**: Test individual functions
2. **Integration Tests**: Test component interactions
3. **Security Tests**: Test cryptographic functions
4. **Performance Tests**: Benchmark critical paths

### Writing Tests

\`\`\`rust
#[cfg(test)]
mod tests {
    use super::*;

    #[test]
    fn test_password_encryption_roundtrip() {
        let password = "test_password";
        let encrypted = encrypt_password(password).unwrap();
        let decrypted = decrypt_password(&encrypted).unwrap();
        assert_eq!(password, decrypted);
    }

    #[test]
    fn test_invalid_key_rejection() {
        let result = decrypt_password(&[0u8; 32]);
        assert!(result.is_err());
    }
}
\`\`\`

## Documentation Contributions

### Areas Needing Help

- User guides and tutorials
- API documentation
- Security documentation
- Architecture documentation
- Troubleshooting guides

### Writing Guidelines

- Clear, concise language
- Step-by-step instructions
- Code examples that work
- Screenshots where helpful
- Consider different skill levels

## Release Process

### Version Numbering

We follow [Semantic Versioning](https://semver.org/):
- \`MAJOR.MINOR.PATCH\`
- Major: Breaking changes
- Minor: New features
- Patch: Bug fixes

### Release Checklist

Before releases:
- [ ] All tests pass
- [ ] Documentation updated
- [ ] Changelog updated
- [ ] Security review completed
- [ ] Performance regression check

## Community

### Communication Channels

- **GitHub Issues**: Bug reports and feature requests
- **GitHub Discussions**: General discussion and questions
- **Twitter**: [@HxHippy](https://x.com/HxHippy) for updates
- **Email**: security@pwgenrust.dev for security issues

### Recognition

Contributors are recognized through:
- Credits in release notes
- Contributor list in README
- Special recognition for significant contributions

## Getting Help

### Resources

- [Rust Book](https://doc.rust-lang.org/book/)
- [Rust Security Guidelines](https://anssi-fr.github.io/rust-guide/)
- [Project Architecture](architecture)
- [Building Guide](building)

### Questions

- Check existing [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)
- Create new discussion for general questions
- Tag maintainers for urgent issues

## License

By contributing to PwGen-rust, you agree that your contributions will be licensed under the Apache 2.0 License.

---

Thank you for contributing to PwGen-rust! Together we can build the most secure password manager possible. 🔐`
  },

  'developers/architecture': {
    title: 'Developer Architecture',
    description: 'Technical deep-dive into PwGen-rust architecture for developers',
    content: `# Developer Architecture

Technical deep-dive into PwGen-rust's architecture for contributors and developers.

## Overview

PwGen-rust is built as a modular, secure password management system leveraging Rust's memory safety guarantees and modern cryptographic standards. This document provides detailed technical information for developers contributing to or building upon PwGen-rust.

## Project Structure

\`\`\`text
pwgen/
├── pwgen-core/          # Core library with business logic
│   ├── src/
│   │   ├── crypto/      # Encryption/decryption modules
│   │   ├── storage/     # Database and file operations
│   │   ├── models/      # Data structures
│   │   └── lib.rs       # Library entry point
│   └── Cargo.toml
├── pwgen-cli/           # Command-line interface
│   ├── src/
│   │   ├── commands/    # CLI command implementations
│   │   └── main.rs      # CLI entry point
│   └── Cargo.toml
├── pwgen-gui/           # Graphical user interface
│   ├── src/
│   │   ├── ui/          # UI components
│   │   └── main.rs      # GUI entry point
│   └── Cargo.toml
└── pwgen-server/        # API server (in development)
    ├── src/
    └── Cargo.toml
\`\`\`

## Core Components

### pwgen-core

The heart of PwGen-rust, providing:

#### Cryptography Module (\`crypto/\`)
\`\`\`rust
// Key components
pub struct CryptoEngine {
    // AES-256-GCM for encryption
    cipher: Aes256Gcm,
    // Argon2 for key derivation
    kdf: Argon2,
}

// Main operations
impl CryptoEngine {
    pub fn encrypt(&self, plaintext: &[u8]) -> Result<Vec<u8>>;
    pub fn decrypt(&self, ciphertext: &[u8]) -> Result<Vec<u8>>;
    pub fn derive_key(&self, password: &str, salt: &[u8]) -> Result<Key>;
}
\`\`\`

#### Storage Module (\`storage/\`)
\`\`\`rust
// Database abstraction
pub trait StorageBackend {
    fn create_vault(&self, path: &Path) -> Result<Vault>;
    fn open_vault(&self, path: &Path, key: &Key) -> Result<Vault>;
    fn save_entry(&self, entry: &Entry) -> Result<()>;
    fn get_entry(&self, id: &Uuid) -> Result<Entry>;
}

// SQLite implementation
pub struct SqliteStorage {
    connection: Connection,
    crypto: CryptoEngine,
}
\`\`\`

#### Models (\`models/\`)
\`\`\`rust
// Core data structures
#[derive(Serialize, Deserialize)]
pub struct PasswordEntry {
    pub id: Uuid,
    pub site: String,
    pub username: String,
    pub password: SecureString,  // Zeroizes on drop
    pub notes: Option<String>,
    pub tags: Vec<String>,
    pub created: DateTime<Utc>,
    pub modified: DateTime<Utc>,
}

#[derive(Serialize, Deserialize)]
pub struct Secret {
    pub id: Uuid,
    pub name: String,
    pub secret_type: SecretType,
    pub data: SecureBytes,       // Zeroizes on drop
    pub metadata: HashMap<String, String>,
}
\`\`\`

### pwgen-cli

Command-line interface built with \`clap\`:

\`\`\`rust
// Command structure
#[derive(Parser)]
#[command(name = "pwgen-cli")]
#[command(about = "Secure password manager CLI")]
struct Cli {
    #[command(subcommand)]
    command: Commands,
}

#[derive(Subcommand)]
enum Commands {
    Password(PasswordCommand),
    Secret(SecretCommand),
    Generate(GenerateCommand),
    Vault(VaultCommand),
}
\`\`\`

### pwgen-gui

Desktop GUI built with \`egui\`:

\`\`\`rust
// Main application state
pub struct PwGenApp {
    vault: Option<Vault>,
    ui_state: UiState,
    crypto: CryptoEngine,
}

impl eframe::App for PwGenApp {
    fn update(&mut self, ctx: &egui::Context, frame: &mut eframe::Frame) {
        // UI rendering logic
    }
}
\`\`\`

## Security Architecture

For detailed security architecture, see [Security Architecture](../security/architecture).

Key security features:
- Zero-knowledge architecture
- Memory-safe Rust implementation
- Authenticated encryption (AES-256-GCM)
- Memory-hard key derivation (Argon2)
- Automatic memory zeroization

## Database Schema

### SQLite Schema

\`\`\`sql
-- Vault metadata
CREATE TABLE vault_metadata (
    id INTEGER PRIMARY KEY,
    version INTEGER NOT NULL,
    created_at TIMESTAMP NOT NULL,
    salt BLOB NOT NULL,
    nonce BLOB NOT NULL,
    settings BLOB  -- Encrypted JSON
);

-- Password entries
CREATE TABLE passwords (
    id TEXT PRIMARY KEY,
    encrypted_data BLOB NOT NULL,
    nonce BLOB NOT NULL,
    created_at TIMESTAMP NOT NULL,
    modified_at TIMESTAMP NOT NULL
);

-- Secrets
CREATE TABLE secrets (
    id TEXT PRIMARY KEY,
    secret_type TEXT NOT NULL,
    encrypted_data BLOB NOT NULL,
    nonce BLOB NOT NULL,
    created_at TIMESTAMP NOT NULL,
    modified_at TIMESTAMP NOT NULL
);

-- Audit log
CREATE TABLE audit_log (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    timestamp TIMESTAMP NOT NULL,
    action TEXT NOT NULL,
    entity_type TEXT,
    entity_id TEXT,
    details TEXT  -- JSON
);
\`\`\`

## Build System

### Dependencies

Key dependencies and their purposes:

\`\`\`toml
[dependencies]
# Cryptography
aes-gcm = "0.10"          # AES-256-GCM encryption
argon2 = "0.5"            # Password hashing
rand = "0.8"              # Secure random generation
zeroize = "1.7"           # Memory clearing

# Storage
sqlcipher = "0.5"         # Encrypted SQLite
serde = "1.0"             # Serialization
uuid = "1.6"              # Unique identifiers

# UI (GUI only)
egui = "0.24"             # Immediate mode GUI
eframe = "0.24"           # egui framework

# CLI only
clap = "4.4"              # Command line parsing
\`\`\`

### Build Profiles

\`\`\`toml
# Development build
[profile.dev]
opt-level = 0
debug = true

# Release build
[profile.release]
opt-level = 3
lto = true
strip = true

# Security-focused build
[profile.secure]
inherits = "release"
panic = "abort"
overflow-checks = true
\`\`\`

## Testing Strategy

### Unit Tests

\`\`\`rust
#[cfg(test)]
mod tests {
    use super::*;

    #[test]
    fn test_encryption_roundtrip() {
        let engine = CryptoEngine::new();
        let plaintext = b"secret data";
        
        let encrypted = engine.encrypt(plaintext).unwrap();
        let decrypted = engine.decrypt(&encrypted).unwrap();
        
        assert_eq!(plaintext, &decrypted[..]);
    }
}
\`\`\`

### Integration Tests

\`\`\`rust
// tests/vault_operations.rs
#[test]
fn test_vault_creation_and_access() {
    let temp_dir = tempdir().unwrap();
    let vault_path = temp_dir.path().join("test.vault");
    
    // Create vault
    let vault = Vault::create(&vault_path, "test_password").unwrap();
    
    // Add entry
    let entry = PasswordEntry::new("github.com", "user@example.com");
    vault.add_password(entry).unwrap();
    
    // Verify
    let entries = vault.list_passwords().unwrap();
    assert_eq!(entries.len(), 1);
}
\`\`\`

## Performance Considerations

### Memory Management

- Use \`zeroize\` for sensitive data
- Avoid unnecessary allocations
- Use references where possible
- Clear clipboard after timeout

### Concurrency

- Vault operations are single-threaded
- GUI runs on main thread
- Background tasks use \`tokio\` runtime
- Database connections are pooled

### Optimization

- Lazy loading for large vaults
- Indexed database queries
- Cached decryption for session
- Efficient search algorithms

## Contributing Guidelines

### Code Style

Follow Rust standard style:
\`\`\`bash
# Format code
cargo fmt

# Run linter
cargo clippy -- -D warnings

# Run tests
cargo test
\`\`\`

### Pull Request Process

1. Fork the repository
2. Create feature branch
3. Write tests for new features
4. Ensure all tests pass
5. Update documentation
6. Submit pull request

### Security Considerations

When contributing:
- Never log sensitive data
- Always zeroize secrets
- Use constant-time comparisons
- Validate all inputs
- Follow OWASP guidelines

## API Design (Future)

The planned API server will provide:

\`\`\`rust
// RESTful endpoints
POST   /api/v1/auth/login
POST   /api/v1/auth/logout
GET    /api/v1/passwords
POST   /api/v1/passwords
PUT    /api/v1/passwords/:id
DELETE /api/v1/passwords/:id

// WebSocket for real-time updates
WS     /api/v1/sync
\`\`\`

## Platform-Specific Code

### Linux
\`\`\`rust
#[cfg(target_os = "linux")]
fn get_config_dir() -> PathBuf {
    // XDG Base Directory compliance
    xdg::BaseDirectories::new()
        .unwrap()
        .get_config_home()
        .join("pwgen")
}
\`\`\`

### macOS
\`\`\`rust
#[cfg(target_os = "macos")]
fn get_config_dir() -> PathBuf {
    dirs::config_dir()
        .unwrap()
        .join("dev.pwgenrust.pwgen")
}
\`\`\`

### Windows
\`\`\`rust
#[cfg(target_os = "windows")]
fn get_config_dir() -> PathBuf {
    dirs::config_dir()
        .unwrap()
        .join("PwGen")
}
\`\`\`

## Debugging

### Debug Builds

\`\`\`bash
# Build with debug symbols
cargo build

# Run with logging
RUST_LOG=debug cargo run

# Use debugger
rust-gdb target/debug/pwgen-cli
\`\`\`

### Profiling

\`\`\`bash
# CPU profiling
cargo build --release
perf record --call-graph=dwarf target/release/pwgen-cli
perf report

# Memory profiling
valgrind --tool=massif target/release/pwgen-cli
\`\`\`

## Resources

### Documentation
- [Rust Book](https://doc.rust-lang.org/book/)
- [Rust Security Guidelines](https://anssi-fr.github.io/rust-guide/)
- [OWASP Guidelines](https://owasp.org/www-project-application-security-verification-standard/)

### Dependencies Documentation
- [aes-gcm](https://docs.rs/aes-gcm/)
- [argon2](https://docs.rs/argon2/)
- [egui](https://docs.rs/egui/)
- [clap](https://docs.rs/clap/)

### Community
- [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)
- [Issue Tracker](https://github.com/KiefStudioMA/PWGen-Rust/issues)
- [Twitter Updates](https://x.com/HxHippy)`
  },

  // Tutorials - Automation
  'tutorials/automation': {
    title: 'Automation and Scripting',
    description: 'Learn how to automate PWGen-Rust-Rust operations for efficient password management',
    content: `# Automation and Scripting

Learn how to automate PWGen-Rust-Rust operations for efficient password management in scripts, CI/CD pipelines, and system administration.

## Introduction

PWGen-Rust-Rust CLI is designed with automation in mind, providing machine-readable output, consistent exit codes, and non-interactive modes perfect for scripting and automated workflows.

## Basic Automation Concepts

### Non-Interactive Mode

Enable non-interactive mode to prevent prompts that would hang automated scripts:

\`\`\`bash
# Using environment variable
export PWGEN_NON_INTERACTIVE=1

# Using command flag
pwgen-cli --non-interactive password list

# In configuration file
[cli]
non_interactive = true
\`\`\`

### Machine-Readable Output

Use structured output formats for parsing in scripts:

\`\`\`bash
# JSON output for parsing
pwgen-cli --format json password list

# CSV for spreadsheet integration
pwgen-cli --format csv password list

# Raw values for direct use
pwgen-cli password copy --site github.com --raw
\`\`\`

### Exit Code Handling

Always check exit codes in automation scripts:

\`\`\`bash
#!/bin/bash
set -e  # Exit on any error

if pwgen-cli vault status > /dev/null 2>&1; then
    echo "Vault is accessible"
else
    echo "Vault is locked or inaccessible"
    exit 1
fi
\`\`\`

## Environment Setup

### Secure Credential Storage

Never hardcode passwords in scripts. Use environment variables or secure credential stores:

\`\`\`bash
#!/bin/bash

# Method 1: Environment variables (for development)
export PWGEN_VAULT_PATH="/secure/vault.db"

# Method 2: Read from secure file
VAULT_PATH=$(cat ~/.config/pwgen/vault_path)

# Method 3: Use system keyring
MASTER_PASS=$(security find-generic-password -s "pwgen-master" -w)
\`\`\`

### Configuration for Automation

Create a dedicated configuration for automated environments:

\`\`\`toml
# ~/.config/pwgen/automation.toml
[general]
default_vault = "/var/lib/pwgen/automation.vault"
confirm_destructive = false
log_file = "/var/log/pwgen/automation.log"

[cli]
non_interactive = true
default_format = "json"
max_results = 10000

[output]
colors = false
unicode = false

[security]
audit_log = true
audit_log_file = "/var/log/pwgen/audit.log"

[backup]
backup_directory = "/var/backups/pwgen"
verify_after_create = true
\`\`\`

## Scripting Examples

### Daily Backup Script

Automated daily backup with rotation:

\`\`\`bash
#!/bin/bash
# daily-backup.sh

set -euo pipefail

# Configuration
BACKUP_DIR="/var/backups/pwgen"
RETENTION_DAYS=30
DATE=$(date +%Y%m%d)
BACKUP_FILE="$BACKUP_DIR/vault-backup-$DATE.pwgen"

# Create backup directory
mkdir -p "$BACKUP_DIR"

# Create backup
echo "Creating backup: $BACKUP_FILE"
if pwgen-cli backup create --output "$BACKUP_FILE" --compression 9; then
    echo "Backup created successfully"
    
    # Verify backup
    if pwgen-cli backup verify --file "$BACKUP_FILE"; then
        echo "Backup verified successfully"
    else
        echo "Backup verification failed!"
        exit 1
    fi
    
    # Clean old backups
    find "$BACKUP_DIR" -name "vault-backup-*.pwgen" -mtime +$RETENTION_DAYS -delete
    echo "Old backups cleaned"
    
else
    echo "Backup creation failed!"
    exit 1
fi
\`\`\`

### Password Strength Audit

Weekly security audit script:

\`\`\`bash
#!/bin/bash
# security-audit.sh

set -euo pipefail

# Configuration
REPORT_DIR="/var/reports/pwgen"
DATE=$(date +%Y%m%d)
REPORT_FILE="$REPORT_DIR/security-audit-$DATE.html"

mkdir -p "$REPORT_DIR"

echo "Starting security audit..."

# Check for weak passwords
echo "Checking for weak passwords..."
WEAK_COUNT=$(pwgen-cli analyze weak --format json | jq '. | length')
echo "Found $WEAK_COUNT weak passwords"

# Check for duplicates
echo "Checking for duplicate passwords..."
DUPLICATE_COUNT=$(pwgen-cli analyze duplicates --format json | jq '. | length')
echo "Found $DUPLICATE_COUNT duplicate passwords"

# Check for old passwords
echo "Checking for old passwords (>90 days)..."
OLD_COUNT=$(pwgen-cli analyze old --max-age 90 --format json | jq '. | length')
echo "Found $OLD_COUNT old passwords"

# Generate comprehensive report
pwgen-cli analyze strength --export "$REPORT_FILE"

# Send alert if issues found
if [ $((WEAK_COUNT + DUPLICATE_COUNT + OLD_COUNT)) -gt 0 ]; then
    echo "Security issues found! Report: $REPORT_FILE"
    # Send notification (customize for your environment)
    # mail -s "PWGen-Rust Security Audit Alert" admin@company.com < "$REPORT_FILE"
fi

echo "Security audit completed"
\`\`\`

### Bulk Password Generation

Generate passwords for multiple new accounts:

\`\`\`bash
#!/bin/bash
# bulk-password-generation.sh

set -euo pipefail

# Read services from file or command line
SERVICES_FILE="\${1:-services.txt}"

if [[ ! -f "$SERVICES_FILE" ]]; then
    echo "Usage: $0 <services-file>"
    echo "Services file format: service_name username tags"
    exit 1
fi

# Process each service
while IFS=' ' read -r service username tags; do
    # Skip empty lines and comments
    [[ -z "$service" || "$service" =~ ^#.*$ ]] && continue
    
    echo "Generating password for $service ($username)..."
    
    # Generate strong password
    password=$(pwgen-cli generate --length 20 --symbols --no-ambiguous)
    
    # Add to vault
    if pwgen-cli password add $
        --site "$service" $
        --username "$username" $
        --password "$password" $
        --tags "$tags" $
        --notes "Auto-generated on $(date)"; then
        echo "✓ Added entry for $service"
    else
        echo "✗ Failed to add entry for $service"
    fi
    
done < "$SERVICES_FILE"

echo "Bulk password generation completed"
\`\`\`

### Database Maintenance

Regular vault maintenance and optimization:

\`\`\`bash
#!/bin/bash
# maintenance.sh

set -euo pipefail

echo "Starting PWGen-Rust vault maintenance..."

# Check vault status
if ! pwgen-cli vault status > /dev/null 2>&1; then
    echo "Vault is not accessible"
    exit 1
fi

# Get vault statistics
TOTAL_ENTRIES=$(pwgen-cli password list --format json | jq '. | length')
echo "Total entries: $TOTAL_ENTRIES"

# Find and report issues
echo "Analyzing vault..."

# Entries without tags
UNTAGGED=$(pwgen-cli password list --format json | jq '[.[] | select(.tags | length == 0)] | length')
echo "Untagged entries: $UNTAGGED"

# Entries with empty notes
EMPTY_NOTES=$(pwgen-cli password list --format json | jq '[.[] | select(.notes == "" or .notes == null)] | length')
echo "Entries with empty notes: $EMPTY_NOTES"

# Recent activity (last 7 days)
RECENT=$(pwgen-cli password list --newer-than "7 days" --format json | jq '. | length')
echo "Recent entries (7 days): $RECENT"

# Create maintenance backup
BACKUP_FILE="/var/backups/pwgen/maintenance-$(date +%Y%m%d-%H%M).pwgen"
pwgen-cli backup create --output "$BACKUP_FILE"
echo "Maintenance backup created: $BACKUP_FILE"

echo "Maintenance completed"
\`\`\`

## CI/CD Integration

### GitHub Actions

Integrate PWGen-Rust into GitHub Actions workflows:

\`\`\`yaml
# .github/workflows/deploy.yml
name: Deploy Application

on:
  push:
    branches: [main]

jobs:
  deploy:
    runs-on: ubuntu-latest
    
    steps:
      - uses: actions/checkout@v3
      
      - name: Install PWGen-Rust-Rust
        run: |
          curl -sSL https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/install.sh | bash
          echo "$HOME/.local/bin" >> $GITHUB_PATH
      
      - name: Setup PWGen-Rust vault
        env:
          VAULT_DATA: \${{ secrets.PWGEN_VAULT_BASE64 }}
        run: |
          echo "$VAULT_DATA" | base64 -d > vault.db
          export PWGEN_VAULT_PATH="$PWD/vault.db"
      
      - name: Get deployment credentials
        id: credentials
        run: |
          API_KEY=$(pwgen-cli password copy --site "production-api" --raw)
          echo "::add-mask::$API_KEY"
          echo "api_key=$API_KEY" >> $GITHUB_OUTPUT
          
          DB_PASSWORD=$(pwgen-cli password copy --site "production-db" --raw)
          echo "::add-mask::$DB_PASSWORD"
          echo "db_password=$DB_PASSWORD" >> $GITHUB_OUTPUT
      
      - name: Deploy application
        env:
          API_KEY: \${{ steps.credentials.outputs.api_key }}
          DB_PASSWORD: \${{ steps.credentials.outputs.db_password }}
        run: |
          # Your deployment script here
          ./deploy.sh
\`\`\`

### GitLab CI

PWGen-Rust integration in GitLab CI pipelines:

\`\`\`yaml
# .gitlab-ci.yml
stages:
  - deploy

deploy_production:
  stage: deploy
  image: alpine:latest
  
  before_script:
    - apk add --no-cache curl bash
    - curl -sSL https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/install.sh | bash
    - export PATH="$HOME/.local/bin:$PATH"
    
  script:
    # Setup vault from GitLab secret
    - echo "$VAULT_DATA" | base64 -d > vault.db
    - export PWGEN_VAULT_PATH="$PWD/vault.db"
    
    # Get credentials
    - API_KEY=$(pwgen-cli password copy --site "gitlab-deploy-api" --raw)
    - DB_PASS=$(pwgen-cli password copy --site "production-database" --raw)
    
    # Deploy with credentials
    - ./deploy.sh "$API_KEY" "$DB_PASS"
    
  only:
    - main
  
  variables:
    PWGEN_NON_INTERACTIVE: "1"
    PWGEN_FORMAT: "json"
\`\`\`

### Jenkins Pipeline

Jenkins pipeline with PWGen-Rust integration:

\`\`\`groovy
pipeline {
    agent any
    
    environment {
        PWGEN_NON_INTERACTIVE = '1'
        PWGEN_FORMAT = 'json'
    }
    
    stages {
        stage('Setup') {
            steps {
                script {
                    // Install PWGen-Rust if not present
                    sh '''
                        if ! command -v pwgen-cli &> /dev/null; then
                            curl -sSL https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/install.sh | bash
                            export PATH="$HOME/.local/bin:$PATH"
                        fi
                    '''
                }
            }
        }
        
        stage('Get Credentials') {
            steps {
                withCredentials([file(credentialsId: 'pwgen-vault', variable: 'VAULT_FILE')]) {
                    script {
                        env.PWGEN_VAULT_PATH = env.VAULT_FILE
                        
                        // Get deployment credentials
                        env.API_KEY = sh(
                            script: 'pwgen-cli password copy --site "jenkins-api" --raw',
                            returnStdout: true
                        ).trim()
                        
                        env.DB_PASSWORD = sh(
                            script: 'pwgen-cli password copy --site "jenkins-db" --raw',
                            returnStdout: true
                        ).trim()
                    }
                }
            }
        }
        
        stage('Deploy') {
            steps {
                sh './deploy.sh "$API_KEY" "$DB_PASSWORD"'
            }
        }
    }
    
    post {
        always {
            // Clean up sensitive environment variables
            script {
                env.API_KEY = null
                env.DB_PASSWORD = null
            }
        }
    }
}
\`\`\`

## System Administration

### User Onboarding Automation

Automate new user account creation:

\`\`\`bash
#!/bin/bash
# onboard-user.sh

set -euo pipefail

# Parse arguments
if [[ $# -lt 3 ]]; then
    echo "Usage: $0 <username> <email> <department>"
    exit 1
fi

USERNAME="$1"
EMAIL="$2"
DEPARTMENT="$3"

echo "Onboarding user: $USERNAME ($EMAIL) - $DEPARTMENT"

# Define services based on department
case "$DEPARTMENT" in
    "engineering")
        SERVICES=("github" "jira" "confluence" "aws" "docker-registry")
        ;;
    "marketing")
        SERVICES=("salesforce" "hubspot" "canva" "analytics")
        ;;
    "hr")
        SERVICES=("workday" "slack" "zoom" "benefits-portal")
        ;;
    *)
        SERVICES=("email" "slack" "zoom")
        ;;
esac

# Generate accounts for each service
for service in "\${SERVICES[@]}"; do
    echo "Creating account for $service..."
    
    # Generate secure password
    password=$(pwgen-cli generate --length 16 --symbols --no-ambiguous)
    
    # Add to vault with user-specific tags
    pwgen-cli password add $
        --site "$service.company.com" $
        --username "$USERNAME" $
        --password "$password" $
        --tags "onboarding,$DEPARTMENT,$USERNAME" $
        --notes "Created for $EMAIL on $(date)" $
        --url "https://$service.company.com/login"
    
    echo "✓ Created $service account"
done

# Generate onboarding report
REPORT_FILE="/tmp/onboarding-$USERNAME-$(date +%Y%m%d).csv"
pwgen-cli password list --tag "$USERNAME" --format csv > "$REPORT_FILE"

echo "Onboarding completed for $USERNAME"
echo "Credentials report: $REPORT_FILE"

# Send secure report (customize for your email system)
# gpg --encrypt --recipient "$EMAIL" "$REPORT_FILE"
# mail -s "Your new account credentials" "$EMAIL" < "$REPORT_FILE.gpg"
\`\`\`

### Password Rotation

Automated password rotation system:

\`\`\`bash
#!/bin/bash
# rotate-passwords.sh

set -euo pipefail

# Configuration
ROTATION_INTERVAL_DAYS=90
NOTIFICATION_EMAIL="security@company.com"

echo "Starting password rotation check..."

# Get passwords older than rotation interval
OLD_PASSWORDS=$(pwgen-cli password list $
    --older-than "\${ROTATION_INTERVAL_DAYS} days" $
    --format json $
    --tag "auto-rotate")

if [[ $(echo "$OLD_PASSWORDS" | jq '. | length') -eq 0 ]]; then
    echo "No passwords need rotation"
    exit 0
fi

echo "Found $(echo "$OLD_PASSWORDS" | jq '. | length') passwords needing rotation"

# Process each password
echo "$OLD_PASSWORDS" | jq -r '.[] | @base64' | while read -r encoded_entry; do
    entry=$(echo "$encoded_entry" | base64 -d)
    site=$(echo "$entry" | jq -r '.site')
    username=$(echo "$entry" | jq -r '.username')
    
    echo "Rotating password for $site ($username)..."
    
    # Generate new password
    new_password=$(pwgen-cli generate --length 20 --symbols --no-ambiguous)
    
    # Update in vault
    pwgen-cli password update $
        --site "$site" $
        --password "$new_password" $
        --add-tags "rotated-$(date +%Y%m%d)" $
        --notes "Automatically rotated on $(date)"
    
    echo "✓ Rotated password for $site"
    
    # Log rotation for audit
    echo "$(date): Rotated password for $site ($username)" >> /var/log/pwgen/rotations.log
done

echo "Password rotation completed"

# Send notification
if command -v mail > /dev/null; then
    echo "Password rotation completed on $(date)" | $
        mail -s "PWGen-Rust Password Rotation Report" "$NOTIFICATION_EMAIL"
fi
\`\`\`

### Health Monitoring

Monitor PWGen-Rust vault health and performance:

\`\`\`bash
#!/bin/bash
# health-check.sh

set -euo pipefail

# Configuration
ALERT_THRESHOLD_WEAK=10
ALERT_THRESHOLD_DUPLICATES=5
ALERT_THRESHOLD_OLD=50

echo "Starting PWGen-Rust health check..."

# Check vault accessibility
if ! pwgen-cli vault status > /dev/null 2>&1; then
    echo "CRITICAL: Vault is not accessible"
    exit 2
fi

# Get metrics
TOTAL_ENTRIES=$(pwgen-cli password list --format json | jq '. | length')
WEAK_PASSWORDS=$(pwgen-cli analyze weak --format json | jq '. | length')
DUPLICATE_PASSWORDS=$(pwgen-cli analyze duplicates --format json | jq '. | length')
OLD_PASSWORDS=$(pwgen-cli analyze old --max-age 90 --format json | jq '. | length')

# Performance metrics
VAULT_SIZE=$(du -h "$PWGEN_VAULT_PATH" | cut -f1)
LAST_BACKUP=$(find /var/backups/pwgen -name "*.pwgen" -printf "%T@ %p\\n" | sort -n | tail -1 | cut -d' ' -f2-)

echo "=== PWGen-Rust Health Report ==="
echo "Total entries: $TOTAL_ENTRIES"
echo "Vault size: $VAULT_SIZE"
echo "Weak passwords: $WEAK_PASSWORDS"
echo "Duplicate passwords: $DUPLICATE_PASSWORDS"
echo "Old passwords (>90 days): $OLD_PASSWORDS"
echo "Last backup: $LAST_BACKUP"

# Health score calculation
HEALTH_SCORE=100

if [[ $WEAK_PASSWORDS -gt $ALERT_THRESHOLD_WEAK ]]; then
    HEALTH_SCORE=$((HEALTH_SCORE - 30))
    echo "WARNING: Too many weak passwords"
fi

if [[ $DUPLICATE_PASSWORDS -gt $ALERT_THRESHOLD_DUPLICATES ]]; then
    HEALTH_SCORE=$((HEALTH_SCORE - 20))
    echo "WARNING: Too many duplicate passwords"
fi

if [[ $OLD_PASSWORDS -gt $ALERT_THRESHOLD_OLD ]]; then
    HEALTH_SCORE=$((HEALTH_SCORE - 25))
    echo "WARNING: Too many old passwords"
fi

# Check backup age
if [[ -z "$LAST_BACKUP" ]] || [[ $(find "$LAST_BACKUP" -mtime +7) ]]; then
    HEALTH_SCORE=$((HEALTH_SCORE - 25))
    echo "WARNING: Backup is more than 7 days old"
fi

echo "Health score: $HEALTH_SCORE/100"

# Exit with appropriate code
if [[ $HEALTH_SCORE -lt 70 ]]; then
    echo "CRITICAL: Health score below threshold"
    exit 2
elif [[ $HEALTH_SCORE -lt 85 ]]; then
    echo "WARNING: Health score could be better"
    exit 1
else
    echo "OK: Vault health is good"
    exit 0
fi
\`\`\`

## Best Practices

### Security Considerations

1. **Never Log Sensitive Data**:
   \`\`\`bash
   # Good: Use exit codes and generic messages
   if pwgen-cli password copy --site "$SITE" > /dev/null 2>&1; then
       echo "Password copied successfully"
   else
       echo "Failed to copy password"
   fi
   
   # Bad: Don't log actual passwords
   # password=$(pwgen-cli password copy --site "$SITE" --raw)
   # echo "Password is: $password"  # NEVER DO THIS
   \`\`\`

2. **Secure File Permissions**:
   \`\`\`bash
   # Set restrictive permissions on scripts
   chmod 750 /usr/local/bin/pwgen-automation.sh
   
   # Protect configuration files
   chmod 600 /etc/pwgen/automation.toml
   
   # Secure log files
   chmod 640 /var/log/pwgen/automation.log
   \`\`\`

3. **Error Handling**:
   \`\`\`bash
   # Always use error handling
   set -euo pipefail
   
   # Trap errors for cleanup
   trap 'echo "Error on line $LINENO"' ERR
   
   # Validate inputs
   if [[ -z "\${SITE:-}" ]]; then
       echo "ERROR: SITE variable not set"
       exit 1
   fi
   \`\`\`

### Performance Optimization

1. **Limit Result Sets**:
   \`\`\`bash
   # Use limits for large vaults
   pwgen-cli password list --limit 100
   
   # Filter results appropriately
   pwgen-cli password list --tag "production" --newer-than "30 days"
   \`\`\`

2. **Batch Operations**:
   \`\`\`bash
   # Process in batches for large operations
   pwgen-cli password list --format json | $
       jq -r '.[0:100] | .[] | .site' | $
       while read -r site; do
           # Process each site
           echo "Processing $site"
       done
   \`\`\`

3. **Caching**:
   \`\`\`bash
   # Cache frequently accessed data
   CACHE_FILE="/tmp/pwgen-cache-$(date +%Y%m%d)"
   
   if [[ ! -f "$CACHE_FILE" || $(find "$CACHE_FILE" -mmin +60) ]]; then
       pwgen-cli password list --format json > "$CACHE_FILE"
   fi
   
   # Use cached data
   jq '.[] | select(.tags[] == "production")' < "$CACHE_FILE"
   \`\`\`

### Error Recovery

1. **Backup Before Changes**:
   \`\`\`bash
   # Always backup before bulk operations
   BACKUP_FILE="/tmp/pre-operation-backup-$(date +%Y%m%d-%H%M%S).pwgen"
   pwgen-cli backup create --output "$BACKUP_FILE"
   
   # Store backup location for recovery
   echo "$BACKUP_FILE" > /tmp/last-backup-location
   \`\`\`

2. **Rollback Procedures**:
   \`\`\`bash
   # Implement rollback function
   rollback() {
       local backup_file="$1"
       echo "Rolling back to $backup_file"
       pwgen-cli restore --file "$backup_file" --force
   }
   
   # Use in error handling
   trap 'rollback "$(cat /tmp/last-backup-location)"' ERR
   \`\`\`

## Integration Examples

### Docker Container

Run PWGen-Rust automation in Docker:

\`\`\`dockerfile
FROM alpine:latest

RUN apk add --no-cache bash curl jq

# Install PWGen-Rust-Rust
RUN curl -sSL https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/install.sh | bash

COPY automation-scripts/ /usr/local/bin/
COPY config/ /etc/pwgen/

VOLUME ["/var/lib/pwgen", "/var/log/pwgen"]

ENTRYPOINT ["/usr/local/bin/pwgen-automation.sh"]
\`\`\`

### Kubernetes CronJob

Schedule PWGen-Rust automation in Kubernetes:

\`\`\`yaml
apiVersion: batch/v1
kind: CronJob
metadata:
  name: pwgen-backup
spec:
  schedule: "0 2 * * *"  # Daily at 2 AM
  jobTemplate:
    spec:
      template:
        spec:
          containers:
          - name: pwgen-backup
            image: pwgen-automation:latest
            command: ["/usr/local/bin/backup-automation.sh"]
            env:
            - name: PWGEN_VAULT_PATH
              value: "/var/lib/pwgen/vault.db"
            - name: PWGEN_NON_INTERACTIVE
              value: "1"
            volumeMounts:
            - name: vault-storage
              mountPath: /var/lib/pwgen
            - name: backup-storage
              mountPath: /var/backups/pwgen
          volumes:
          - name: vault-storage
            persistentVolumeClaim:
              claimName: pwgen-vault
          - name: backup-storage
            persistentVolumeClaim:
              claimName: pwgen-backups
          restartPolicy: OnFailure
\`\`\`

### Ansible Playbook

Automate PWGen-Rust deployment and configuration:

\`\`\`yaml
---
- name: Deploy PWGen-Rust automation
  hosts: pwgen_servers
  become: yes
  
  tasks:
    - name: Install PWGen-Rust-Rust
      shell: |
        curl -sSL https://github.com/KiefStudioMA/PWGen-Rust/releases/latest/download/install.sh | bash
      args:
        creates: /usr/local/bin/pwgen-cli
    
    - name: Create pwgen user
      user:
        name: pwgen
        system: yes
        shell: /bin/bash
        home: /var/lib/pwgen
    
    - name: Create directories
      file:
        path: "{{ item }}"
        state: directory
        owner: pwgen
        group: pwgen
        mode: '0750'
      loop:
        - /var/lib/pwgen
        - /var/log/pwgen
        - /var/backups/pwgen
    
    - name: Copy automation scripts
      copy:
        src: "{{ item }}"
        dest: /usr/local/bin/
        owner: root
        group: root
        mode: '0755'
      loop:
        - scripts/daily-backup.sh
        - scripts/security-audit.sh
        - scripts/health-check.sh
    
    - name: Setup cron jobs
      cron:
        name: "{{ item.name }}"
        job: "{{ item.job }}"
        minute: "{{ item.minute }}"
        hour: "{{ item.hour }}"
        user: pwgen
      loop:
        - name: "Daily backup"
          job: "/usr/local/bin/daily-backup.sh"
          minute: "0"
          hour: "2"
        - name: "Weekly security audit"
          job: "/usr/local/bin/security-audit.sh"
          minute: "0"
          hour: "3"
\`\`\`

## See Also

- [CLI Commands](../cli/commands) - Complete command reference
- [CLI Configuration](../cli/configuration) - Configuration options
- [Security Architecture](../security/architecture) - Security considerations
- [Team Usage](team-usage) - Collaborative workflows`
  },

  // Tutorials - Migration
  'tutorials/migration': {
    title: 'Migration from Other Password Managers',
    description: 'Comprehensive guide to migrating from other password managers to PWGen-Rust-Rust',
    content: `# Migration from Other Password Managers

Comprehensive guide to migrating your passwords and data from other password managers to PWGen-Rust-Rust.

## Overview

PWGen-Rust-Rust supports importing from all major password managers and browsers, making migration straightforward and secure. This guide covers step-by-step migration processes, best practices, and troubleshooting.

## Supported Sources

### Password Managers
- **LastPass** - Direct CSV export import
- **Bitwarden** - Native export format support
- **1Password** - CSV and JSON export support
- **KeePass/KeePassXC** - KDBX and CSV support
- **Dashlane** - CSV export import
- **RoboForm** - CSV export support
- **Enpass** - CSV export support

### Browsers
- **Chrome/Chromium** - Direct password import
- **Firefox** - Profile-based import
- **Safari** - Keychain integration
- **Microsoft Edge** - Direct import
- **Opera** - Browser data import
- **Brave** - Chromium-based import

### Generic Sources
- **CSV files** - Custom format support
- **JSON files** - Structured data import
- **Text files** - Line-based password lists

## Before You Start

### Pre-Migration Checklist

1. **Backup Current Data**:
   \`\`\`bash
   # Create a backup of your current PWGen-Rust vault (if you have one)
   pwgen-cli backup create --output pre-migration-backup.pwgen
   \`\`\`

2. **Export from Source**:
   - Follow your current password manager's export process
   - Choose CSV format when possible for best compatibility
   - Note any special characters or formatting in your data

3. **Security Considerations**:
   - Export files contain unencrypted passwords
   - Work on a secure, private computer
   - Delete export files after successful import
   - Consider using an encrypted drive or folder

4. **Verify Source Data**:
   - Check export file for completeness
   - Verify special characters display correctly
   - Note any entries that might need manual cleanup

## Migration Guides by Source

### LastPass Migration

LastPass is one of the most common migration sources. Here's the complete process:

#### Step 1: Export from LastPass

1. Log into LastPass web vault
2. Go to **Account Options** → **Advanced** → **Export**
3. Choose **LastPass CSV File**
4. Save as \`lastpass_export.csv\`

#### Step 2: Import to PWGen-Rust-Rust

\`\`\`bash
# Basic import
pwgen-cli import lastpass --file lastpass_export.csv

# Preview import first (recommended)
pwgen-cli import lastpass --file lastpass_export.csv --dry-run

# Import with backup
pwgen-cli import lastpass --file lastpass_export.csv --backup-before-import

# Merge with existing entries
pwgen-cli import lastpass --file lastpass_export.csv --merge
\`\`\`

#### Step 3: Verify Import

\`\`\`bash
# Check total entries
pwgen-cli password list --format table

# Look for any issues
pwgen-cli password list --format json | jq '.[] | select(.site == "" or .username == "")'

# Check specific entries
pwgen-cli password search "example.com"
\`\`\`

### Bitwarden Migration

Bitwarden provides excellent export options:

#### Step 1: Export from Bitwarden

**Web Vault Method:**
1. Login to Bitwarden web vault
2. Go to **Tools** → **Export Vault**
3. Choose **JSON** format for best results
4. Save as \`bitwarden_export.json\`

**CLI Method:**
\`\`\`bash
# Login to Bitwarden CLI
bw login

# Export vault
bw export --output bitwarden_export.json --format json
\`\`\`

#### Step 2: Import to PWGen-Rust-Rust

\`\`\`bash
# Import JSON export
pwgen-cli import bitwarden --file bitwarden_export.json

# Or CSV if you exported that format
pwgen-cli import csv --file bitwarden_export.csv --format bitwarden
\`\`\`

### 1Password Migration

1Password has specific export requirements:

#### Step 1: Export from 1Password

**Desktop App Method:**
1. Open 1Password desktop app
2. Select all items in a vault
3. **File** → **Export** → **All Items**
4. Choose **CSV** format
5. Save as \`1password_export.csv\`

#### Step 2: Import to PWGen-Rust-Rust

\`\`\`bash
# Import 1Password CSV
pwgen-cli import 1password --file 1password_export.csv

# Handle multiple vaults
pwgen-cli import 1password --file personal_vault.csv --tag "personal"
pwgen-cli import 1password --file work_vault.csv --tag "work"
\`\`\`

### KeePass Migration

KeePass requires special handling due to its database format:

#### Step 1: Export from KeePass

**KeePass Classic:**
1. **File** → **Export** → **Generic CSV**
2. Choose all fields
3. Save as \`keepass_export.csv\`

**KeePassXC:**
1. **Database** → **Export** → **CSV**
2. Include all fields
3. Save as \`keepassx_export.csv\`

#### Step 2: Import to PWGen-Rust-Rust

\`\`\`bash
# Import KeePass CSV
pwgen-cli import keepass --file keepass_export.csv

# Handle custom fields
pwgen-cli import csv --file keepass_export.csv --map-fields "Title:site,Username:username,Password:password,URL:url,Notes:notes"
\`\`\`

### Browser Migration

Browsers often have the simplest migration process:

#### Chrome Migration

\`\`\`bash
# Direct import from Chrome
pwgen-cli import chrome

# Specific profile
pwgen-cli import chrome --profile "Profile 1"

# Preview first
pwgen-cli import chrome --dry-run

# Custom Chrome installation
pwgen-cli import chrome --chrome-path "/opt/google/chrome/chrome"
\`\`\`

#### Firefox Migration

\`\`\`bash
# Import from default profile
pwgen-cli import firefox

# Specific profile
pwgen-cli import firefox --profile "work"

# Custom Firefox directory
pwgen-cli import firefox --firefox-dir "~/snap/firefox/common/.mozilla"
\`\`\`

#### Safari Migration

\`\`\`bash
# macOS only - imports from Keychain
pwgen-cli import safari

# Requires permission to access Keychain
# You may be prompted for admin password
\`\`\`

## Advanced Migration Scenarios

### Large Dataset Migration

For password managers with thousands of entries:

\`\`\`bash
# Enable progress reporting
pwgen-cli import lastpass --file large_export.csv --show-progress

# Process in batches
split -l 1000 large_export.csv batch_
for batch in batch_*; do
    pwgen-cli import csv --file "$batch" --merge
    echo "Processed $batch"
done

# Monitor system resources
pwgen-cli import lastpass --file large_export.csv --batch-size 500
\`\`\`

### Multiple Sources Migration

Combining data from multiple password managers:

\`\`\`bash
# Import each source with unique tags
pwgen-cli import lastpass --file lastpass.csv --tag "from-lastpass"
pwgen-cli import bitwarden --file bitwarden.json --tag "from-bitwarden"
pwgen-cli import chrome --tag "from-chrome"

# Analyze for duplicates after all imports
pwgen-cli analyze duplicates --format table

# Clean up duplicates interactively
pwgen-cli password list --tag "from-lastpass" --format json | $
  jq -r '.[] | select(.site == "example.com") | .id' | $
  xargs -I {} pwgen-cli password delete --id {} --confirm
\`\`\`

### Corporate/Team Migration

For organizations migrating team vaults:

\`\`\`bash
# Create separate vaults for different teams
pwgen-cli vault create --path /shared/vaults/engineering.vault
pwgen-cli vault create --path /shared/vaults/marketing.vault

# Import team-specific data
PWGEN_VAULT_PATH="/shared/vaults/engineering.vault" $
  pwgen-cli import bitwarden --file engineering_vault.json

PWGEN_VAULT_PATH="/shared/vaults/marketing.vault" $
  pwgen-cli import bitwarden --file marketing_vault.json

# Set appropriate permissions
chmod 660 /shared/vaults/*.vault
chgrp engineering /shared/vaults/engineering.vault
chgrp marketing /shared/vaults/marketing.vault
\`\`\`

## Custom CSV Import

When your source isn't directly supported, use the flexible CSV import:

### CSV Format Mapping

\`\`\`bash
# Basic CSV with custom field mapping
pwgen-cli import csv --file custom.csv $
  --map-fields "Website:site,Login:username,Pass:password,Comments:notes"

# Handle special delimiters
pwgen-cli import csv --file custom.csv --delimiter "|"

# Skip header row
pwgen-cli import csv --file custom.csv --skip-header

# Handle quoted fields
pwgen-cli import csv --file custom.csv --quote-char '"'
\`\`\`

### CSV Preprocessing

Sometimes you need to clean up CSV data first:

\`\`\`bash
# Remove empty lines
sed '/^$/d' original.csv > cleaned.csv

# Fix encoding issues
iconv -f iso-8859-1 -t utf-8 original.csv > utf8.csv

# Add missing headers
echo "site,username,password,notes" > header.csv
cat data.csv >> header.csv

# Convert TSV to CSV
tr '\\t' ',' < data.tsv > data.csv
\`\`\`

## Post-Migration Tasks

### Verification and Cleanup

After importing, perform these verification steps:

\`\`\`bash
# Get import statistics
echo "Total entries: $(pwgen-cli password list --format json | jq '. | length')"
echo "Entries without passwords: $(pwgen-cli password list --format json | jq '[.[] | select(.password == "")] | length')"
echo "Entries without sites: $(pwgen-cli password list --format json | jq '[.[] | select(.site == "")] | length')"

# Find potential duplicates
pwgen-cli analyze duplicates --format table

# Check for weak passwords from import
pwgen-cli analyze weak --format table

# Look for entries needing attention
pwgen-cli password list --format json | $
  jq '.[] | select(.site == "" or .username == "" or .password == "")' | $
  jq -r '"\\(.id): \\(.site) - \\(.username)"'
\`\`\`

### Data Cleanup

Clean up imported data:

\`\`\`bash
# Remove test entries
pwgen-cli password delete --site "test" --confirm
pwgen-cli password search "example" --format json | $
  jq -r '.[].id' | $
  xargs -I {} pwgen-cli password delete --id {} --confirm

# Standardize site names
pwgen-cli password update --site "gmail.com" --site "Google Mail"
pwgen-cli password update --site "github.com" --site "GitHub"

# Add missing tags
pwgen-cli password list --site "*bank*" --format json | $
  jq -r '.[].id' | $
  xargs -I {} pwgen-cli password update --id {} --add-tags "banking,financial"

# Update URLs for entries missing them
pwgen-cli password list --format json | $
  jq -r '.[] | select(.url == "") | "\\(.id):\\(.site)"' | $
  while IFS=: read -r id site; do
    pwgen-cli password update --id "$id" --url "https://$site"
  done
\`\`\`

### Security Review

Perform security analysis on imported data:

\`\`\`bash
# Comprehensive security audit
echo "=== Security Review ==="
echo "Weak passwords: $(pwgen-cli analyze weak --format json | jq '. | length')"
echo "Duplicate passwords: $(pwgen-cli analyze duplicates --format json | jq '. | length')"
echo "Old passwords: $(pwgen-cli analyze old --max-age 90 --format json | jq '. | length')"

# Check for common weak passwords
pwgen-cli password list --format json | $
  jq -r '.[] | select(.password | test("password|123456|qwerty"; "i")) | "\\(.site): \\(.password)"'

# Generate security report
pwgen-cli analyze strength --export "post-migration-security-report.html"
\`\`\`

## Troubleshooting Common Issues

### Import Failures

**Problem: Import fails with "Invalid format" error**

\`\`\`bash
# Check file encoding
file -I your_export.csv

# Convert if needed
iconv -f iso-8859-1 -t utf-8 your_export.csv > fixed_export.csv

# Check for invalid characters
grep -P '[^\\x00-\\x7F]' your_export.csv
\`\`\`

**Problem: Some entries are missing after import**

\`\`\`bash
# Compare counts
echo "Lines in CSV: $(wc -l < export.csv)"
echo "Entries imported: $(pwgen-cli password list --format json | jq '. | length')"

# Check for import errors in logs
pwgen-cli import csv --file export.csv --verbose 2>&1 | grep -i error

# Look for problematic entries
awk -F, 'NF != 4 { print NR ": " $0 }' export.csv
\`\`\`

### Encoding Issues

**Problem: Special characters are corrupted**

\`\`\`bash
# Detect encoding
chardet your_export.csv

# Convert to UTF-8
iconv -f windows-1252 -t utf-8 your_export.csv > utf8_export.csv

# Handle different encodings
for encoding in iso-8859-1 windows-1252 utf-16; do
    if iconv -f "$encoding" -t utf-8 your_export.csv > test.csv 2>/dev/null; then
        echo "Successfully converted from $encoding"
        mv test.csv fixed_export.csv
        break
    fi
done
\`\`\`

### Duplicate Handling

**Problem: Many duplicate entries after import**

\`\`\`bash
# Find all duplicates
pwgen-cli analyze duplicates --format json > duplicates.json

# Remove duplicates by keeping newest
cat duplicates.json | jq -r '.[] | sort_by(.created) | .[0].id' | $
  xargs -I {} pwgen-cli password delete --id {} --confirm

# Or merge duplicates manually
pwgen-cli password list --site "example.com" --format table
# Review and decide which to keep
\`\`\`

### Performance Issues

**Problem: Import is very slow**

\`\`\`bash
# Import in smaller batches
split -l 1000 large_export.csv batch_
for batch in batch_*; do
    echo "Processing $batch..."
    pwgen-cli import csv --file "$batch" --merge --batch-size 100
done

# Use optimized settings
PWGEN_NON_INTERACTIVE=1 PWGEN_NO_PROGRESS=1 $
  pwgen-cli import csv --file large_export.csv
\`\`\`

## Migration Scripts

### Complete Migration Script

Here's a comprehensive migration script:

\`\`\`bash
#!/bin/bash
# migrate-to-pwgen.sh

set -euo pipefail

SOURCE_TYPE="$1"
SOURCE_FILE="$2"
VAULT_PATH="\${3:-$HOME/.config/pwgen/vault.db}"

echo "Starting migration from $SOURCE_TYPE to PWGen-Rust-Rust"

# Create backup if vault exists
if [[ -f "$VAULT_PATH" ]]; then
    BACKUP_FILE="pre-migration-backup-$(date +%Y%m%d-%H%M%S).pwgen"
    echo "Creating backup: $BACKUP_FILE"
    PWGEN_VAULT_PATH="$VAULT_PATH" pwgen-cli backup create --output "$BACKUP_FILE"
fi

# Verify source file
if [[ ! -f "$SOURCE_FILE" ]]; then
    echo "Error: Source file $SOURCE_FILE not found"
    exit 1
fi

echo "Source file: $SOURCE_FILE ($(wc -l < "$SOURCE_FILE") lines)"

# Set vault path
export PWGEN_VAULT_PATH="$VAULT_PATH"

# Create vault if it doesn't exist
if [[ ! -f "$VAULT_PATH" ]]; then
    echo "Creating new vault: $VAULT_PATH"
    pwgen-cli vault create --path "$VAULT_PATH"
fi

# Perform import based on source type
case "$SOURCE_TYPE" in
    "lastpass")
        pwgen-cli import lastpass --file "$SOURCE_FILE" --backup-before-import
        ;;
    "bitwarden")
        pwgen-cli import bitwarden --file "$SOURCE_FILE" --backup-before-import
        ;;
    "1password")
        pwgen-cli import 1password --file "$SOURCE_FILE" --backup-before-import
        ;;
    "keepass")
        pwgen-cli import keepass --file "$SOURCE_FILE" --backup-before-import
        ;;
    "chrome")
        pwgen-cli import chrome --backup-before-import
        ;;
    "firefox")
        pwgen-cli import firefox --backup-before-import
        ;;
    "csv")
        pwgen-cli import csv --file "$SOURCE_FILE" --backup-before-import
        ;;
    *)
        echo "Error: Unsupported source type: $SOURCE_TYPE"
        echo "Supported types: lastpass, bitwarden, 1password, keepass, chrome, firefox, csv"
        exit 1
        ;;
esac

# Post-import analysis
echo "Migration completed. Running analysis..."

TOTAL=$(pwgen-cli password list --format json | jq '. | length')
WEAK=$(pwgen-cli analyze weak --format json | jq '. | length')
DUPLICATES=$(pwgen-cli analyze duplicates --format json | jq '. | length')

echo "=== Migration Summary ==="
echo "Total entries imported: $TOTAL"
echo "Weak passwords found: $WEAK"
echo "Duplicate passwords found: $DUPLICATES"

if [[ $WEAK -gt 0 ]]; then
    echo "Consider updating weak passwords:"
    pwgen-cli analyze weak --format table | head -10
fi

if [[ $DUPLICATES -gt 0 ]]; then
    echo "Consider removing duplicate passwords:"
    pwgen-cli analyze duplicates --format table | head -10
fi

# Clean up source file
read -p "Delete source file $SOURCE_FILE? [y/N]: " -n 1 -r
echo
if [[ $REPLY =~ ^[Yy]$ ]]; then
    rm "$SOURCE_FILE"
    echo "Source file deleted"
fi

echo "Migration complete! Vault location: $VAULT_PATH"
\`\`\`

### Batch Migration Script

For migrating multiple sources:

\`\`\`bash
#!/bin/bash
# batch-migrate.sh

set -euo pipefail

SOURCES_DIR="$1"
VAULT_PATH="$2"

export PWGEN_VAULT_PATH="$VAULT_PATH"

# Create vault if needed
if [[ ! -f "$VAULT_PATH" ]]; then
    pwgen-cli vault create --path "$VAULT_PATH"
fi

# Process each export file
for file in "$SOURCES_DIR"/*.csv "$SOURCES_DIR"/*.json; do
    [[ ! -f "$file" ]] && continue
    
    filename=$(basename "$file")
    echo "Processing $filename..."
    
    # Determine type from filename
    case "$filename" in
        *lastpass*)
            pwgen-cli import lastpass --file "$file" --merge --tag "from-lastpass"
            ;;
        *bitwarden*)
            pwgen-cli import bitwarden --file "$file" --merge --tag "from-bitwarden"
            ;;
        *1password*)
            pwgen-cli import 1password --file "$file" --merge --tag "from-1password"
            ;;
        *keepass*)
            pwgen-cli import keepass --file "$file" --merge --tag "from-keepass"
            ;;
        *)
            echo "Unknown format: $filename, trying generic CSV..."
            pwgen-cli import csv --file "$file" --merge --tag "imported"
            ;;
    esac
    
    echo "Completed $filename"
done

echo "All files processed. Running final analysis..."
pwgen-cli analyze duplicates --format table
\`\`\`

## Best Practices

### Security During Migration

1. **Use Secure Environment**:
   - Migrate on a secure, private computer
   - Disable cloud sync during migration
   - Use full-disk encryption
   - Close unnecessary applications

2. **Handle Export Files Securely**:
   \`\`\`bash
   # Create encrypted directory for exports
   mkdir -p ~/migration-temp
   chmod 700 ~/migration-temp
   
   # Work in temporary directory
   cd ~/migration-temp
   
   # Securely delete after migration
   shred -vfz -n 3 *.csv *.json
   cd ..
   rmdir ~/migration-temp
   \`\`\`

3. **Verify Data Integrity**:
   \`\`\`bash
   # Compare entry counts
   echo "Source entries: $(wc -l < export.csv)"
   echo "Imported entries: $(pwgen-cli password list --format json | jq '. | length')"
   
   # Spot check important entries
   pwgen-cli password search "bank" --format table
   pwgen-cli password search "email" --format table
   \`\`\`

### Performance Optimization

1. **For Large Imports**:
   \`\`\`bash
   # Use batch processing
   pwgen-cli import csv --file large-export.csv --batch-size 1000
   
   # Disable progress reporting for speed
   PWGEN_NO_PROGRESS=1 pwgen-cli import csv --file large-export.csv
   \`\`\`

2. **Memory Management**:
   \`\`\`bash
   # Monitor memory usage during import
   top -p $(pgrep pwgen-cli)
   
   # Split very large files
   split -l 5000 huge-export.csv chunk_
   \`\`\`

### Data Quality

1. **Standardize Data**:
   \`\`\`bash
   # Standardize common sites
   pwgen-cli password update --site "www.google.com" --site "google.com"
   pwgen-cli password update --site "mail.google.com" --site "gmail.com"
   
   # Add consistent tagging
   pwgen-cli password list --site "*bank*" --format json | $
     jq -r '.[].id' | $
     xargs -I {} pwgen-cli password update --id {} --add-tags "banking"
   \`\`\`

2. **Enhance Imported Data**:
   \`\`\`bash
   # Add URLs for entries missing them
   pwgen-cli password list --format json | $
     jq -r '.[] | select(.url == "") | "\\(.id):\\(.site)"' | $
     while IFS=: read -r id site; do
       if [[ "$site" =~ \\. ]]; then
         pwgen-cli password update --id "$id" --url "https://$site"
       fi
     done
   \`\`\`

## See Also

- [CLI Commands](../cli/commands) - Import command reference
- [Getting Started](../getting-started/installation) - Initial setup
- [User Guide](../user-guide/passwords) - Managing passwords after migration
- [Security](../security/architecture) - Security considerations`
  },

  // Tutorials - Team Usage
  'tutorials/team-usage': {
    title: 'Team Usage and Collaboration',
    description: 'Guide to using PWGen-Rust-Rust in team environments with shared vaults and access control',
    content: `# Team Usage and Collaboration

Guide to using PWGen-Rust-Rust in team environments, including shared vaults, access control, and collaborative workflows.

## Overview

PWGen-Rust-Rust provides several approaches for team collaboration while maintaining security and access control. This guide covers different deployment models, from shared filesystem access to enterprise-grade team management.

## Team Deployment Models

### Shared Filesystem Access

The simplest approach for small teams with shared infrastructure:

\`\`\`bash
# Create shared vault location
sudo mkdir -p /shared/team-vaults
sudo chgrp developers /shared/team-vaults
sudo chmod 770 /shared/team-vaults

# Create team vault
pwgen-cli vault create --path /shared/team-vaults/engineering.vault

# Set permissions
chmod 660 /shared/team-vaults/engineering.vault
chgrp developers /shared/team-vaults/engineering.vault
\`\`\`

### Department-Based Vaults

Organize vaults by department or project:

\`\`\`bash
# Engineering team vault
pwgen-cli vault create --path /shared/vaults/engineering.vault

# Marketing team vault
pwgen-cli vault create --path /shared/vaults/marketing.vault

# DevOps infrastructure vault
pwgen-cli vault create --path /shared/vaults/infrastructure.vault

# Set department-specific permissions
chgrp engineering /shared/vaults/engineering.vault
chgrp marketing /shared/vaults/marketing.vault
chgrp devops /shared/vaults/infrastructure.vault
\`\`\`

### Project-Based Organization

Organize credentials by project or client:

\`\`\`bash
# Create project vaults
for project in "project-alpha" "project-beta" "client-acme"; do
    pwgen-cli vault create --path "/shared/projects/$project/vault.db"
    chmod 660 "/shared/projects/$project/vault.db"
done

# Team members use project-specific configuration
export PWGEN_VAULT_PATH="/shared/projects/project-alpha/vault.db"
\`\`\`

## Access Control Strategies

### Unix Groups and Permissions

Leverage standard Unix permissions for access control:

\`\`\`bash
# Create team groups
sudo groupadd pwgen-admins
sudo groupadd pwgen-engineering
sudo groupadd pwgen-devops
sudo groupadd pwgen-readonly

# Add users to groups
sudo usermod -a -G pwgen-engineering alice
sudo usermod -a -G pwgen-devops bob
sudo usermod -a -G pwgen-admins,pwgen-engineering carol

# Set vault permissions
chgrp pwgen-engineering /shared/vaults/engineering.vault
chmod 660 /shared/vaults/engineering.vault

chgrp pwgen-devops /shared/vaults/infrastructure.vault
chmod 660 /shared/vaults/infrastructure.vault
\`\`\`

### Role-Based Access Control

Define roles with specific responsibilities:

\`\`\`bash
# Admin role - full access to all vaults
create_admin_user() {
    local username="$1"
    sudo usermod -a -G pwgen-admins "$username"
    
    # Create admin configuration
    sudo -u "$username" tee "/home/$username/.config/pwgen/admin.toml" <<EOF
[general]
default_vault = "/shared/vaults/master.vault"
confirm_destructive = true

[security]
audit_log = true
audit_log_file = "/var/log/pwgen/admin-audit.log"
EOF
}

# Developer role - access to project vaults
create_developer_user() {
    local username="$1"
    local team="$2"
    sudo usermod -a -G "pwgen-$team" "$username"
    
    # Create team configuration
    sudo -u "$username" tee "/home/$username/.config/pwgen/config.toml" <<EOF
[general]
default_vault = "/shared/vaults/$team.vault"

[security]
audit_log = true
session_timeout_minutes = 30
EOF
}

# Readonly role - limited access
create_readonly_user() {
    local username="$1"
    sudo usermod -a -G pwgen-readonly "$username"
    
    # Readonly configuration with limited permissions
    sudo -u "$username" tee "/home/$username/.config/pwgen/readonly.toml" <<EOF
[general]
default_vault = "/shared/vaults/readonly.vault"
confirm_destructive = true

[cli]
non_interactive = false
max_results = 50
EOF
}
\`\`\`

## Team Workflows

### Daily Team Operations

Standard workflow for team password management:

\`\`\`bash
# Morning team sync - check vault status
pwgen-cli vault status

# List recently added entries
pwgen-cli password list --newer-than "1 day" --format table

# Check for team announcements in notes
pwgen-cli password search "TEAM:" --format table
\`\`\`

### Adding Shared Credentials

Process for adding new shared accounts:

\`\`\`bash
# Add new service with team tags
add_team_credential() {
    local service="$1"
    local username="$2"
    local team_tags="$3"
    local added_by="$4"
    
    echo "Adding team credential for $service..."
    
    # Generate secure password
    password=$(pwgen-cli generate --length 20 --symbols --no-ambiguous)
    
    # Add with team metadata
    pwgen-cli password add $
        --site "$service" $
        --username "$username" $
        --password "$password" $
        --tags "team,shared,$team_tags" $
        --notes "Added by $added_by on $(date). Team shared account." $
        --url "https://$service"
    
    echo "✓ Added $service credentials for team use"
}

# Usage example
add_team_credential "aws-console" "team@company.com" "aws,production" "alice"
\`\`\`

### Password Updates and Rotation

Coordinated password updates:

\`\`\`bash
# Team password rotation workflow
rotate_team_password() {
    local service="$1"
    local updated_by="$2"
    
    echo "Rotating password for $service..."
    
    # Generate new password
    new_password=$(pwgen-cli generate --length 20 --symbols --no-ambiguous)
    
    # Update with rotation metadata
    pwgen-cli password update $
        --site "$service" $
        --password "$new_password" $
        --add-tags "rotated-$(date +%Y%m%d)" $
        --notes "Password rotated by $updated_by on $(date). Previous team members should use new credentials."
    
    echo "✓ Password rotated for $service"
    echo "📢 Notify team members to use new credentials"
}

# Bulk rotation for critical services
critical_services=("aws-console" "github-org" "docker-registry" "kubernetes-dashboard")

for service in "\${critical_services[@]}"; do
    rotate_team_password "$service" "security-team"
    sleep 5  # Avoid overwhelming the system
done
\`\`\`

### Team Onboarding

Streamlined process for new team members:

\`\`\`bash
#!/bin/bash
# team-onboarding.sh

onboard_team_member() {
    local new_member="$1"
    local team="$2"
    local role="$3"
    
    echo "Onboarding $new_member to $team team as $role"
    
    # Add to appropriate groups
    sudo usermod -a -G "pwgen-$team" "$new_member"
    
    # Create user configuration
    user_home=$(eval echo "~$new_member")
    config_dir="$user_home/.config/pwgen"
    
    sudo -u "$new_member" mkdir -p "$config_dir"
    
    # Team-specific configuration
    sudo -u "$new_member" tee "$config_dir/config.toml" <<EOF
[general]
default_vault = "/shared/vaults/$team.vault"
auto_lock_minutes = 30

[cli]
default_format = "table"
show_progress = true

[security]
audit_log = true
audit_log_file = "$user_home/.local/share/pwgen/audit.log"
session_timeout_minutes = 30

[aliases]
team = "password list --tag team"
shared = "password list --tag shared"
my-additions = "password list --tag added-by-$new_member"
EOF
    
    # Set permissions
    chmod 600 "$config_dir/config.toml"
    
    # Grant vault access
    vault_path="/shared/vaults/$team.vault"
    if [[ -f "$vault_path" ]]; then
        sudo chgrp "pwgen-$team" "$vault_path"
        chmod 660 "$vault_path"
    fi
    
    echo "✓ $new_member onboarded to $team team"
    echo "📝 Next steps:"
    echo "   1. Share team vault master password securely"
    echo "   2. Review team password policies"
    echo "   3. Introduce to team workflows"
}

# Usage
onboard_team_member "david" "engineering" "developer"
\`\`\`

### Team Offboarding

Secure process for removing team members:

\`\`\`bash
#!/bin/bash
# team-offboarding.sh

offboard_team_member() {
    local member="$1"
    local team="$2"
    local reason="$3"
    
    echo "Offboarding $member from $team team - Reason: $reason"
    
    # Remove from team groups
    sudo deluser "$member" "pwgen-$team"
    
    # Lock user account if needed
    if [[ "$reason" == "terminated" ]]; then
        sudo usermod -L "$member"
        echo "🔒 User account locked"
    fi
    
    # Tag entries added by this user
    pwgen-cli password list --format json | $
        jq -r --arg user "$member" '.[] | select(.notes | contains($user)) | .id' | $
        xargs -I {} pwgen-cli password update --id {} --add-tags "former-member-$member"
    
    # Generate security report
    report_file="/tmp/offboarding-$member-$(date +%Y%m%d).txt"
    cat > "$report_file" <<EOF
Team Offboarding Report
=====================
Member: $member
Team: $team
Date: $(date)
Reason: $reason

Passwords that may need rotation:
EOF
    
    # List passwords they had access to
    pwgen-cli password list --tag "team" --format table >> "$report_file"
    
    echo "📋 Offboarding report generated: $report_file"
    echo "🔄 Consider rotating critical passwords"
    echo "📝 Review vault access logs for this user"
}

# Usage
offboard_team_member "eve" "engineering" "left-company"
\`\`\`

## Shared Workflows

### Team Password Reviews

Regular security reviews with the team:

\`\`\`bash
#!/bin/bash
# team-security-review.sh

conduct_team_review() {
    local team="$1"
    local vault_path="/shared/vaults/$team.vault"
    
    export PWGEN_VAULT_PATH="$vault_path"
    
    echo "=== $team Team Security Review ==="
    echo "Date: $(date)"
    
    # Basic statistics
    total=$(pwgen-cli password list --format json | jq '. | length')
    team_passwords=$(pwgen-cli password list --tag "team" --format json | jq '. | length')
    
    echo "Total passwords: $total"
    echo "Team shared passwords: $team_passwords"
    
    # Security analysis
    echo -e "\\n🔍 Security Analysis:"
    weak=$(pwgen-cli analyze weak --format json | jq '. | length')
    duplicates=$(pwgen-cli analyze duplicates --format json | jq '. | length')
    old=$(pwgen-cli analyze old --max-age 90 --format json | jq '. | length')
    
    echo "Weak passwords: $weak"
    echo "Duplicate passwords: $duplicates" 
    echo "Old passwords (>90 days): $old"
    
    # Recent activity
    echo -e "\\n📊 Recent Activity:"
    recent=$(pwgen-cli password list --newer-than "7 days" --format json | jq '. | length')
    echo "New entries (7 days): $recent"
    
    if [[ $recent -gt 0 ]]; then
        echo -e "\\nRecent additions:"
        pwgen-cli password list --newer-than "7 days" --format table
    fi
    
    # Action items
    echo -e "\\n✅ Action Items:"
    if [[ $weak -gt 0 ]]; then
        echo "- Update $weak weak passwords"
    fi
    if [[ $duplicates -gt 0 ]]; then
        echo "- Resolve $duplicates duplicate passwords"
    fi
    if [[ $old -gt 0 ]]; then
        echo "- Consider rotating $old old passwords"
    fi
    
    # Generate detailed report
    report_file="/shared/reports/$team-security-review-$(date +%Y%m%d).html"
    mkdir -p "$(dirname "$report_file")"
    pwgen-cli analyze strength --export "$report_file"
    
    echo -e "\\n📄 Detailed report: $report_file"
}

# Run for all teams
for team in engineering marketing devops; do
    conduct_team_review "$team"
    echo -e "\\n" + $(printf '=%.0s' {1..50}) + "\\n"
done
\`\`\`

### Collaborative Password Management

Structured approach to team password changes:

\`\`\`bash
# Team change request workflow
request_password_change() {
    local service="$1"
    local requestor="$2"
    local reason="$3"
    
    # Log the request
    echo "$(date): Password change requested for $service by $requestor - $reason" >> /shared/logs/password-requests.log
    
    # Check current password info
    pwgen-cli password search "$service" --format table
    
    # Create change ticket
    ticket_file="/shared/tickets/password-change-$service-$(date +%Y%m%d-%H%M).txt"
    cat > "$ticket_file" <<EOF
Password Change Request
======================
Service: $service
Requested by: $requestor
Date: $(date)
Reason: $reason

Current Status:
$(pwgen-cli password search "$service" --format table)

Actions Required:
[ ] Approve change request
[ ] Generate new password
[ ] Update service
[ ] Update vault
[ ] Notify team
[ ] Verify access

Approver: ________________
Date Completed: ____________
EOF
    
    echo "📋 Change request created: $ticket_file"
    echo "📧 Notify team lead for approval"
}

# Approve and execute password change
execute_password_change() {
    local service="$1"
    local approver="$2"
    local ticket_file="$3"
    
    echo "Executing approved password change for $service..."
    
    # Generate new password
    new_password=$(pwgen-cli generate --length 20 --symbols --no-ambiguous)
    
    # Update vault
    pwgen-cli password update $
        --site "$service" $
        --password "$new_password" $
        --add-tags "changed-$(date +%Y%m%d)" $
        --notes "Password changed on $(date) by $approver. Ticket: $(basename "$ticket_file")"
    
    # Log completion
    echo "$(date): Password changed for $service by $approver" >> /shared/logs/password-changes.log
    
    # Mark ticket complete
    echo "Completed by: $approver" >> "$ticket_file"
    echo "Date: $(date)" >> "$ticket_file"
    
    echo "✅ Password change completed for $service"
    echo "📢 Notify team of password update"
}
\`\`\`

## Team Communication

### Password Change Notifications

Automated notifications for password updates:

\`\`\`bash
# Notification system for password changes
notify_team_password_change() {
    local service="$1"
    local changed_by="$2"
    local team_email="$3"
    
    # Create notification
    notification_file="/tmp/password-change-notification-$(date +%Y%m%d-%H%M).txt"
    
    cat > "$notification_file" <<EOF
Subject: Password Updated - $service

Team,

The password for $service has been updated.

Changed by: $changed_by
Date: $(date)

Please update your local copies if you use this service directly.

Current service details:
$(pwgen-cli password search "$service" --format table | grep -v "Password")

Access the updated password via the team vault.

Questions? Contact the security team.

--
Automated PWGen-Rust Team Notification
EOF
    
    # Send via email (configure for your environment)
    if command -v mail >/dev/null 2>&1; then
        mail -s "Password Updated - $service" "$team_email" < "$notification_file"
        echo "📧 Team notification sent to $team_email"
    else
        echo "📋 Notification prepared: $notification_file"
        echo "📧 Manually send to team: $team_email"
    fi
}

# Usage
notify_team_password_change "aws-console" "alice" "engineering@company.com"
\`\`\`

### Team Status Dashboard

Create team status overview:

\`\`\`bash
#!/bin/bash
# team-dashboard.sh

generate_team_dashboard() {
    local team="$1"
    local vault_path="/shared/vaults/$team.vault"
    
    export PWGEN_VAULT_PATH="$vault_path"
    
    # Create dashboard HTML
    dashboard_file="/shared/dashboards/$team-dashboard.html"
    mkdir -p "$(dirname "$dashboard_file")"
    
    cat > "$dashboard_file" <<EOF
<!DOCTYPE html>
<html>
<head>
    <title>$team Team Password Dashboard</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .metric { display: inline-block; margin: 10px; padding: 15px; background: #f0f0f0; border-radius: 5px; }
        .warning { background: #fff3cd; border: 1px solid #ffeaa7; }
        .danger { background: #f8d7da; border: 1px solid #f5c6cb; }
        table { border-collapse: collapse; width: 100%; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
    </style>
</head>
<body>
    <h1>$team Team Password Dashboard</h1>
    <p>Generated: $(date)</p>
    
    <div class="metrics">
        <div class="metric">
            <h3>Total Passwords</h3>
            <p>$(pwgen-cli password list --format json | jq '. | length')</p>
        </div>
        <div class="metric">
            <h3>Team Shared</h3>
            <p>$(pwgen-cli password list --tag "team" --format json | jq '. | length')</p>
        </div>
        <div class="metric warning">
            <h3>Weak Passwords</h3>
            <p>$(pwgen-cli analyze weak --format json | jq '. | length')</p>
        </div>
        <div class="metric danger">
            <h3>Duplicates</h3>
            <p>$(pwgen-cli analyze duplicates --format json | jq '. | length')</p>
        </div>
    </div>
    
    <h2>Recent Activity (7 days)</h2>
    <table>
        <tr><th>Site</th><th>Username</th><th>Tags</th><th>Added</th></tr>
EOF
    
    # Add recent entries
    pwgen-cli password list --newer-than "7 days" --format json | $
        jq -r '.[] | "<tr><td>\\(.site)</td><td>\\(.username)</td><td>\\(.tags | join(", "))</td><td>\\(.created)</td></tr>"' >> "$dashboard_file"
    
    cat >> "$dashboard_file" <<EOF
    </table>
    
    <h2>Action Items</h2>
    <ul>
EOF
    
    # Add action items based on analysis
    weak_count=$(pwgen-cli analyze weak --format json | jq '. | length')
    if [[ $weak_count -gt 0 ]]; then
        echo "        <li>Update $weak_count weak passwords</li>" >> "$dashboard_file"
    fi
    
    duplicate_count=$(pwgen-cli analyze duplicates --format json | jq '. | length')
    if [[ $duplicate_count -gt 0 ]]; then
        echo "        <li>Resolve $duplicate_count duplicate passwords</li>" >> "$dashboard_file"
    fi
    
    old_count=$(pwgen-cli analyze old --max-age 90 --format json | jq '. | length')
    if [[ $old_count -gt 0 ]]; then
        echo "        <li>Consider rotating $old_count passwords older than 90 days</li>" >> "$dashboard_file"
    fi
    
    cat >> "$dashboard_file" <<EOF
    </ul>
    
    <p><em>Dashboard auto-updated every hour via cron</em></p>
</body>
</html>
EOF
    
    echo "📊 Dashboard generated: $dashboard_file"
}

# Generate dashboards for all teams
for team in engineering marketing devops; do
    generate_team_dashboard "$team"
done
\`\`\`

## Security and Compliance

### Audit Trail Management

Comprehensive audit logging for team environments:

\`\`\`bash
# Setup team audit logging
setup_team_auditing() {
    local team="$1"
    
    # Create audit log directory
    audit_dir="/var/log/pwgen/$team"
    sudo mkdir -p "$audit_dir"
    sudo chgrp "pwgen-$team" "$audit_dir"
    sudo chmod 750 "$audit_dir"
    
    # Configure team-wide audit settings
    cat > "/shared/config/$team-audit.toml" <<EOF
[security]
audit_log = true
audit_log_file = "$audit_dir/team-audit.log"
require_master_password = true
session_timeout_minutes = 30

[logging]
log_level = "info"
log_file = "$audit_dir/application.log"
rotate_logs = true
max_log_size = "10MB"
max_log_files = 5
EOF
    
    # Setup log rotation
    sudo tee "/etc/logrotate.d/pwgen-$team" <<EOF
$audit_dir/*.log {
    daily
    rotate 30
    compress
    delaycompress
    missingok
    notifempty
    copytruncate
    postrotate
        # Generate daily audit report
        /usr/local/bin/generate-audit-report.sh $team
    endscript
}
EOF
    
    echo "🔍 Audit logging configured for $team team"
}

# Generate audit reports
generate_audit_report() {
    local team="$1"
    local date_range="\${2:-1 day}"
    
    audit_log="/var/log/pwgen/$team/team-audit.log"
    report_file="/shared/reports/$team-audit-$(date +%Y%m%d).txt"
    
    if [[ ! -f "$audit_log" ]]; then
        echo "No audit log found for $team team"
        return 1
    fi
    
    # Parse audit log for specified date range
    since_date=$(date -d "$date_range ago" '+%Y-%m-%d')
    
    cat > "$report_file" <<EOF
$team Team Audit Report
=====================
Report Date: $(date)
Period: Last $date_range

EOF
    
    # Extract key activities
    echo "Password Access Events:" >> "$report_file"
    grep -E "(password|vault)" "$audit_log" | $
        awk -v since="$since_date" '$1 >= since' >> "$report_file"
    
    echo -e "\\nVault Operations:" >> "$report_file"
    grep -E "(create|unlock|lock)" "$audit_log" | $
        awk -v since="$since_date" '$1 >= since' >> "$report_file"
    
    echo -e "\\nUser Sessions:" >> "$report_file"
    grep -E "(login|logout|session)" "$audit_log" | $
        awk -v since="$since_date" '$1 >= since' >> "$report_file"
    
    echo "📋 Audit report generated: $report_file"
}
\`\`\`

### Compliance Monitoring

Monitor team compliance with security policies:

\`\`\`bash
# Compliance checker for team environments
check_team_compliance() {
    local team="$1"
    local vault_path="/shared/vaults/$team.vault"
    
    export PWGEN_VAULT_PATH="$vault_path"
    
    echo "🔍 Compliance Check - $team Team"
    echo "Date: $(date)"
    
    compliance_score=100
    issues=()
    
    # Check 1: Password strength requirements
    weak_passwords=$(pwgen-cli analyze weak --format json | jq '. | length')
    if [[ $weak_passwords -gt 0 ]]; then
        compliance_score=$((compliance_score - 10))
        issues+=("$weak_passwords weak passwords found")
    fi
    
    # Check 2: Password age requirements
    old_passwords=$(pwgen-cli analyze old --max-age 90 --format json | jq '. | length')
    if [[ $old_passwords -gt 0 ]]; then
        compliance_score=$((compliance_score - 15))
        issues+=("$old_passwords passwords older than 90 days")
    fi
    
    # Check 3: Duplicate password policy
    duplicate_passwords=$(pwgen-cli analyze duplicates --format json | jq '. | length')
    if [[ $duplicate_passwords -gt 0 ]]; then
        compliance_score=$((compliance_score - 20))
        issues+=("$duplicate_passwords duplicate passwords found")
    fi
    
    # Check 4: Audit logging enabled
    if [[ ! -f "/var/log/pwgen/$team/team-audit.log" ]]; then
        compliance_score=$((compliance_score - 25))
        issues+=("Audit logging not properly configured")
    fi
    
    # Check 5: Backup recency
    latest_backup=$(find /shared/backups -name "$team-*.pwgen" -printf "%T@ %p\\n" 2>/dev/null | sort -n | tail -1 | cut -d' ' -f2-)
    if [[ -z "$latest_backup" ]] || [[ $(find "$latest_backup" -mtime +7 2>/dev/null) ]]; then
        compliance_score=$((compliance_score - 20))
        issues+=("No recent backup found (>7 days)")
    fi
    
    # Generate compliance report
    compliance_file="/shared/reports/$team-compliance-$(date +%Y%m%d).txt"
    cat > "$compliance_file" <<EOF
$team Team Compliance Report
===========================
Date: $(date)
Compliance Score: $compliance_score/100

Issues Found:
EOF
    
    if [[ \${#issues[@]} -eq 0 ]]; then
        echo "✅ No compliance issues found" >> "$compliance_file"
    else
        for issue in "\${issues[@]}"; do
            echo "❌ $issue" >> "$compliance_file"
        done
    fi
    
    cat >> "$compliance_file" <<EOF

Recommendations:
- Review and update weak passwords immediately
- Implement password rotation schedule
- Ensure audit logging is enabled and monitored
- Maintain regular backup schedule
- Train team members on security policies

Next Review: $(date -d "+30 days" +%Y-%m-%d)
EOF
    
    echo "Compliance Score: $compliance_score/100"
    echo "📋 Full report: $compliance_file"
    
    # Alert if compliance is poor
    if [[ $compliance_score -lt 70 ]]; then
        echo "⚠️  LOW COMPLIANCE SCORE - Immediate action required"
        # Send alert to security team
        mail -s "LOW COMPLIANCE: $team Team" security@company.com < "$compliance_file"
    fi
}

# Run compliance checks for all teams
for team in engineering marketing devops; do
    check_team_compliance "$team"
    echo ""
done
\`\`\`

## Best Practices for Teams

### Security Guidelines

1. **Access Control**:
   \`\`\`bash
   # Principle of least privilege
   # Only grant access to vaults team members need
   
   # Regular access reviews
   quarterly_access_review() {
       echo "Quarterly Access Review - $(date)"
       for team in engineering marketing devops; do
           echo "=== $team Team ==="
           getent group "pwgen-$team" | cut -d: -f4 | tr ',' '\\n'
       done
   }
   \`\`\`

2. **Password Policies**:
   \`\`\`bash
   # Enforce strong password generation
   team_password_policy() {
       pwgen-cli generate --length 20 --symbols --no-ambiguous
   }
   
   # Regular rotation schedule
   # Critical: 30 days, Standard: 90 days, Low-risk: 180 days
   \`\`\`

3. **Backup Strategy**:
   \`\`\`bash
   # Automated team backups
   daily_team_backup() {
       for team in engineering marketing devops; do
           backup_file="/shared/backups/$team-$(date +%Y%m%d).pwgen"
           PWGEN_VAULT_PATH="/shared/vaults/$team.vault" $
               pwgen-cli backup create --output "$backup_file"
           
           # Keep 30 days of backups
           find /shared/backups -name "$team-*.pwgen" -mtime +30 -delete
       done
   }
   \`\`\`

### Operational Procedures

1. **Change Management**:
   \`\`\`bash
   # All password changes require:
   # 1. Request with business justification
   # 2. Approval from team lead
   # 3. Documentation of change
   # 4. Team notification
   # 5. Verification of access
   \`\`\`

2. **Incident Response**:
   \`\`\`bash
   security_incident_response() {
       local incident_type="$1"
       
       case "$incident_type" in
           "credential-compromise")
               # Immediate password rotation
               # Revoke access
               # Audit vault access
               # Generate incident report
               ;;
           "unauthorized-access")
               # Lock affected accounts
               # Review audit logs
               # Check for data exfiltration
               # Strengthen access controls
               ;;
       esac
   }
   \`\`\`

3. **Training and Documentation**:
   \`\`\`bash
   # Regular security training
   # Updated procedures documentation
   # Team knowledge sharing sessions
   # Security awareness updates
   \`\`\`

## Monitoring and Alerting

### Automated Monitoring

Setup automated monitoring for team vaults:

\`\`\`bash
#!/bin/bash
# team-monitoring.sh

monitor_team_vaults() {
    local alert_email="security@company.com"
    
    for team in engineering marketing devops; do
        vault_path="/shared/vaults/$team.vault"
        
        # Check vault accessibility
        if ! PWGEN_VAULT_PATH="$vault_path" pwgen-cli vault status >/dev/null 2>&1; then
            echo "ALERT: $team vault inaccessible" | $
                mail -s "Vault Alert - $team" "$alert_email"
            continue
        fi
        
        # Check for security issues
        export PWGEN_VAULT_PATH="$vault_path"
        
        weak_count=$(pwgen-cli analyze weak --format json | jq '. | length')
        if [[ $weak_count -gt 5 ]]; then
            echo "ALERT: $team vault has $weak_count weak passwords" | $
                mail -s "Security Alert - $team Weak Passwords" "$alert_email"
        fi
        
        # Check backup freshness
        latest_backup=$(find /shared/backups -name "$team-*.pwgen" -printf "%T@ %p\\n" | sort -n | tail -1)
        if [[ -z "$latest_backup" ]] || [[ $(echo "$latest_backup" | cut -d' ' -f1) -lt $(date -d "7 days ago" +%s) ]]; then
            echo "ALERT: $team vault backup is outdated" | $
                mail -s "Backup Alert - $team" "$alert_email"
        fi
    done
}

# Run every hour via cron
# 0 * * * * /usr/local/bin/team-monitoring.sh
\`\`\`

## See Also

- [CLI Commands](../cli/commands) - Team-relevant commands
- [Security Architecture](../security/architecture) - Security implementation
- [Automation](automation) - Automated team workflows
- [User Guide](../user-guide/passwords) - Basic password management`
  },

  'developers/advanced-building': {
    title: 'Advanced Building from Source',
    description: 'Complete guide to building PwGen-rust from source code on all supported platforms',
    content: `# Building from Source

Complete guide to building PwGen-rust from source code on all supported platforms.

## Prerequisites

### Required Tools

1. **Rust Toolchain**
   \`\`\`bash
   # Install rustup (Rust installer)
   curl --proto '=https' --tlsv1.2 -sSf https://sh.rustup.rs | sh
   
   # Use minimum required version
   rustup install 1.75.0
   rustup default 1.75.0
   \`\`\`

2. **Git**
   \`\`\`bash
   # Ubuntu/Debian
   sudo apt install git
   
   # macOS
   brew install git
   
   # Windows: Download from git-scm.com
   \`\`\`

3. **Build Tools**
   \`\`\`bash
   # Ubuntu/Debian
   sudo apt install build-essential pkg-config
   
   # CentOS/RHEL/Fedora
   sudo dnf install gcc gcc-c++ pkg-config
   
   # macOS (install Xcode Command Line Tools)
   xcode-select --install
   
   # Windows (install Visual Studio Build Tools)
   # Download from visualstudio.microsoft.com
   \`\`\`

### Platform-Specific Dependencies

#### Linux

**Ubuntu/Debian:**
\`\`\`bash
# GUI dependencies
sudo apt install libgtk-3-dev libgtk-3-0

# Optional: For system integration
sudo apt install libxss1 libgconf-2-4 libnss3 libxtst6 libatspi2.0-0 libdrm2 libxcomposite1 libxdamage1 libxrandr2 libgbm1 libxss1 libasound2

# SSL support
sudo apt install libssl-dev
\`\`\`

**CentOS/RHEL/Fedora:**
\`\`\`bash
# GUI dependencies
sudo dnf install gtk3-devel

# SSL support
sudo dnf install openssl-devel
\`\`\`

**Arch Linux:**
\`\`\`bash
# All dependencies
sudo pacman -S base-devel gtk3 openssl
\`\`\`

#### macOS

\`\`\`bash
# Install dependencies via Homebrew
brew install pkg-config openssl cmake

# Set environment variables for OpenSSL
echo 'export PATH="/opt/homebrew/bin:$PATH"' >> ~/.zshrc
echo 'export LDFLAGS="-L/opt/homebrew/lib"' >> ~/.zshrc
echo 'export CPPFLAGS="-I/opt/homebrew/include"' >> ~/.zshrc
\`\`\`

#### Windows

1. **Visual Studio Build Tools 2022**
   - Download and install from Microsoft
   - Include C++ build tools
   - Include Windows 10/11 SDK

2. **Optional: vcpkg for dependencies**
   \`\`\`powershell
   git clone https://github.com/Microsoft/vcpkg.git
   cd vcpkg
   .\\bootstrap-vcpkg.bat
   \`\`\`

## Source Code

### Clone Repository

\`\`\`bash
# Clone the repository
git clone https://github.com/KiefStudioMA/PWGen-Rust.git
cd PWGen-Rust

# Check available branches/tags
git branch -a
git tag -l

# Use specific version (recommended for production)
git checkout v1.2.0
\`\`\`

### Repository Structure

\`\`\`
PWGen-Rust/
├── Cargo.toml          # Workspace configuration
├── Cargo.lock          # Dependency lockfile
├── pwgen-core/         # Core library
├── pwgen-cli/          # Command-line interface
├── pwgen-gui/          # Desktop GUI application
├── pwgen-server/       # API server (in development)
├── scripts/            # Build and installation scripts
├── docs/               # Additional documentation
├── tests/              # Integration tests
└── .github/            # CI/CD workflows
\`\`\`

## Build Process

### Default Build

\`\`\`bash
# Build all components (debug mode)
cargo build

# Build specific component
cargo build -p pwgen-cli
cargo build -p pwgen-gui
cargo build -p pwgen-core

# Run tests
cargo test

# Format code
cargo fmt

# Run linter
cargo clippy -- -D warnings
\`\`\`

### Release Build

\`\`\`bash
# Build optimized release version
cargo build --release

# Binaries will be in target/release/
ls target/release/pwgen-*
\`\`\`

### Feature-Specific Builds

PwGen-rust supports conditional compilation for different use cases:

\`\`\`bash
# Minimal build (CLI only, no GUI dependencies)
cargo build --release --no-default-features --features "cli"

# GUI only
cargo build --release --no-default-features --features "gui"

# Maximum security build
cargo build --release --features "secure"

# Development build with debug features
cargo build --features "dev-tools,logging"
\`\`\`

### Cross-Compilation

#### Linux to Windows

\`\`\`bash
# Install target
rustup target add x86_64-pc-windows-gnu

# Install cross-compilation tools
sudo apt install gcc-mingw-w64-x86-64

# Build
cargo build --release --target x86_64-pc-windows-gnu
\`\`\`

#### macOS to Linux

\`\`\`bash
# Install target
rustup target add x86_64-unknown-linux-gnu

# Note: May require Docker or additional setup for dependencies
\`\`\`

## Build Optimization

### Performance Optimization

\`\`\`toml
# Cargo.toml profile for maximum performance
[profile.release]
opt-level = 3           # Maximum optimization
lto = "fat"             # Link-time optimization
codegen-units = 1       # Single codegen unit for better optimization
panic = "abort"         # Smaller binary size
strip = true            # Remove debug symbols
\`\`\`

### Size Optimization

\`\`\`toml
# Cargo.toml profile for minimal size
[profile.min-size]
inherits = "release"
opt-level = "z"         # Optimize for size
lto = true
panic = "abort"
strip = true
\`\`\`

\`\`\`bash
# Build with size optimization
cargo build --profile min-size
\`\`\`

### Security-Focused Build

\`\`\`toml
# Cargo.toml profile for security
[profile.secure]
inherits = "release"
overflow-checks = true  # Runtime integer overflow checks
debug-assertions = true # Enable debug assertions in release
\`\`\`

## Platform-Specific Builds

### Linux Packaging

#### AppImage

\`\`\`bash
# Install linuxdeploy
wget https://github.com/linuxdeploy/linuxdeploy/releases/download/continuous/linuxdeploy-x86_64.AppImage
chmod +x linuxdeploy-x86_64.AppImage

# Build and package
cargo build --release
./scripts/build-appimage.sh
\`\`\`

#### Snap Package

\`\`\`bash
# Install snapcraft
sudo apt install snapcraft

# Build snap
snapcraft
\`\`\`

#### Debian Package

\`\`\`bash
# Install cargo-deb
cargo install cargo-deb

# Build .deb package
cargo deb -p pwgen-gui
\`\`\`

### macOS Packaging

#### App Bundle

\`\`\`bash
# Build GUI application
cargo build --release -p pwgen-gui

# Create app bundle
./scripts/create-macos-app.sh

# Sign and notarize (requires Apple Developer account)
codesign --sign "Developer ID Application: Your Name" PwGen.app
xcrun notarytool submit PwGen.dmg
\`\`\`

#### Installer Package

\`\`\`bash
# Create .pkg installer
pkgbuild --root dist/macos --identifier dev.pwgenrust.pwgen --version 1.2.0 PwGen.pkg
\`\`\`

### Windows Packaging

#### NSIS Installer

\`\`\`bash
# Install NSIS (on Windows)
# Download from nsis.sourceforge.io

# Build executable
cargo build --release --target x86_64-pc-windows-msvc

# Create installer
makensis scripts/windows-installer.nsi
\`\`\`

#### MSI Installer (WiX)

\`\`\`bash
# Install WiX Toolset
# Download from wixtoolset.org

# Build MSI
candle scripts/pwgen.wxs
light pwgen.wixobj -out PwGen.msi
\`\`\`

## Testing

### Unit Tests

\`\`\`bash
# Run all tests
cargo test

# Run tests with output
cargo test -- --nocapture

# Run specific test
cargo test test_encryption

# Run tests for specific crate
cargo test -p pwgen-core
\`\`\`

### Integration Tests

\`\`\`bash
# Run integration tests
cargo test --test integration

# Run with real vault file
PWGEN_TEST_VAULT=/tmp/test.vault cargo test
\`\`\`

### Security Tests

\`\`\`bash
# Install cargo-audit for vulnerability scanning
cargo install cargo-audit

# Check for vulnerabilities
cargo audit

# Install cargo-deny for dependency analysis
cargo install cargo-deny

# Check licenses and security
cargo deny check
\`\`\`

### Fuzzing

\`\`\`bash
# Install cargo-fuzz
cargo install cargo-fuzz

# Run fuzzing tests
cargo fuzz run fuzz_encryption
cargo fuzz run fuzz_parser
\`\`\`

## Continuous Integration

### GitHub Actions

The project includes CI/CD workflows:

\`\`\`yaml
# .github/workflows/ci.yml
name: CI
on: [push, pull_request]
jobs:
  test:
    runs-on: \${{ matrix.os }}
    strategy:
      matrix:
        os: [ubuntu-latest, macos-latest, windows-latest]
        rust: [stable, beta]
    steps:
      - uses: actions/checkout@v4
      - uses: dtolnay/rust-toolchain@master
        with:
          toolchain: \${{ matrix.rust }}
      - run: cargo test --all-features
\`\`\`

### Local CI Simulation

\`\`\`bash
# Install act to run GitHub Actions locally
curl https://raw.githubusercontent.com/nektos/act/master/install.sh | sudo bash

# Run CI locally
act
\`\`\`

## Troubleshooting

### Common Build Issues

#### Missing Dependencies

**Error:** \`error: Microsoft Visual C++ 14.0 is required\`
\`\`\`bash
# Solution: Install Visual Studio Build Tools
\`\`\`

**Error:** \`fatal error: 'gtk/gtk.h' file not found\`
\`\`\`bash
# Solution: Install GTK development headers
sudo apt install libgtk-3-dev
\`\`\`

#### Compilation Errors

**Error:** \`error: linking with 'cc' failed\`
\`\`\`bash
# Solution: Install build tools
sudo apt install build-essential
\`\`\`

**Error:** \`error: could not find system library 'openssl'\`
\`\`\`bash
# Ubuntu/Debian
sudo apt install libssl-dev

# macOS
brew install openssl
export OPENSSL_DIR=/opt/homebrew/opt/openssl
\`\`\`

#### Performance Issues

**Slow compilation:**
\`\`\`bash
# Use more CPU cores
export CARGO_BUILD_JOBS=4

# Use faster linker (Linux)
sudo apt install lld
export RUSTFLAGS="-C link-arg=-fuse-ld=lld"
\`\`\`

**Large binary size:**
\`\`\`bash
# Strip debug symbols
cargo build --release
strip target/release/pwgen-gui

# Use UPX compression (optional)
upx --best target/release/pwgen-gui
\`\`\`

## Advanced Topics

### Custom Build Scripts

Create custom build configurations:

\`\`\`toml
# build.rs
fn main() {
    // Custom build logic
    println!("cargo:rustc-link-lib=ssl");
    
    // Platform-specific compilation
    if cfg!(target_os = "windows") {
        println!("cargo:rustc-link-lib=crypt32");
    }
}
\`\`\`

### Environment Variables

Control build behavior:

\`\`\`bash
# Debug logging during build
export RUST_LOG=debug

# Custom features
export PWGEN_FEATURES="secure,audit"

# Build with custom config
export PWGEN_CONFIG_PATH=/custom/config.toml
\`\`\`

### Memory Safety

Verify memory safety:

\`\`\`bash
# Install Miri for unsafe code checking
rustup component add miri

# Run with Miri
cargo miri test
\`\`\`

## Release Process

### Preparing a Release

\`\`\`bash
# Update version numbers
sed -i 's/version = "1.1.0"/version = "1.2.0"/' Cargo.toml

# Update changelog
echo "## [1.2.0] - $(date +%Y-%m-%d)" >> CHANGELOG.md

# Run full test suite
cargo test --all-features

# Build all targets
cargo build --release --all-features
\`\`\`

### Creating Release Artifacts

\`\`\`bash
# Run release script
./scripts/prepare-release.sh v1.2.0

# This creates:
# - Linux: .tar.gz, .deb, .AppImage
# - macOS: .dmg, .pkg
# - Windows: .exe, .msi
# - Source: .tar.gz with verification
\`\`\`

## Resources

### Build Tools

- [Rust Installation](https://rustup.rs/)
- [Cargo Book](https://doc.rust-lang.org/cargo/)
- [Cross-compilation Guide](https://rust-lang.github.io/rustup/cross-compilation.html)

### Platform-Specific

- [Linux Packaging](https://wiki.debian.org/HowToPackageForDebian)
- [macOS Deployment](https://developer.apple.com/documentation/xcode/distributing-your-app-for-beta-testing-and-releases)
- [Windows Packaging](https://docs.microsoft.com/en-us/windows/msix/)

### Security

- [Rust Security Guidelines](https://anssi-fr.github.io/rust-guide/)
- [Supply Chain Security](https://github.com/RustSec/rustsec)
- [Cargo Audit](https://github.com/RustSec/rustsec/tree/main/cargo-audit)

---

For additional help with building, see our [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions) or [open an issue](https://github.com/KiefStudioMA/PWGen-Rust/issues).`
  },

  'developers/advanced-contributing': {
    title: 'Advanced Contributing',
    description: 'Help make PwGen-rust even better! This guide covers everything you need to know about contributing to the project',
    content: `# Contributing

Help make PwGen-rust even better! This guide covers everything you need to know about contributing to the project.

## Getting Started

### Prerequisites

1. **Rust Development Environment**
   - Latest stable Rust (1.75+)
   - Familiarity with Rust programming
   - Basic understanding of cryptography concepts

2. **Development Tools**
   - Git for version control
   - Your preferred IDE/editor
   - Understanding of security best practices

### Quick Setup

\`\`\`bash
# Fork and clone the repository
git clone https://github.com/YOUR_USERNAME/PWGen-Rust.git
cd PWGen-Rust

# Build and test
cargo build
cargo test

# Format and lint
cargo fmt
cargo clippy
\`\`\`

## Contribution Guidelines

### Code of Conduct

We are committed to providing a welcoming and inclusive experience for everyone. Please read our [Code of Conduct](https://github.com/KiefStudioMA/PWGen-Rust/blob/main/CODE_OF_CONDUCT.md) before contributing.

### Types of Contributions

- **Bug Reports**: Help us identify and fix issues
- **Feature Requests**: Suggest new functionality
- **Code Contributions**: Submit pull requests
- **Documentation**: Improve guides and tutorials
- **Security**: Report vulnerabilities responsibly
- **Testing**: Help expand test coverage

## Development Workflow

### 1. Create an Issue

Before starting work:
- Check existing issues to avoid duplication
- Create a new issue describing your proposal
- Wait for maintainer feedback on significant changes

### 2. Fork and Branch

\`\`\`bash
# Fork the repository on GitHub
# Clone your fork
git clone https://github.com/YOUR_USERNAME/PWGen-Rust.git

# Create a feature branch
git checkout -b feature/your-feature-name

# Or for bug fixes
git checkout -b fix/issue-123
\`\`\`

### 3. Development

Follow our coding standards:
- Write clear, documented code
- Include comprehensive tests
- Follow Rust conventions
- Add security considerations

### 4. Testing

\`\`\`bash
# Run all tests
cargo test

# Run specific tests
cargo test test_name

# Check formatting
cargo fmt --check

# Run linter
cargo clippy -- -D warnings

# Security audit
cargo audit
\`\`\`

### 5. Commit and Push

\`\`\`bash
# Make focused commits
git add .
git commit -m "feat: add password strength meter

- Implement zxcvbn algorithm
- Add visual strength indicator
- Include tests for edge cases"

# Push to your fork
git push origin feature/your-feature-name
\`\`\`

### 6. Pull Request

Create a pull request with:
- Clear title and description
- Reference to related issues
- Screenshots for UI changes
- Test coverage information

## Coding Standards

### Rust Style

Follow standard Rust conventions:
\`\`\`rust
// Good: Clear, documented functions
/// Encrypts plaintext using AES-256-GCM
/// 
/// # Arguments
/// * \`plaintext\` - Data to encrypt
/// * \`key\` - 256-bit encryption key
/// 
/// # Returns
/// Encrypted data with nonce and authentication tag
pub fn encrypt(plaintext: &[u8], key: &Key) -> Result<Vec<u8>, CryptoError> {
    // Implementation
}

// Bad: Unclear, undocumented
pub fn enc(d: &[u8], k: &Key) -> Result<Vec<u8>, CryptoError> {
    // Implementation
}
\`\`\`

### Security Considerations

- **No Unsafe Code**: Avoid \`unsafe\` unless absolutely necessary
- **Zeroize Secrets**: Always clear sensitive data from memory
- **Constant-Time Operations**: Use constant-time comparisons for secrets
- **Input Validation**: Validate all inputs thoroughly
- **Error Handling**: Handle errors gracefully without leaking information

### Documentation

- Document all public APIs
- Include examples in doc comments
- Update README and guides as needed
- Write clear commit messages

## Security Contributions

### Reporting Vulnerabilities

**Do NOT create public issues for security vulnerabilities.**

Instead:
1. Email: security@pwgenrust.dev
2. Include detailed reproduction steps
3. Wait for acknowledgment
4. Allow time for fix before disclosure

### Security Code Reviews

Security-sensitive changes require:
- Extra careful review
- Multiple approvals
- Security-focused testing
- Documentation of security implications

## Testing Guidelines

### Test Types

1. **Unit Tests**: Test individual functions
2. **Integration Tests**: Test component interactions
3. **Security Tests**: Test cryptographic functions
4. **Performance Tests**: Benchmark critical paths

### Writing Tests

\`\`\`rust
#[cfg(test)]
mod tests {
    use super::*;

    #[test]
    fn test_password_encryption_roundtrip() {
        let password = "test_password";
        let encrypted = encrypt_password(password).unwrap();
        let decrypted = decrypt_password(&encrypted).unwrap();
        assert_eq!(password, decrypted);
    }

    #[test]
    fn test_invalid_key_rejection() {
        let result = decrypt_password(&[0u8; 32]);
        assert!(result.is_err());
    }
}
\`\`\`

## Documentation Contributions

### Areas Needing Help

- User guides and tutorials
- API documentation
- Security documentation
- Architecture documentation
- Troubleshooting guides

### Writing Guidelines

- Clear, concise language
- Step-by-step instructions
- Code examples that work
- Screenshots where helpful
- Consider different skill levels

## Release Process

### Version Numbering

We follow [Semantic Versioning](https://semver.org/):
- \`MAJOR.MINOR.PATCH\`
- Major: Breaking changes
- Minor: New features
- Patch: Bug fixes

### Release Checklist

Before releases:
- [ ] All tests pass
- [ ] Documentation updated
- [ ] Changelog updated
- [ ] Security review completed
- [ ] Performance regression check

## Community

### Communication Channels

- **GitHub Issues**: Bug reports and feature requests
- **GitHub Discussions**: General discussion and questions
- **Twitter**: [@HxHippy](https://x.com/HxHippy) for updates
- **Email**: security@pwgenrust.dev for security issues

### Recognition

Contributors are recognized through:
- Credits in release notes
- Contributor list in README
- Special recognition for significant contributions

## Getting Help

### Resources

- [Rust Book](https://doc.rust-lang.org/book/)
- [Rust Security Guidelines](https://anssi-fr.github.io/rust-guide/)
- [Project Architecture](architecture)
- [Building Guide](building)

### Questions

- Check existing [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)
- Create new discussion for general questions
- Tag maintainers for urgent issues

## License

By contributing to PwGen-rust, you agree that your contributions will be licensed under the Apache 2.0 License.

---

Thank you for contributing to PwGen-rust! Together we can build the most secure password manager possible. 🔐`
  },

  'developers/full-architecture': {
    title: 'Full Developer Architecture',
    description: 'Technical deep-dive into PwGen-rust\'s architecture for contributors and developers',
    content: `# Developer Architecture

Technical deep-dive into PwGen-rust's architecture for contributors and developers.

## Overview

PwGen-rust is built as a modular, secure password management system leveraging Rust's memory safety guarantees and modern cryptographic standards. This document provides detailed technical information for developers contributing to or building upon PwGen-rust.

## Project Structure

\`\`\`text
pwgen/
├── pwgen-core/          # Core library with business logic
│   ├── src/
│   │   ├── crypto/      # Encryption/decryption modules
│   │   ├── storage/     # Database and file operations
│   │   ├── models/      # Data structures
│   │   └── lib.rs       # Library entry point
│   └── Cargo.toml
├── pwgen-cli/           # Command-line interface
│   ├── src/
│   │   ├── commands/    # CLI command implementations
│   │   └── main.rs      # CLI entry point
│   └── Cargo.toml
├── pwgen-gui/           # Graphical user interface
│   ├── src/
│   │   ├── ui/          # UI components
│   │   └── main.rs      # GUI entry point
│   └── Cargo.toml
└── pwgen-server/        # API server (in development)
    ├── src/
    └── Cargo.toml
\`\`\`

## Core Components

### pwgen-core

The heart of PwGen-rust, providing:

#### Cryptography Module (\`crypto/\`)
\`\`\`rust
// Key components
pub struct CryptoEngine {
    // AES-256-GCM for encryption
    cipher: Aes256Gcm,
    // Argon2 for key derivation
    kdf: Argon2,
}

// Main operations
impl CryptoEngine {
    pub fn encrypt(&self, plaintext: &[u8]) -> Result<Vec<u8>>;
    pub fn decrypt(&self, ciphertext: &[u8]) -> Result<Vec<u8>>;
    pub fn derive_key(&self, password: &str, salt: &[u8]) -> Result<Key>;
}
\`\`\`

#### Storage Module (\`storage/\`)
\`\`\`rust
// Database abstraction
pub trait StorageBackend {
    fn create_vault(&self, path: &Path) -> Result<Vault>;
    fn open_vault(&self, path: &Path, key: &Key) -> Result<Vault>;
    fn save_entry(&self, entry: &Entry) -> Result<()>;
    fn get_entry(&self, id: &Uuid) -> Result<Entry>;
}

// SQLite implementation
pub struct SqliteStorage {
    connection: Connection,
    crypto: CryptoEngine,
}
\`\`\`

#### Models (\`models/\`)
\`\`\`rust
// Core data structures
#[derive(Serialize, Deserialize)]
pub struct PasswordEntry {
    pub id: Uuid,
    pub site: String,
    pub username: String,
    pub password: SecureString,  // Zeroizes on drop
    pub notes: Option<String>,
    pub tags: Vec<String>,
    pub created: DateTime<Utc>,
    pub modified: DateTime<Utc>,
}

#[derive(Serialize, Deserialize)]
pub struct Secret {
    pub id: Uuid,
    pub name: String,
    pub secret_type: SecretType,
    pub data: SecureBytes,       // Zeroizes on drop
    pub metadata: HashMap<String, String>,
}
\`\`\`

### pwgen-cli

Command-line interface built with \`clap\`:

\`\`\`rust
// Command structure
#[derive(Parser)]
#[command(name = "pwgen-cli")]
#[command(about = "Secure password manager CLI")]
struct Cli {
    #[command(subcommand)]
    command: Commands,
}

#[derive(Subcommand)]
enum Commands {
    Password(PasswordCommand),
    Secret(SecretCommand),
    Generate(GenerateCommand),
    Vault(VaultCommand),
}
\`\`\`

### pwgen-gui

Desktop GUI built with \`egui\`:

\`\`\`rust
// Main application state
pub struct PwGenApp {
    vault: Option<Vault>,
    ui_state: UiState,
    crypto: CryptoEngine,
}

impl eframe::App for PwGenApp {
    fn update(&mut self, ctx: &egui::Context, frame: &mut eframe::Frame) {
        // UI rendering logic
    }
}
\`\`\`

## Security Architecture

For detailed security architecture, see [Security Architecture](../security/architecture).

Key security features:
- Zero-knowledge architecture
- Memory-safe Rust implementation
- Authenticated encryption (AES-256-GCM)
- Memory-hard key derivation (Argon2)
- Automatic memory zeroization

## Database Schema

### SQLite Schema

\`\`\`sql
-- Vault metadata
CREATE TABLE vault_metadata (
    id INTEGER PRIMARY KEY,
    version INTEGER NOT NULL,
    created_at TIMESTAMP NOT NULL,
    salt BLOB NOT NULL,
    nonce BLOB NOT NULL,
    settings BLOB  -- Encrypted JSON
);

-- Password entries
CREATE TABLE passwords (
    id TEXT PRIMARY KEY,
    encrypted_data BLOB NOT NULL,
    nonce BLOB NOT NULL,
    created_at TIMESTAMP NOT NULL,
    modified_at TIMESTAMP NOT NULL
);

-- Secrets
CREATE TABLE secrets (
    id TEXT PRIMARY KEY,
    secret_type TEXT NOT NULL,
    encrypted_data BLOB NOT NULL,
    nonce BLOB NOT NULL,
    created_at TIMESTAMP NOT NULL,
    modified_at TIMESTAMP NOT NULL
);

-- Audit log
CREATE TABLE audit_log (
    id INTEGER PRIMARY KEY AUTOINCREMENT,
    timestamp TIMESTAMP NOT NULL,
    action TEXT NOT NULL,
    entity_type TEXT,
    entity_id TEXT,
    details TEXT  -- JSON
);
\`\`\`

## Build System

### Dependencies

Key dependencies and their purposes:

\`\`\`toml
[dependencies]
# Cryptography
aes-gcm = "0.10"          # AES-256-GCM encryption
argon2 = "0.5"            # Password hashing
rand = "0.8"              # Secure random generation
zeroize = "1.7"           # Memory clearing

# Storage
sqlcipher = "0.5"         # Encrypted SQLite
serde = "1.0"             # Serialization
uuid = "1.6"              # Unique identifiers

# UI (GUI only)
egui = "0.24"             # Immediate mode GUI
eframe = "0.24"           # egui framework

# CLI only
clap = "4.4"              # Command line parsing
\`\`\`

### Build Profiles

\`\`\`toml
# Development build
[profile.dev]
opt-level = 0
debug = true

# Release build
[profile.release]
opt-level = 3
lto = true
strip = true

# Security-focused build
[profile.secure]
inherits = "release"
panic = "abort"
overflow-checks = true
\`\`\`

## Testing Strategy

### Unit Tests

\`\`\`rust
#[cfg(test)]
mod tests {
    use super::*;

    #[test]
    fn test_encryption_roundtrip() {
        let engine = CryptoEngine::new();
        let plaintext = b"secret data";
        
        let encrypted = engine.encrypt(plaintext).unwrap();
        let decrypted = engine.decrypt(&encrypted).unwrap();
        
        assert_eq!(plaintext, &decrypted[..]);
    }
}
\`\`\`

### Integration Tests

\`\`\`rust
// tests/vault_operations.rs
#[test]
fn test_vault_creation_and_access() {
    let temp_dir = tempdir().unwrap();
    let vault_path = temp_dir.path().join("test.vault");
    
    // Create vault
    let vault = Vault::create(&vault_path, "test_password").unwrap();
    
    // Add entry
    let entry = PasswordEntry::new("github.com", "user@example.com");
    vault.add_password(entry).unwrap();
    
    // Verify
    let entries = vault.list_passwords().unwrap();
    assert_eq!(entries.len(), 1);
}
\`\`\`

## Performance Considerations

### Memory Management

- Use \`zeroize\` for sensitive data
- Avoid unnecessary allocations
- Use references where possible
- Clear clipboard after timeout

### Concurrency

- Vault operations are single-threaded
- GUI runs on main thread
- Background tasks use \`tokio\` runtime
- Database connections are pooled

### Optimization

- Lazy loading for large vaults
- Indexed database queries
- Cached decryption for session
- Efficient search algorithms

## Contributing Guidelines

### Code Style

Follow Rust standard style:
\`\`\`bash
# Format code
cargo fmt

# Run linter
cargo clippy -- -D warnings

# Run tests
cargo test
\`\`\`

### Pull Request Process

1. Fork the repository
2. Create feature branch
3. Write tests for new features
4. Ensure all tests pass
5. Update documentation
6. Submit pull request

### Security Considerations

When contributing:
- Never log sensitive data
- Always zeroize secrets
- Use constant-time comparisons
- Validate all inputs
- Follow OWASP guidelines

## API Design (Future)

The planned API server will provide:

\`\`\`rust
// RESTful endpoints
POST   /api/v1/auth/login
POST   /api/v1/auth/logout
GET    /api/v1/passwords
POST   /api/v1/passwords
PUT    /api/v1/passwords/:id
DELETE /api/v1/passwords/:id

// WebSocket for real-time updates
WS     /api/v1/sync
\`\`\`

## Platform-Specific Code

### Linux
\`\`\`rust
#[cfg(target_os = "linux")]
fn get_config_dir() -> PathBuf {
    // XDG Base Directory compliance
    xdg::BaseDirectories::new()
        .unwrap()
        .get_config_home()
        .join("pwgen")
}
\`\`\`

### macOS
\`\`\`rust
#[cfg(target_os = "macos")]
fn get_config_dir() -> PathBuf {
    dirs::config_dir()
        .unwrap()
        .join("dev.pwgenrust.pwgen")
}
\`\`\`

### Windows
\`\`\`rust
#[cfg(target_os = "windows")]
fn get_config_dir() -> PathBuf {
    dirs::config_dir()
        .unwrap()
        .join("PwGen")
}
\`\`\`

## Debugging

### Debug Builds

\`\`\`bash
# Build with debug symbols
cargo build

# Run with logging
RUST_LOG=debug cargo run

# Use debugger
rust-gdb target/debug/pwgen-cli
\`\`\`

### Profiling

\`\`\`bash
# CPU profiling
cargo build --release
perf record --call-graph=dwarf target/release/pwgen-cli
perf report

# Memory profiling
valgrind --tool=massif target/release/pwgen-cli
\`\`\`

## Resources

### Documentation
- [Rust Book](https://doc.rust-lang.org/book/)
- [Rust Security Guidelines](https://anssi-fr.github.io/rust-guide/)
- [OWASP Guidelines](https://owasp.org/www-project-application-security-verification-standard/)

### Dependencies Documentation
- [aes-gcm](https://docs.rs/aes-gcm/)
- [argon2](https://docs.rs/argon2/)
- [egui](https://docs.rs/egui/)
- [clap](https://docs.rs/clap/)

### Community
- [GitHub Discussions](https://github.com/KiefStudioMA/PWGen-Rust/discussions)
- [Issue Tracker](https://github.com/KiefStudioMA/PWGen-Rust/issues)
- [Twitter Updates](https://x.com/HxHippy)`
  }
};

export const getDocumentationPage = (section, page) => {
  const key = `${section}/${page}`;
  return documentationPages[key];
};

export const getDocumentationSection = (section) => {
  return docSections[section];
};

export const getAllDocumentationSections = () => {
  return Object.entries(docSections)
    .sort(([,a], [,b]) => a.order - b.order)
    .map(([key, section]) => ({ key, ...section }));
};